import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useLanguage } from '../context/LanguageContext';
import Tooltip from '../components/Tooltip';
import { 
  Users, 
  Settings, 
  Check,
  QrCode, 
  FileText, 
  StickyNote,
  Calendar,
  Plus,
  CheckCircle,
  Clock,
  X,
  Edit,
  Edit2,
  Trash2,
  MapPin,
  Eye,
  EyeOff,
  BarChart3,
  Download,
  Printer,
  Shield,
  Star,
  Target,
  Copy,
  Package,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Mail,
  TrendingUp,
  Activity,
  DollarSign,
  Pencil,
  Send,
  List,
  Link as LinkIcon,
  User,
  CalendarClock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { AttendeesModal } from '../components/CalendarAttendance';
import { CapacityLocationsSettings } from '../components/CapacityLocationsSettings';
import { SelfDemoteModal } from '../components/SelfDemoteModal';
import { saveAs } from 'file-saver';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QRPosterGenerator from '../components/QRPosterGenerator';
import ManualTrainingRegistration from '../components/ManualTrainingRegistration';
import AmmoBulletsIcon from '../components/AmmoBulletsIcon';
import ScrollableTabBar from '../components/ScrollableTabBar';
import MemberEditModal from '../components/MemberEditModal';
import MultiClubBadge from '../components/MultiClubBadge';
import LocationFormModal from '../components/LocationFormModal';
import QRGeneratorModal from '../components/QRGeneratorModal';
import StatisticsSection from '../components/StatisticsSection';
import MerkeskytingStatsCard from '../components/MerkeskytingStatsCard';
import DisciplineBoxEditor from '../components/DisciplineBoxEditor';
import AmmoSettings from '../components/AmmoSettings';
import AmmoReportSettings from '../components/AmmoReportSettings';
import AmmoSaleModal from '../components/AmmoSaleModal';
import StatsEmbedSection from '../components/StatsEmbedSection';
import CalendarImportSection from '../components/CalendarImportSection';
import AmmoSalesList from '../components/AmmoSalesList';
import AmmoStockInfoBox from '../components/AmmoStockInfoBox';
import AmmoStockFullWidth from '../components/AmmoStockFullWidth';
import AmmoSalesHeader from '../components/AmmoSalesHeader';
import AmmoStatistics from '../components/AmmoStatistics';
import AmmoAnnualReport from '../components/AmmoAnnualReport';
import InternalActivityStatistics from '../components/InternalActivityStatistics';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper functions for date filtering
const isToday = (date) => {
  const today = new Date();
  const checkDate = new Date(date);
  return today.toDateString() === checkDate.toDateString();
};

const isThisWeek = (date) => {
  const today = new Date();
  const checkDate = new Date(date);
  const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
  const weekEnd = new Date(today.setDate(today.getDate() - today.getDay() + 6));
  return checkDate >= weekStart && checkDate <= weekEnd;
};

// Get ISO week number (Norwegian calendar standard)
const getWeekNumber = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const Admin = () => {
  const { user, refreshUser, impersonationContext } = useAuth();
  const toast = useToast();
  const { changeLanguage, loadAvailableLanguages, availableLanguages, currentLanguage, t } = useLanguage();

  // Bransje informasjon for fargekoding
  const getDisciplineInfo = (discipline) => {
    const disciplineMap = {
      'nsf': { name: 'NSF', color: '#FFD700', fullName: 'NSF (Norges Sportsskytterforbund)' },
      'dfs': { name: 'DFS', color: '#10B981', fullName: 'DFS (Det Frivillige Skyttervesen, kun rifle)' },
      'dssn': { name: 'DSSN', color: '#3B82F6', fullName: 'DSSN (Dynamisk Sports-skyting Norge)' },
      'sws': { name: 'SWS', color: '#8B4513', fullName: 'SWS (Scandinavian Western Shooters)' },
      'nsu': { name: 'NSU', color: '#2F4F4F', fullName: 'NSU (Norsk Svartkruttunion)' },
      'nrof': { name: 'NROF', color: '#556B2F', fullName: 'NROF (Norske Reserveoffiserers Forbund)' },
      'nmsf': { name: 'NMSF', color: '#8B008B', fullName: 'NMSF (Norges Metallsilhuett Forbund)' },
      'njff': { name: 'NJFF', color: '#228B22', fullName: 'NJFF (Norges Jeger- og Fiskerforbund, rifle/hagle)' },
      'nbif': { name: 'NBIF', color: '#4682B4', fullName: 'NBIF (Norges bedriftsidrettsforbund, kun rifle)' },
      'nbsf': { name: 'NBSF', color: '#DC143C', fullName: 'NBSF (Norges Benkeskytterforbund, kun rifle)' },
      'nssf': { name: 'NSSF', color: '#00CED1', fullName: 'NSSF (Norges skiskytterforbund, kun rifle)' }
    };
    return disciplineMap[discipline] || disciplineMap['nsf'];
  };
  const [activeTab, setActiveTab] = useState('overview');
  const location = useLocation();

  // Read URL query param + hash to auto-open a specific tab and scroll to an anchor
  // (used by deep-links like /admin?tab=settings#ammo-salg).
  // Re-runs whenever the URL changes, so navigating from within /admin works too.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
      const hash = location.hash;
      if (hash) {
        const targetId = hash.slice(1);
        // Poll for element up to ~5s since target tab content may render async
        let attempts = 0;
        const maxAttempts = 50;
        const tryScroll = () => {
          const el = document.getElementById(targetId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Highlight briefly so the user sees where they landed
            try {
              el.classList.add('ring-2', 'ring-yellow-400', 'ring-offset-2', 'ring-offset-gray-900');
              setTimeout(() => {
                el.classList.remove('ring-2', 'ring-yellow-400', 'ring-offset-2', 'ring-offset-gray-900');
              }, 2000);
            } catch {}
            return;
          }
          attempts += 1;
          if (attempts < maxAttempts) setTimeout(tryScroll, 100);
        };
        setTimeout(tryScroll, 150);
      }
    }
  }, [location.search, location.hash]);
  const [stats, setStats] = useState({
    totalMembers: 0,
    pendingApproval: 0,
    thisMonthSessions: 0,
    pendingSessions: 0
  });
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [trainingStats, setTrainingStats] = useState({ total: 0, this_week: 0, today: 0 });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // MULTI-CLUB: map of user_id -> [{id, name}, ...] of OTHER clubs they're members of
  const [multiClubMap, setMultiClubMap] = useState({});
  
  // Locked accounts state
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedLockedMember, setSelectedLockedMember] = useState(null);
  const [showPosterGenerator, setShowPosterGenerator] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showManualRegistration, setShowManualRegistration] = useState(false);
  const [showMemberEdit, setShowMemberEdit] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showQRGenerator, setShowQRGenerator] = useState(false);
  const [qrGeneratorLocation, setQRGeneratorLocation] = useState(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showQRWarningModal, setShowQRWarningModal] = useState(false);
  const [qrWarningLocation, setQRWarningLocation] = useState(null);
  
  // Ammo sales states
  const [ammoModuleEnabled, setAmmoModuleEnabled] = useState(false);
  const [showAmmoSaleModal, setShowAmmoSaleModal] = useState(false);
  const [selectedSessionForAmmo, setSelectedSessionForAmmo] = useState(null);
  const [sessionAmmoSales, setSessionAmmoSales] = useState({});  // Track which sessions have ammo sales
  
  // Member management states
  const [memberSearch, setMemberSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState('all'); // all, admin, skyteleder, member, pending, deactivated
  const [showDugnadCol, setShowDugnadCol] = useState(() => {
    try {
      const v = localStorage.getItem('admin.showDugnadCol');
      return v === null ? false : v === 'true';
    } catch { return false; }
  });
  const [showRoleCol, setShowRoleCol] = useState(() => {
    try {
      const v = localStorage.getItem('admin.showRoleCol');
      return v === null ? true : v === 'true';
    } catch { return true; }
  });

  // Persist column visibility choices
  useEffect(() => {
    try { localStorage.setItem('admin.showDugnadCol', String(showDugnadCol)); } catch {}
  }, [showDugnadCol]);
  useEffect(() => {
    try { localStorage.setItem('admin.showRoleCol', String(showRoleCol)); } catch {}
  }, [showRoleCol]);

  // Auto-scroll active admin tab into center of viewport when it changes
  useEffect(() => {
    try {
      const el = document.querySelector(`[data-testid="admin-tab-${activeTab}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    } catch {}
  }, [activeTab]);
  const [memberSortField, setMemberSortField] = useState('full_name'); // NEW: Sorting field
  const [memberSortDirection, setMemberSortDirection] = useState('asc'); // NEW: Sorting direction
  const [memberPage, setMemberPage] = useState(1);
  const [membersPerPage] = useState(10);
  
  // Email change and backup states
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [emailChangeMember, setEmailChangeMember] = useState(null);
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [emailChangeReason, setEmailChangeReason] = useState('');
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [backupCreated, setBackupCreated] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [emailAuditLogs, setEmailAuditLogs] = useState([]);

  // Pause-medlem modal (gul status)
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseTargetMember, setPauseTargetMember] = useState(null);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  
  // Session pagination for Admin > LOGG
  const [sessionPage, setSessionPage] = useState(1);
  const sessionsPerPage = 25;

  // Session filter states - Enhanced like TrainingLog.js
  const [sessionLocationFilter, setSessionLocationFilter] = useState('');
  const [sessionOfficerFilter, setSessionOfficerFilter] = useState('');
  const [sessionStatusFilter, setSessionStatusFilter] = useState('');
  const [sessionMemberFilter, setSessionMemberFilter] = useState(''); // New: Filter by specific member
  const [sessionSearch, setSessionSearch] = useState('');
  const [sessionDateFilter, setSessionDateFilter] = useState('alle'); // 'alle', '6months', '1year', '2years', 'custom'
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [sessionActivityTypes, setSessionActivityTypes] = useState(new Set());
  const [selectedActivities, setSelectedActivities] = useState(new Set()); // For multiple activity filter
  const [sessionBranchFilter, setSessionBranchFilter] = useState('alle'); // 'alle', 'nsf', 'dfs', 'dssn'
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Dugnad statistics states
  const [showDugnadStats, setShowDugnadStats] = useState(false);
  const [dugnadThreshold, setDugnadThreshold] = useState(3); // Default: 3 dugnader for gold crown
  const [memberDugnadCounts, setMemberDugnadCounts] = useState({}); // {memberId: count}
  const [selectedDugnadActivity, setSelectedDugnadActivity] = useState('Dugnad'); // Selected activity to count as dugnad
  const [isGeneratingDugnadPdf, setIsGeneratingDugnadPdf] = useState(false);
  const [showDugnadDateFilter, setShowDugnadDateFilter] = useState(false);
  const [dugnadDateFilter, setDugnadDateFilter] = useState('year'); // 'year', '30', '90', '365', 'all', 'custom'
  const [dugnadCustomStartDate, setDugnadCustomStartDate] = useState('');
  const [dugnadCustomEndDate, setDugnadCustomEndDate] = useState('');
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [messageRecipients, setMessageRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState(new Set());
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [scheduledSend, setScheduledSend] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [adminMessagesPage, setAdminMessagesPage] = useState(1);
  const ADMIN_MESSAGES_PER_PAGE = 5;
  const [messageAuditLogs, setMessageAuditLogs] = useState([]);
  const [showMessageAuditLog, setShowMessageAuditLog] = useState(false);
  
  // Quill editor modules configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': [1, 2, false] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  }), []);

  const quillFormats = [
    'bold', 'italic', 'underline',
    'header',
    'list', 'bullet',
    'link'
  ];
  
  // Calendar - Today's skyteleder
  const [todaySkyteledere, setTodaySkyteledere] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [showTodayAttendees, setShowTodayAttendees] = useState(false);
  
  // PDF Options dialog for Klubb-logg (with document selection like TrainingLog.js)
  const [showKlubbloggPdfOptions, setShowKlubbloggPdfOptions] = useState(false);
  const [selectedMemberDocs, setSelectedMemberDocs] = useState([]);
  const [klubbloggPdfType, setKlubbloggPdfType] = useState('politi'); // 'politi' or 'standard'
  
  // Simple bulk delete states for Klubb-logg (like TrainingLog.js)
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  
  // Bulk approve states
  const [bulkApproveMode, setBulkApproveMode] = useState(false);
  const [selectedSessionsForApproval, setSelectedSessionsForApproval] = useState(new Set());
  const [bulkApprovingInProgress, setBulkApprovingInProgress] = useState(false);
  
  // NEW: Bulk member deletion states
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [deletionLogs, setDeletionLogs] = useState([]);
  const [showDeletionLogs, setShowDeletionLogs] = useState(false);
  const [memberAuditLogs, setMemberAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [showAmmoReminderModal, setShowAmmoReminderModal] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Deletion log states
  const [showDeletionLog, setShowDeletionLog] = useState(false);
  const [deletedSessions, setDeletedSessions] = useState([]);
  const [loadingDeletedSessions, setLoadingDeletedSessions] = useState(false);
  
  // Session edit states
  const [showEditSessionModal, setShowEditSessionModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editSessionData, setEditSessionData] = useState({
    date: '',
    activity: '',
    location_id: '',
    range_officer_name: '',
    shooting_branch: 'nsf',
    user_id: '',  // Member/user who this session belongs to
    admin_notes: ''
  });
  
  // Sorting states
  const [sortColumn, setSortColumn] = useState('start_time');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  // Add Member Modal states
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberData, setAddMemberData] = useState({
    full_name: '',
    email: '',
    role: 'member' // 'member' or 'org_admin'
  });
  const [addingMember, setAddingMember] = useState(false);
  
  // Overview/Dagens filter states
  const [overviewDateFilter, setOverviewDateFilter] = useState('today'); // today, week, all
  const [overviewStatusFilter, setOverviewStatusFilter] = useState('all'); // all, pending, approved

  // Settings states
  const [settingsData, setSettingsData] = useState({
    organizationName: '',
    organizationNumber: '',
    subscriptionPlan: 'start',
    description: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    primaryColor: '#FFD700',
    secondaryColor: '#1F2937',
    backgroundColor: '#111827',
    logoFile: null,
    logoUrl: '',
    bannerFiles: [],
    bannerUrls: [],
    bannerLinks: [], // URLs for banner clickthrough
    bannerExpiryDates: [], // End dates for each banner
    bannerVisible: [], // Visibility toggle for each banner
    bannerRotationSeconds: 15, // Seconds between banners
    activeLanguage: 'no',
    memberOrganizations: [
      {clubName: '', organization: 'NSF - Norges Sportsskytterforbund'}
    ], // Array of {clubName, organization} pairs
    shootingBranches: {
      nsf: true,
      dfs: false,
      dssn: false,
      sws: false,
      nsu: false,
      nrof: false,
      nmsf: false,
      njff: false,
      nbif: false,
      nbsf: false,
      nssf: false
    },
    activityTypes: ['Organisert trening', 'Klubbstevne', 'Dugnad', 'NSF-øvelser'],
    dugnadFeatureEnabled: true, // Default to enabled
    dugnadActivity: 'Dugnad',
    dugnadThreshold: 3,
    calendarEnabled: true, // Default to enabled
    messagesEnabled: true, // Default to enabled
    clubOperationsEnabled: false // Default to disabled during development
  });
  const [newActivityType, setNewActivityType] = useState('');
  
  // Calendar event types management
  const [calendarEventTypes, setCalendarEventTypes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [showAddEventTypeModal, setShowAddEventTypeModal] = useState(false);
  const [showEditEventTypeModal, setShowEditEventTypeModal] = useState(false);
  const [editingEventType, setEditingEventType] = useState(null);
  const [newEventType, setNewEventType] = useState({ id: '', label: '', color: 'bg-blue-500' });
  
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [dstInfo, setDstInfo] = useState(null);
  
  // Language file states
  const [languageFiles, setLanguageFiles] = useState([]);
  const [uploadingLanguage, setUploadingLanguage] = useState(false);
  const [languageUploadError, setLanguageUploadError] = useState('');

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (user?.organization_id) {
      loadLanguageFiles();
      loadAvailableLanguages(user.organization_id, user.role);
      loadDstInfo();
      // MULTI-CLUB: Fetch map of multi-club members in current org for badge display
      (async () => {
        try {
          const flagsRes = await axios.get(`${API_BASE}/api/feature-flags`);
          if (!flagsRes.data?.multi_club_enabled) return;
          const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
          const res = await axios.get(`${API_BASE}/api/admin/multi-club-members`, { headers });
          setMultiClubMap(res.data || {});
        } catch (e) {
          // Silent fail - badge is non-critical
        }
      })();
    }
  }, [user?.organization_id]);

  // Fetch locked accounts for organization
  const fetchLockedAccounts = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/admin/locked-accounts`, { headers });
      setLockedAccounts(response.data.lockouts || []);
    } catch (error) {
      console.error('Failed to fetch locked accounts:', error);
    }
  };

  // Unlock account (Skyteleder)
  const unlockMemberAccount = async (email) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.post(
        `${API_BASE}/api/superadmin/unlock-account`,
        { email },
        { headers }
      );

      if (response.data.success) {
        toast.success(`🔓 ${response.data.message}`);
        // Refresh locked accounts and members
        await fetchLockedAccounts();
        setShowUnlockModal(false);
        setSelectedLockedMember(null);
      }
    } catch (error) {
      console.error('Unlock account error:', error);
      toast.error('❌ Opplåsing feilet: ' + (error.response?.data?.detail || error.message));
    }
  };

  // NEW: Auto-refresh members list when on "Medlemmer" tab (polling every 15 seconds)
  useEffect(() => {
    if (activeTab !== 'members' || !user?.organization_id) return;

    // Initial fetch
    fetchLockedAccounts();

    const pollInterval = setInterval(async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        
        // Silently fetch updated members in background
        const membersResponse = await axios.get(`${API_BASE}/api/users`, {
          params: { organization_id: user?.organization_id },
          headers
        });

        // Update state with fresh data
        setMembers(membersResponse.data);
        
        // Calculate pending count from members array (same as initial load and Dagens tab)
        const pendingCount = membersResponse.data.filter(m => !m.approved && m.active !== false && m.is_deleted !== true).length;
        setPendingMembers(membersResponse.data.filter(m => !m.approved && m.active !== false));
        
        // Update stats with calculated count
        setStats(prev => ({
          ...prev,
          totalMembers: membersResponse.data.length,
          pendingApproval: pendingCount  // Use calculated count, not separate endpoint!
        }));

        // Also refresh locked accounts
        await fetchLockedAccounts();

        console.log('✅ Auto-refreshed members list (polling)');
      } catch (error) {
        console.error('Failed to auto-refresh members:', error);
        // Silent failure - don't disrupt user experience
      }
    }, 15000); // Poll every 15 seconds

    // Cleanup interval on unmount or tab change
    return () => clearInterval(pollInterval);
  }, [activeTab, user?.organization_id]);
  // NEW: Auto-refresh training sessions when activeTab is 'overview' (Dagens)
  useEffect(() => {
    if (activeTab !== 'overview' || !user?.organization_id) return;

    const pollInterval = setInterval(async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

        // Fetch fresh sessions data
        const sessionsResponse = await axios.get(`${API_BASE}/api/training/sessions`, {
          params: { 
            organization_id: user?.organization_id
          },
          headers
        });

        // Update sessions and recalculate stats
        setSessions(sessionsResponse.data);
        
        // Also refresh training stats for accurate counts
        try {
          const statsResponse = await axios.get(`${API_BASE}/api/training/stats`, { headers });
          setTrainingStats(statsResponse.data);
        } catch (e) {
          // Silent failure for stats
        }
        
        const now = new Date();
        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const sessionsThisMonth = sessionsResponse.data.filter(s => new Date(s.start_time) >= thisMonth);
        
        setStats(prev => ({
          ...prev,
          thisMonthSessions: sessionsThisMonth.length,
          pendingSessions: sessionsResponse.data.filter(s => !s.verified).length
        }));

        console.log('✅ Auto-refreshed training sessions (polling)');
      } catch (error) {
        console.error('Failed to auto-refresh sessions:', error);
        // Silent failure - don't disrupt user experience
      }
    }, 15000); // Poll every 15 seconds

    // Cleanup interval on unmount or tab change
    return () => clearInterval(pollInterval);
  }, [activeTab, user?.organization_id]);

  // Fetch today's skyteleder from calendar
  const fetchTodaySkyteleder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/calendar/today-skyteleder`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodaySkyteledere(response.data.skyteledere || []);
    } catch (error) {
      console.error('Failed to fetch today skyteleder:', error);
      setTodaySkyteledere([]);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/calendar/attendance/today`, { headers: { Authorization: `Bearer ${token}` } });
      setTodayAttendance(r.data);
    } catch {
      setTodayAttendance(null);
    }
  };

  // Fetch today's skyteleder when component mounts or when switching to sessions tab
  useEffect(() => {
    if (user?.organization?.calendar_enabled !== false) {
      fetchTodaySkyteleder();
      fetchTodayAttendance();
      const t = setInterval(fetchTodayAttendance, 60000);
      return () => clearInterval(t);
    }
  }, [user?.organization?.calendar_enabled]);

  // Fetch calendar event types
  const fetchCalendarEventTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/calendar/event-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarEventTypes(response.data.event_types || []);
      setAvailableColors(response.data.available_colors || []);
    } catch (error) {
      console.error('Failed to fetch calendar event types:', error);
    }
  };

  // Fetch calendar event types when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings' && user?.organization?.calendar_enabled !== false) {
      fetchCalendarEventTypes();
    }
  }, [activeTab, user?.organization?.calendar_enabled]);

  // Hent nåværende Merkeskyting-status når Innstillinger-fanen åpnes
  useEffect(() => {
    if (activeTab !== 'settings') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_BASE}/api/merkeskyting/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setSettingsData(prev => ({ ...prev, merkeskytingEnabled: r.data?.enabled !== false }));
      })
      .catch(() => {});
  }, [activeTab]);

  // Add new calendar event type
  const handleAddCalendarEventType = async () => {
    if (!newEventType.label.trim()) {
      toast.error('Skriv inn navn på aktivitetstypen');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const typeId = newEventType.id.trim() || newEventType.label.trim().toLowerCase().replace(/\s+/g, '_');
      
      await axios.post(`${API_BASE}/api/calendar/event-types`, {
        id: typeId,
        label: newEventType.label.trim(),
        color: newEventType.color
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Aktivitetstype lagt til');
      setShowAddEventTypeModal(false);
      setNewEventType({ id: '', label: '', color: 'bg-blue-500' });
      fetchCalendarEventTypes();
    } catch (error) {
      console.error('Failed to add event type:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke legge til aktivitetstype');
    }
  };

  // Update calendar event type
  const handleUpdateCalendarEventType = async () => {
    if (!editingEventType || !editingEventType.label.trim()) {
      toast.error('Skriv inn navn på aktivitetstypen');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Determine final color and text color
      const finalColor = editingEventType.customColor || editingEventType.color;
      const finalTextColor = editingEventType.customTextColor && !editingEventType.textColor 
        ? editingEventType.customTextColor 
        : editingEventType.textColor;
      
      await axios.put(`${API_BASE}/api/calendar/event-types/${editingEventType.id}`, {
        label: editingEventType.label.trim(),
        color: finalColor,
        textColor: finalTextColor || 'text-white',
        customColor: editingEventType.customColor || null,
        customTextColor: editingEventType.customTextColor || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Aktivitetstype oppdatert');
      setShowEditEventTypeModal(false);
      setEditingEventType(null);
      fetchCalendarEventTypes();
    } catch (error) {
      console.error('Failed to update event type:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere aktivitetstype');
    }
  };

  // Open edit modal for event type
  const openEditEventTypeModal = (eventType) => {
    setEditingEventType({...eventType});
    setShowEditEventTypeModal(true);
  };

  // Delete calendar event type
  const handleDeleteCalendarEventType = async (typeId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne aktivitetstypen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API_BASE}/api/calendar/event-types/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Aktivitetstype slettet');
      fetchCalendarEventTypes();
    } catch (error) {
      console.error('Failed to delete event type:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke slette aktivitetstype');
    }
  };

  // ============== MESSAGES FUNCTIONS ==============
  
  // Fetch all messages for admin view
  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Kunne ikke hente meldinger');
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchMessageAuditLog = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/messages/audit-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessageAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch message audit logs:', error);
    }
  };

  // Fetch potential recipients for message
  const fetchMessageRecipients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/messages/recipients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessageRecipients(response.data.recipients || []);
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    }
  };

  // Send a new message
  const handleSendMessage = async () => {
    if (!messageSubject.trim()) {
      toast.error('Skriv inn et emne');
      return;
    }
    if (!messageContent.trim() || messageContent === '<p><br></p>') {
      toast.error('Skriv inn meldingsinnhold');
      return;
    }
    if (selectedRecipients.size === 0) {
      toast.error('Velg minst én mottaker');
      return;
    }
    if (scheduledSend && !scheduledDateTime) {
      toast.error('Velg dato og tidspunkt for utsending');
      return;
    }

    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        subject: messageSubject.trim(),
        content: messageContent,
        recipient_ids: Array.from(selectedRecipients)
      };
      
      // Add scheduled time if enabled
      if (scheduledSend && scheduledDateTime) {
        payload.scheduled_at = new Date(scheduledDateTime).toISOString();
      }
      
      await axios.post(`${API_BASE}/api/messages`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const successMsg = scheduledSend 
        ? `Melding planlagt for utsending til ${selectedRecipients.size} mottaker(e)`
        : `Melding sendt til ${selectedRecipients.size} mottaker(e)`;
      toast.success(successMsg);
      
      setShowMessageForm(false);
      setMessageSubject('');
      setMessageContent('');
      setSelectedRecipients(new Set());
      setScheduledSend(false);
      setScheduledDateTime('');
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke sende melding');
    } finally {
      setSendingMessage(false);
    }
  };

  // Delete a message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne meldingen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Melding slettet');
      fetchMessages();
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Kunne ikke slette meldingen');
    }
  };

  // Toggle all recipients
  const toggleAllRecipients = () => {
    if (selectedRecipients.size === messageRecipients.length) {
      setSelectedRecipients(new Set());
    } else {
      setSelectedRecipients(new Set(messageRecipients.map(r => r.id)));
    }
  };

  // Toggle single recipient
  const toggleRecipient = (recipientId) => {
    const newSet = new Set(selectedRecipients);
    if (newSet.has(recipientId)) {
      newSet.delete(recipientId);
    } else {
      newSet.add(recipientId);
    }
    setSelectedRecipients(newSet);
  };

  // Fetch messages and recipients when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessages();
      fetchMessageRecipients();
    }
  }, [activeTab]);

  // Add Norwegian tooltips to ReactQuill toolbar buttons when modal opens
  useEffect(() => {
    if (showMessageForm) {
      // Small delay to ensure ReactQuill is rendered
      const timer = setTimeout(() => {
        const tooltipMap = {
          '.ql-bold': 'Fet tekst',
          '.ql-italic': 'Kursiv',
          '.ql-underline': 'Understreket',
          '.ql-header .ql-picker-label': 'Tekststørrelse',
          '.ql-list[value="ordered"]': 'Nummerert liste',
          '.ql-list[value="bullet"]': 'Punktliste',
          '.ql-link': 'Sett inn lenke',
          '.ql-clean': 'Fjern formatering'
        };

        Object.entries(tooltipMap).forEach(([selector, tooltip]) => {
          const element = document.querySelector(`.message-quill-editor ${selector}`);
          if (element) {
            element.setAttribute('title', tooltip);
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [showMessageForm]);

  // NEW: Auto-refresh training sessions when activeTab is 'sessions' (Klubb-logg)
  useEffect(() => {
    if (activeTab !== 'sessions' || !user?.organization_id) return;

    const pollInterval = setInterval(async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

        // Fetch fresh sessions data
        const sessionsResponse = await axios.get(`${API_BASE}/api/training/sessions`, {
          params: { 
            organization_id: user?.organization_id
          },
          headers
        });

        // Update sessions
        setSessions(sessionsResponse.data);

        console.log('✅ Auto-refreshed Klubb-logg (polling)');
      } catch (error) {
        console.error('Failed to auto-refresh Klubb-logg:', error);
        // Silent failure - don't disrupt user experience
      }
    }, 15000); // Poll every 15 seconds

    // Cleanup interval on unmount or tab change
    return () => clearInterval(pollInterval);
  }, [activeTab, user?.organization_id]);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Load critical data first (members and deletion logs - faster queries)
      // Locations only for admin and super_admin
      const promises = [
        axios.get(`${API_BASE}/api/users`, {
          params: { organization_id: user?.organization_id },
          headers
        }),
        axios.get(`${API_BASE}/api/member-deletion-logs?limit=50`, { headers }).catch(() => ({ data: [] }))
      ];

      // Add locations request for all admin roles (including when impersonating)
      const hasAdminAccess = ['admin', 'org_admin', 'range_officer', 'super_admin'].includes(user?.role) || user?.original_role === 'super_admin';
      if (hasAdminAccess) {
        promises.splice(1, 0, axios.get(`${API_BASE}/api/organizations/${user?.organization_id}/locations`, { headers }));
      }

      const responses = await Promise.all(promises);
      const membersResponse = responses[0];
      const locationsResponse = hasAdminAccess ? responses[1] : null;
      const deletionLogsResponse = hasAdminAccess ? responses[2] : responses[1];

      setMembers(membersResponse.data);
      
      // Calculate pending approval count from members array (same as Dagens tab)
      const pendingCount = membersResponse.data.filter(m => !m.approved && m.active !== false && m.is_deleted !== true).length;
      setPendingMembers(membersResponse.data.filter(m => !m.approved && m.active !== false));
      setLocations(locationsResponse ? locationsResponse.data : []);
      setDeletionLogs(deletionLogsResponse.data || []);

      // Load sessions - higher limit for consistent counts with Min logg
      const sessionsResponse = await axios.get(`${API_BASE}/api/training/sessions`, {
        params: { 
          organization_id: user?.organization_id
        },
        headers
      });
      setSessions(sessionsResponse.data);

      // Fetch training stats (accurate counts from database)
      try {
        const statsResponse = await axios.get(`${API_BASE}/api/training/stats`, { headers });
        setTrainingStats(statsResponse.data);
      } catch (error) {
        console.error('Error fetching training stats:', error);
        // Fallback to session array length if stats endpoint fails
        setTrainingStats({ 
          total: sessionsResponse.data.length, 
          this_week: 0, 
          today: 0 
        });
      }

      // Calculate stats
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const sessionsThisMonth = sessionsResponse.data.filter(s => new Date(s.start_time) >= thisMonth);
      
      setStats({
        totalMembers: membersResponse.data.length,
        pendingApproval: pendingCount,  // Members waiting approval
        thisMonthSessions: sessionsThisMonth.length,  // Fixed field name to match state definition
        pendingSessions: sessionsResponse.data.filter(s => !s.verified).length  // Sessions waiting approval
      });
      
      // Check if ammo module is enabled
      try {
        const ammoResponse = await axios.get(`${API_BASE}/api/admin/ammo-settings`, { headers });
        console.log('🔍 Ammo settings response:', {
          module_enabled: ammoResponse.data.module_enabled,
          userRole: user?.role,
          hasAmmoTypes: !!ammoResponse.data.ammo_types
        });
        setAmmoModuleEnabled(ammoResponse.data.module_enabled || false);
        
        // Load ammo sales if module is enabled
        if (ammoResponse.data.module_enabled) {
          try {
            const salesResponse = await axios.get(`${API_BASE}/api/admin/ammo-sales?limit=500`, { headers });
            // Group sales by session_id for quick lookup
            const salesBySession = {};
            salesResponse.data.sales.forEach(sale => {
              if (!salesBySession[sale.session_id]) {
                salesBySession[sale.session_id] = [];
              }
              salesBySession[sale.session_id].push(sale);
            });
            setSessionAmmoSales(salesBySession);
          } catch (error) {
            console.error('Error loading ammo sales:', error);
          }
        }
      } catch (error) {
        console.error('❌ Error loading ammo settings:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          userRole: user?.role
        });
        setAmmoModuleEnabled(false);
      }

    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLanguageFiles = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/organizations/${user?.organization_id}/language-files`, { headers });
      setLanguageFiles(response.data.language_files || []);
    } catch (error) {
      console.error('Failed to load language files:', error);
    }
  };

  const loadDstInfo = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/calendar/dst-info`, { headers });
      setDstInfo(response.data);
    } catch (error) {
      console.error('Failed to load DST info:', error);
    }
  };
  const handleLanguageFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset error state
    setLanguageUploadError('');
    
    if (!file.name.endsWith('.json')) {
      setLanguageUploadError('Kun .json filer er tillatt');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setLanguageUploadError('Filen er for stor. Maksimum 10MB tillatt.');
      return;
    }

    setUploadingLanguage(true);

    try {
      // Read and parse JSON file
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      let translations;
      try {
        translations = JSON.parse(fileContent);
      } catch (parseError) {
        throw new Error('Ugyldig JSON format. Sjekk at filen er riktig formatert.');
      }

      // Validate JSON structure - should have basic required fields
      const requiredSections = ['nav', 'auth', 'messages'];
      const missingSections = requiredSections.filter(section => !translations[section]);
      
      if (missingSections.length > 0) {
        throw new Error(`Språkfilen mangler påkrevde seksjoner: ${missingSections.join(', ')}`);
      }

      // Extract language info from filename or structure
      let languageCode = file.name.replace('.json', '').toLowerCase();
      let languageName = translations.languageInfo?.name || languageCode;
      
      // Map common language codes to names
      const languageNames = {
        'en': 'English',
        'sv': 'Svenska', 
        'da': 'Dansk',
        'de': 'Deutsch',
        'fr': 'Français',
        'es': 'Español',
        'it': 'Italiano',
        'no': 'Norsk',
        'nb': 'Norsk Bokmål',
        'nn': 'Nynorsk'
      };
      
      if (languageNames[languageCode]) {
        languageName = languageNames[languageCode];
      }

      // Upload to backend
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };
      
      const uploadData = {
        language_code: languageCode,
        language_name: languageName,
        translations: translations
      };

      const response = await axios.post(
        `${API_BASE}/api/organizations/${user?.organization_id}/language-files`,
        uploadData,
        { headers }
      );

      console.log('Language file upload response:', response.data);

      toast.success(`Språkfil ${languageName} (${languageCode}) lastet opp!`);
      
      // Reload language files
      await loadLanguageFiles();
      
      // Update available languages in settings
      if (!settingsData.activeLanguage) {
        setSettingsData(prev => ({ ...prev, activeLanguage: languageCode }));
      }

      // Clear the file input
      event.target.value = '';

    } catch (error) {
      console.error('Language file upload failed:', error);
      setLanguageUploadError(error.message || 'Feil ved opplasting av språkfil');
    } finally {
      setUploadingLanguage(false);
    }
  };

  const deleteLanguageFile = async (languageCode) => {
    if (!window.confirm(`Er du sikker på at du vil slette språkfilen for "${languageCode}"?`)) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.delete(
        `${API_BASE}/api/organizations/${user?.organization_id}/language-files/${languageCode}`,
        { headers }
      );

      toast.addToast('Språkfil ble slettet!', 'success');
      await loadLanguageFiles(); // Refresh language files list
    } catch (error) {
      console.error('Failed to delete language file:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Kunne ikke slette språkfil. Prøv igjen.';
      toast.addToast(errorMessage, 'error');
    }
  };

  const approveUser = async (userId, approved) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API_BASE}/api/users/${userId}`, { approved }, { headers });
      await loadAdminData(); // Refresh data
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error('Kunne ikke oppdatere bruker. Prøv igjen.');
    }
  };

  const approveAllPendingMembers = async () => {
    try {
      // Only approve EKTE pending (not deactivated/deleted)
      const pendingMembers = members.filter(m => !m.approved && m.active !== false && m.is_deleted !== true);
      if (pendingMembers.length === 0) return;
      
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Approve all pending members
      await Promise.all(
        pendingMembers.map(member => 
          axios.put(`${API_BASE}/api/users/${member.id}`, { approved: true }, { headers })
        )
      );
      
      await loadAdminData(); // Refresh data
      toast.success(`${pendingMembers.length} medlemmer ble godkjent.`);
    } catch (error) {
      console.error('Failed to approve all pending members:', error);
      toast.error('Kunne ikke godkjenne alle medlemmer. Prøv igjen.');
    }
  };

  const toggleAdminStatus = async (userId, makeAdmin) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const newRole = makeAdmin ? 'org_admin' : 'member';
      await axios.put(`${API_BASE}/api/users/${userId}`, { role: newRole }, { headers });
      await loadAdminData(); // Refresh data
    } catch (error) {
      console.error('Failed to update admin status:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere admin-status. Prøv igjen.');
    }
  };

  // Advarsel når en Admin nedgraderer seg selv (kan ikke oppgradere seg selv tilbake)
  const [selfDemote, setSelfDemote] = useState(null);
  const guardRoleChange = (member, toRole, action) => {
    const isSelf = member.id === user?.id;
    const isRealSuper = user?.role === 'super_admin' || impersonationContext?.originalRole === 'super_admin';
    if (isSelf && member.role === 'admin' && toRole !== 'admin' && !isRealSuper) {
      setSelfDemote({ toRole, action });
      return;
    }
    action();
  };

  // Toggle organization admin status (admin role with full access to QR and Settings)
  const toggleOrgAdminStatus = async (userId, makeOrgAdmin) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const newRole = makeOrgAdmin ? 'admin' : 'member';
      await axios.put(`${API_BASE}/api/users/${userId}`, { role: newRole }, { headers });
      await loadAdminData(); // Refresh data
      toast.success(makeOrgAdmin ? 'Medlem oppgradert til Admin' : 'Admin nedgradert til Medlem');
    } catch (error) {
      console.error('Failed to update org admin status:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere admin-status. Prøv igjen.');
    }
  };

  // Function to get branch info from location discipline (same as TrainingLog.js)
  const getBranchFromLocation = (session) => {
    // Use location discipline if available (more reliable)
    if (session.location_discipline) {
      const disciplineKey = session.location_discipline.toLowerCase();
      
      const branchMap = {
        'nsf': { key: 'nsf', name: 'NSF', color: 'bg-blue-500', textColor: 'text-white' },
        'dfs': { key: 'dfs', name: 'DFS', color: 'bg-purple-500', textColor: 'text-white' },
        'dssn': { key: 'dssn', name: 'DSSN', color: 'bg-green-500', textColor: 'text-white' },
        'sws': { key: 'sws', name: 'SWS', color: 'bg-yellow-600', textColor: 'text-white' },
        'nsu': { key: 'nsu', name: 'NSU', color: 'bg-gray-600', textColor: 'text-white' },
        'nrof': { key: 'nrof', name: 'NROF', color: 'bg-indigo-600', textColor: 'text-white' },
        'nmsf': { key: 'nmsf', name: 'NMSF', color: 'bg-pink-600', textColor: 'text-white' },
        'njff': { key: 'njff', name: 'NJFF', color: 'bg-emerald-600', textColor: 'text-white' },
        'nbif': { key: 'nbif', name: 'NBIF', color: 'bg-cyan-600', textColor: 'text-white' },
        'nbsf': { key: 'nbsf', name: 'NBSF', color: 'bg-red-600', textColor: 'text-white' },
        'nssf': { key: 'nssf', name: 'NSSF', color: 'bg-teal-600', textColor: 'text-white' }
      };
      
      if (branchMap[disciplineKey]) {
        return branchMap[disciplineKey];
      }
    }
    
    // Fallback to activity-based detection for older sessions
    return getActivityBranch(session.activity || session.training_type || 'Pistoltrening');
  };

  // Fallback function for activity-based branch detection
  const getActivityBranch = (activity) => {
    if (!activity) return { key: 'nsf', name: 'NSF', color: 'bg-blue-500', textColor: 'text-white' };
    
    const activityLower = activity.toLowerCase();
    
    // DSSN - Dynamic shooting activities
    if (activityLower.includes('dssn') || activityLower.includes('dynamisk') || activityLower.includes('ipsc')) {
      return { key: 'dssn', name: 'DSSN', color: 'bg-green-500', textColor: 'text-white' };
    }
    
    // DFS - Field shooting activities  
    if (activityLower.includes('dfs') || activityLower.includes('felt') || activityLower.includes('hagle')) {
      return { key: 'dfs', name: 'DFS', color: 'bg-purple-500', textColor: 'text-white' };
    }
    
    // Default NSF
    return { key: 'nsf', name: 'NSF', color: 'bg-blue-500', textColor: 'text-white' };
  };

  // Date filter helper function
  const isWithinDateRange = (date, filterType) => {
    if (filterType === 'alle') return true;
    
    // Handle custom date range
    if (filterType === 'custom') {
      if (!customDateFrom && !customDateTo) return true;
      
      const sessionDate = new Date(date);
      sessionDate.setHours(0, 0, 0, 0);
      
      if (customDateFrom) {
        const fromDate = new Date(customDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (sessionDate < fromDate) return false;
      }
      
      if (customDateTo) {
        const toDate = new Date(customDateTo);
        toDate.setHours(23, 59, 59, 999);
        if (sessionDate > toDate) return false;
      }
      
      return true;
    }
    
    const now = new Date();
    const monthsBack = { '6months': 6, '1year': 12, '2years': 24 };
    
    const cutoffDate = new Date(now.getFullYear(), now.getMonth() - monthsBack[filterType], now.getDate());
    return date >= cutoffDate;
  };

  // Sorting function
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Add Member functionality
  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);

    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      
      const memberPayload = {
        full_name: addMemberData.full_name,
        email: addMemberData.email,
        role: addMemberData.role,
        approved: true, // Auto-approve manually created members
        organization_id: user?.organization_id,
        send_setup_email: true // NEW: Signal backend to send password setup email
      };

      const response = await axios.post(`${API_BASE}/api/admin/create-member`, memberPayload, { headers });

      if (response.data.success) {
        toast.success('Medlem lagt til! E-post med passord-oppsett sendt.');
        setShowAddMemberModal(false);
        setAddMemberData({ full_name: '', email: '', role: 'member' });
        loadAdminData(); // Refresh member list
      }
    } catch (error) {
      console.error('Add member error:', error);
      if (error.response?.data?.detail) {
        toast.error(`Kunne ikke legge til medlem: ${error.response.data.detail}`);
      } else {
        toast.error('Kunne ikke legge til medlem. Prøv igjen.');
      }
    } finally {
      setAddingMember(false);
    }
  };

  const deleteLocation = async (locationId) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.delete(`${API_BASE}/api/organizations/${user.organization_id}/locations/${locationId}`, { headers });
      await loadAdminData(); // Refresh data
      toast.success('Skytebanen ble slettet.');
    } catch (error) {
      console.error('Failed to delete location:', error);
      toast.error('Kunne ikke slette skytebanen. Prøv igjen.');
    }
  };

  const editMember = (member) => {
    setSelectedMember(member);
    setShowMemberEdit(true);
  };

  const deactivateUser = async (userId, userName) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      // Deaktiverer: setter active=false (approved beholdes TRUE slik at det IKKE forveksles med "venter godkjenning")
      await axios.put(`${API_BASE}/api/users/${userId}`, { active: false }, { headers });
      await loadAdminData(); // Refresh data
      toast.success(`Brukeren "${userName}" ble deaktivert og kan ikke lenger logge inn.`);
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Kunne ikke deaktivere brukeren. Prøv igjen.';
      toast.addToast(errorMessage, 'error');
    }
  };

  // PAUSE / UNPAUSE handlers (kun ADMIN / Super Admin)
  const canManagePause = (
    user?.role === 'admin' ||
    user?.role === 'super_admin' ||
    impersonationContext?.originalRole === 'super_admin'
  );

  const openPauseModal = (member) => {
    if (!canManagePause) return;
    setPauseTargetMember(member);
    setPauseReason('');
    setShowPauseModal(true);
  };

  const submitPause = async () => {
    if (!pauseTargetMember) return;
    const trimmed = (pauseReason || '').trim();
    if (!trimmed) {
      toast.addToast('Du må fylle ut årsak. Denne vises for medlemmet på login-siden.', 'error');
      return;
    }
    if (trimmed.length > 500) {
      toast.addToast('Årsak kan maks være 500 tegn.', 'error');
      return;
    }
    setPauseSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(
        `${API_BASE}/api/users/${pauseTargetMember.id}/pause`,
        { reason: trimmed },
        { headers }
      );
      toast.success(`${pauseTargetMember.full_name} satt på PAUSE 🟡`);
      setShowPauseModal(false);
      setPauseTargetMember(null);
      setPauseReason('');
      await loadAdminData();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Kunne ikke sette på pause';
      toast.addToast(msg, 'error');
    } finally {
      setPauseSubmitting(false);
    }
  };

  const unpauseMember = async (member) => {
    if (!canManagePause) return;
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API_BASE}/api/users/${member.id}/unpause`, {}, { headers });
      toast.success(`${member.full_name} reaktivert ✅`);
      await loadAdminData();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Kunne ikke reaktivere medlem';
      toast.addToast(msg, 'error');
    }
  };

  const deleteUser = async (userId, userName) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.delete(`${API_BASE}/api/users/${userId}`, { headers });
      await loadAdminData(); // Refresh data
      toast.success(`Brukeren "${userName}" ble permanent slettet. HUSK: Du burde ha eksportert treningsloggen FØR sletting!`, {
        duration: 8000,
        position: 'top-center'
      });
    } catch (error) {
      console.error('Failed to delete user:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Kunne ikke slette brukeren. Prøv igjen.';
      toast.addToast(errorMessage, 'error');
    }
  };

  const handleUserAction = (userId, userName) => {
    // Use confirm with shorter text that fits without scrolling
    const confirmed = window.confirm(
      `⚠️ ADVARSEL: PERMANENT SLETTING ⚠️\n\n` +
      `Bruker: "${userName}"\n\n` +
      `⚠️ DU MÅ FØRST EKSPORTERE TRENINGSLOGGEN!\n\n` +
      `Gå til brukerens profil og eksporter treningsloggen til PDF FØR du sletter.\n\n` +
      `Dette sletter PERMANENT:\n` +
      `• Brukerens konto\n` +
      `• ALLE treningsøkter\n` +
      `• Alle dokumenter\n` +
      `• All innloggingshistorikk\n\n` +
      `Etter sletting er det INGEN måte å få dette tilbake på!\n\n` +
      `Trykk OK for å bekrefte sletting.`
    );
    
    if (confirmed) {
      // Extra confirmation with prompt for critical action
      const confirmation = prompt('Skriv "SLETT" med store bokstaver for å bekrefte:');
      if (confirmation === "SLETT") {
        deleteUser(userId, userName);
      }
    }
  };

  const handleBulkDeleteMembers = async () => {
    if (selectedMembers.length === 0) return;
    
    const deleteConfirm = `⚠️ ADVARSEL: BULK-SLETTING ⚠️

Du er i ferd med å permanent slette ${selectedMembers.length} medlem(mer).

Dette vil PERMANENT slette:
✓ Alle valgte brukeres kontoer
✓ ALLE deres treningsøkter
✓ Alle dokumenter
✓ All innloggingshistorikk

⚠️ DU BØR FØRST EKSPORTERE UT ALLE TRENINGSOPPFØRINGENE!

Skriv "SLETT ${selectedMembers.length}" for å bekrefte:`;
    
    const confirmation = prompt(deleteConfirm);
    if (confirmation !== `SLETT ${selectedMembers.length}`) return;
    
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.post(`${API_BASE}/api/users/bulk-delete`, 
        selectedMembers,
        { headers }
      );
      
      await loadAdminData(); // Refresh data including deletion logs
      setSelectedMembers([]); // Clear selection
      
      toast.success(
        `${response.data.deleted_count} medlem(mer) ble slettet. HUSK: Du burde ha eksportert treningsloggene!`,
        { duration: 8000, position: 'top-center' }
      );
      
      if (response.data.failed_count > 0) {
        toast.error(`${response.data.failed_count} sletting(er) feilet`, { duration: 5000 });
      }
    } catch (error) {
      console.error('Failed to bulk delete members:', error);
      toast.error('Kunne ikke slette medlemmer. Prøv igjen.');
    }
  };

  const toggleMemberSelection = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllMembers = () => {
    if (selectedMembers.length === filteredMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredMembers.map(m => m.id));
    }
  };

  // ============== EMAIL CHANGE & BACKUP FUNCTIONS ==============
  
  const openEmailChangeModal = (member) => {
    setEmailChangeMember(member);
    setNewEmailAddress('');
    setEmailChangeReason('');
    setBackupCreated(false);
    setShowEmailChangeModal(true);
  };

  const createMemberBackup = async (memberId) => {
    setIsCreatingBackup(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.post(`${API_BASE}/api/admin/users/${memberId}/backup`, {}, { 
        headers,
        responseType: 'blob'  // Important: receive as blob for ZIP file
      });
      
      // Create download link for ZIP file
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'backup.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setBackupCreated(true);
      toast.success('Backup (ZIP-fil) opprettet og lastet ned!');
    } catch (error) {
      console.error('Failed to create backup:', error);
      toast.error('Kunne ikke opprette backup. Prøv igjen.');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleEmailChange = async () => {
    if (!emailChangeMember || !newEmailAddress.trim()) {
      toast.error('Vennligst fyll inn ny e-postadresse');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmailAddress.trim())) {
      toast.error('Ugyldig e-postformat');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.put(
        `${API_BASE}/api/admin/users/${emailChangeMember.id}/email`,
        {
          new_email: newEmailAddress.trim().toLowerCase(),
          reason: emailChangeReason.trim() || null
        },
        { headers }
      );

      if (response.data.success) {
        toast.success(`E-post endret fra ${response.data.old_email} til ${response.data.new_email}`);
        setShowEmailChangeModal(false);
        setEmailChangeMember(null);
        setNewEmailAddress('');
        setEmailChangeReason('');
        setBackupCreated(false);
        await loadAdminData(); // Refresh member list
      }
    } catch (error) {
      console.error('Failed to change email:', error);
      const errorMessage = error?.response?.data?.detail || 'Kunne ikke endre e-post. Prøv igjen.';
      toast.error(errorMessage);
    }
  };

  const loadEmailAuditLog = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/admin/email-change-audit`, { headers });
      setEmailAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      console.error('Failed to load email audit log:', error);
      toast.error('Kunne ikke laste audit-logg');
    }
  };

  // ============== END EMAIL CHANGE & BACKUP FUNCTIONS ==============

  const verifySession = async (sessionId, verified) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API_BASE}/api/training/sessions/${sessionId}/verify`, null, {
        params: { verified },
        headers
      });

      // Refresh the sessions list
      await loadAdminData();
      
      toast.success(verified ? 'Økten er godkjent!' : 'Økten er avvist');
    } catch (error) {
      console.error('Failed to verify session:', error);
      toast.error('Kunne ikke oppdatere økten. Prøv igjen.');
    }
  };

  // Toggle session selection for bulk approval
  const toggleSessionForApproval = (sessionId) => {
    setSelectedSessionsForApproval(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Select all visible sessions for approval (only non-verified ones)
  const selectAllForApproval = () => {
    const nonVerifiedSessions = paginatedSessions
      .filter(session => !session.verified)
      .map(session => session.id);
    setSelectedSessionsForApproval(new Set(nonVerifiedSessions));
  };

  // Deselect all sessions for approval
  const deselectAllForApproval = () => {
    setSelectedSessionsForApproval(new Set());
  };

  // Bulk approve selected sessions
  const bulkApproveSessions = async () => {
    if (selectedSessionsForApproval.size === 0) {
      toast.error('Velg minst én trening for godkjenning');
      return;
    }

    const confirmed = window.confirm(
      `Er du sikker på at du vil godkjenne ${selectedSessionsForApproval.size} valgte treninger?\n\n` +
      `Dette vil sette verified=true for alle valgte økter.`
    );

    if (!confirmed) return;

    setBulkApprovingInProgress(true);

    try {
      const sessionIds = Array.from(selectedSessionsForApproval);
      
      // Call backend to approve multiple sessions
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.post(
        `${API_BASE}/api/training/sessions/bulk-approve`,
        { session_ids: sessionIds },
        { headers }
      );

      toast.success(`✅ ${response.data.approved_count} treninger godkjent!`);
      
      // Refresh data
      await loadAdminData();
      
      // Clear selection and exit bulk mode
      setSelectedSessionsForApproval(new Set());
      setBulkApproveMode(false);
      
    } catch (error) {
      console.error('Bulk approve error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Godkjenning feilet: ${errorMsg}`);
    } finally {
      setBulkApprovingInProgress(false);
    }
  };

  // Delete single session with warning
  const deleteSession = async (sessionId) => {
    // Show warning toast first
    toast.warning('⚠️ Advarsel: Denne handlingen kan ikke angres!', {
      duration: 3000,
      position: 'top-center'
    });
    
    // Small delay to let user see the warning
    setTimeout(async () => {
      const confirmed = window.confirm('Er du sikker på at du vil slette denne treningsøkten? Dette kan ikke angres.');
      
      if (confirmed) {
        try {
          const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
          await axios.delete(`${API_BASE}/api/training/sessions/${sessionId}`, { headers });

          // Refresh the sessions list
          await loadAdminData();
          
          toast.success('Økten er slettet!');
        } catch (error) {
          console.error('Failed to delete session:', error);
          toast.error('Kunne ikke slette økten. Prøv igjen.');
        }
      }
    }, 500);
  };

  // Simple bulk delete functions (from TrainingLog.js)
  const toggleSessionSelection = (sessionId) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const selectAllSessions = () => {
    const currentPageIds = paginatedSessions.map(s => s.id);
    setSelectedSessions(new Set([...selectedSessions, ...currentPageIds]));
  };

  const clearAllSelections = () => {
    setSelectedSessions(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) {
      toast.error('Ingen treningsøkter er valgt for sletting');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const deletePromises = Array.from(selectedSessions).map(sessionId =>
        axios.delete(`${API_BASE}/api/training/sessions/${sessionId}`, { headers })
      );

      await Promise.all(deletePromises);
      
      toast.success(`${selectedSessions.size} treningsøkter slettet`);
      
      // Clear selections and reload sessions
      setSelectedSessions(new Set());
      setBulkDeleteMode(false);
      setShowBulkDeleteConfirm(false);
      await loadAdminData();
      
    } catch (error) {
      console.error('Failed to delete sessions:', error);
      toast.error('Kunne ikke slette treningsøkter. Prøv igjen.');
    }
  };

  // Load deleted sessions for the deletion log
  const loadDeletedSessions = async () => {
    if (!user?.organization?.id) return;
    
    setLoadingDeletedSessions(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(
        `${API_BASE}/api/training/sessions/audit-history?organization_id=${user.organization.id}&limit=50`,
        { headers }
      );
      setDeletedSessions(response.data);
    } catch (error) {
      console.error('Failed to load audit history:', error);
      toast.error('Kunne ikke hente endringslogg.');
    } finally {
      setLoadingDeletedSessions(false);
    }
  };

  // Load member audit logs (all changes to members - creates, updates, deletes)
  const loadMemberAuditLogs = async () => {
    setLoadingAuditLogs(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(
        `${API_BASE}/api/member-audit-logs?limit=50`,
        { headers }
      );
      setMemberAuditLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load member audit logs:', error);
      // Fallback to deletion logs if audit endpoint doesn't exist
      setMemberAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  // Restore a deleted session
  const restoreSession = async (sessionId) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(
        `${API_BASE}/api/training/sessions/${sessionId}/restore`,
        {},
        { headers }
      );
      
      toast.success('Oppføring gjenopprettet!');
      
      // Reload both lists
      await Promise.all([
        loadAdminData(),
        loadDeletedSessions()
      ]);
      
    } catch (error) {
      console.error('Failed to restore session:', error);
      toast.error('Kunne ikke gjenopprette oppføringen. Prøv igjen.');
    }
  };

  // Edit session (training log entry)
  const saveSessionEdit = async () => {
    if (!editingSession) return;
    
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Prepare the update data
      const updateData = {
        date: editSessionData.date,
        activity: editSessionData.activity,
        location_id: editSessionData.location_id,
        range_officer_name: editSessionData.range_officer_name,
        shooting_branch: editSessionData.shooting_branch,
        admin_notes: editSessionData.admin_notes || ''
      };
      
      // Only include user_id if it has changed
      if (editSessionData.user_id && editSessionData.user_id !== editingSession.user_id) {
        updateData.user_id = editSessionData.user_id;
      }
      
      console.log('🔧 Saving session edit:', {
        sessionId: editingSession.id,
        oldActivity: editingSession.activity,
        newActivity: updateData.activity,
        updateData
      });
      
      const response = await axios.put(
        `${API_BASE}/api/training/sessions/${editingSession.id}`,
        updateData,
        { headers }
      );
      
      console.log('✅ Session update response:', response.data);
      
      toast.success('Treningslogg oppdatert!');
      
      // Close modal and reload data
      setShowEditSessionModal(false);
      setEditingSession(null);
      await Promise.all([
        loadAdminData(),
        loadDeletedSessions() // Reload to show edit history
      ]);
      
    } catch (error) {
      console.error('Failed to update session:', error);
      toast.error('Kunne ikke oppdatere treningsloggen. Prøv igjen.');
    }
  };

  // Filter and search members
  const filteredMembers = members.filter(member => {
    // ALWAYS exclude soft-deleted members from Admin/Skyteleder views
    if (member.is_deleted === true) return false;

    // Search filter
    const matchesSearch = memberSearch === '' || 
      member.full_name.toLowerCase().includes(memberSearch.toLowerCase()) ||
      member.email.toLowerCase().includes(memberSearch.toLowerCase());
    
    // Role/status filter
    let matchesFilter = true;
    switch (memberFilter) {
      case 'admin':
        matchesFilter = member.role === 'admin';
        break;
      case 'skyteleder':
        matchesFilter = member.role === 'org_admin';
        break;
      case 'member':
        matchesFilter = member.role === 'member';
        break;
      case 'pending':
        // Kun EKTE ventende godkjenning (ikke deaktiverte eller slettede)
        matchesFilter = !member.approved && member.active !== false && member.is_deleted !== true;
        break;
      case 'deactivated':
        // Deaktiverte: active=false. Skiller nå tydelig fra "Venter"-status.
        matchesFilter = member.active === false && member.is_deleted !== true;
        break;
      case 'paused':
        matchesFilter = member.is_paused === true;
        break;
      case 'all':
      default:
        // Standard liste: ekskluder deaktiverte så de ikke forveksles med nye medlemmer
        matchesFilter = member.active !== false;
    }
    
    return matchesSearch && matchesFilter;
  });

  // NEW: Sorting
  const sortedMembers = [...filteredMembers].sort((a, b) => {
    let aValue = a[memberSortField];
    let bValue = b[memberSortField];
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    // Convert to lowercase for case-insensitive sorting
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    // Sort based on direction
    if (memberSortDirection === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });

  // NEW: Conditional Pagination (only if > 100 members)
  const totalMembers = sortedMembers.length;
  const shouldPaginate = totalMembers > 100;
  const totalPages = shouldPaginate ? Math.ceil(totalMembers / membersPerPage) : 1;
  const startIndex = shouldPaginate ? (memberPage - 1) * membersPerPage : 0;
  const paginatedMembers = shouldPaginate 
    ? sortedMembers.slice(startIndex, startIndex + membersPerPage)
    : sortedMembers;

  // Reset page when filter changes
  const handleFilterChange = (newFilter) => {
    setMemberFilter(newFilter);
    setMemberPage(1);
  };

  const handleSearchChange = (newSearch) => {
    setMemberSearch(newSearch);
    setMemberPage(1);
  };

  // NEW: Handle column sorting
  const handleSort = (field) => {
    if (memberSortField === field) {
      // Toggle direction if same field
      setMemberSortDirection(memberSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setMemberSortField(field);
      setMemberSortDirection('asc');
    }
  };

  // Get dugnad date range based on filter
  const getDugnadDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (dugnadDateFilter) {
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      case '30':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case '90':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case '365':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'all':
        startDate = new Date(2000, 0, 1);
        endDate = now;
        break;
      case 'custom':
        startDate = dugnadCustomStartDate ? new Date(dugnadCustomStartDate) : new Date(now.getFullYear(), 0, 1);
        endDate = dugnadCustomEndDate ? new Date(dugnadCustomEndDate + 'T23:59:59') : now;
        break;
      default:
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    }
    
    return { startDate, endDate };
  };

  // Calculate dugnad counts for all members based on selected date filter
  const calculateDugnadCounts = (activityToCount = selectedDugnadActivity) => {
    const counts = {};
    
    const { startDate, endDate } = getDugnadDateRange();
    
    // Count selected activity per member
    sessions.forEach(session => {
      const activity = session.activity || session.training_type || '';
      const userId = session.user_id;
      
      // Check if it matches the selected dugnad activity (case-insensitive)
      if (activity.toLowerCase() !== activityToCount.toLowerCase()) return;
      
      // Check date range
      const sessionDate = new Date(session.start_time);
      if (sessionDate < startDate || sessionDate > endDate) return;
      
      // Count for this member
      counts[userId] = (counts[userId] || 0) + 1;
    });
    
    setMemberDugnadCounts(counts);
    return counts;
  };

  // Always calculate dugnad counts on mount and when sessions, activity, or date filter changes
  useEffect(() => {
    calculateDugnadCounts();
  }, [sessions, selectedDugnadActivity, dugnadDateFilter, dugnadCustomStartDate, dugnadCustomEndDate]);

  // Handle dugnad activity change and auto-save to organization settings
  const handleDugnadActivityChange = async (newActivity) => {
    setSelectedDugnadActivity(newActivity);
    
    // Auto-save to organization settings
    try {
      await axios.put(`${API_BASE}/api/organizations/${user.organization.id}`, {
        dugnad_activity: newActivity
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      console.error('Failed to save dugnad activity preference:', error);
    }
  };

  // Load saved dugnad activity preference from organization
  useEffect(() => {
    if (user?.organization?.dugnad_activity) {
      setSelectedDugnadActivity(user.organization.dugnad_activity);
    }
  }, [user?.organization?.dugnad_activity]);

  // Generate PDF for members who haven't met the dugnad requirement
  const generateDugnadMissingPDF = async () => {
    setIsGeneratingDugnadPdf(true);
    
    try {
      const jsPDF = (await import('jspdf')).default;
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const currentYear = new Date().getFullYear();
      
      // Profile colors
      const primaryColor = [234, 179, 8]; // Yellow (#EAB308)
      const darkBg = [31, 41, 55]; // Dark gray (#1F2937)
      const textWhite = [255, 255, 255];
      const textGray = [156, 163, 175];
      
      // Header background
      pdf.setFillColor(...darkBg);
      pdf.rect(0, 0, pageWidth, 45, 'F');
      
      // Yellow accent line
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 45, pageWidth, 3, 'F');
      
      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.setTextColor(...textWhite);
      pdf.text('Dugnadsoversikt', margin, 20);
      
      // Subtitle
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.setTextColor(...primaryColor);
      pdf.text(`Medlemmer som mangler dugnadskrav - ${currentYear}`, margin, 30);
      
      // Organization name
      pdf.setFontSize(10);
      pdf.setTextColor(...textGray);
      pdf.text(user?.organization?.name || 'Organisasjon', margin, 38);
      
      // Info box
      let yPos = 58;
      pdf.setFillColor(249, 250, 251); // Light gray background
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 20, 3, 3, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Aktivitetstype: ${selectedDugnadActivity}`, margin + 5, yPos + 8);
      pdf.text(`Krav for gullkrone: ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()}${dugnadThreshold > 1 ? 'er' : ''}`, margin + 5, yPos + 15);
      
      // Count members
      const approvedMembers = members.filter(m => m.approved);
      const membersWithoutEnough = approvedMembers.filter(m => (memberDugnadCounts[m.id] || 0) < dugnadThreshold);
      
      pdf.setTextColor(...primaryColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${membersWithoutEnough.length} av ${approvedMembers.length} medlemmer mangler kravet`, pageWidth - margin - 70, yPos + 12);
      
      yPos += 30;
      
      // Table header
      pdf.setFillColor(...darkBg);
      pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...textWhite);
      pdf.text('Navn', margin + 5, yPos + 7);
      pdf.text('E-post', margin + 70, yPos + 7);
      pdf.text('Antall', pageWidth - margin - 25, yPos + 7);
      pdf.text('Mangler', pageWidth - margin - 10, yPos + 7);
      
      yPos += 12;
      
      // Table rows
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      
      // Sort by dugnad count (ascending - those with 0 first)
      const sortedMembers = [...membersWithoutEnough].sort((a, b) => 
        (memberDugnadCounts[a.id] || 0) - (memberDugnadCounts[b.id] || 0)
      );
      
      sortedMembers.forEach((member, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = 20;
          
          // Repeat header on new page
          pdf.setFillColor(...darkBg);
          pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(...textWhite);
          pdf.text('Navn', margin + 5, yPos + 7);
          pdf.text('E-post', margin + 70, yPos + 7);
          pdf.text('Antall', pageWidth - margin - 25, yPos + 7);
          pdf.text('Mangler', pageWidth - margin - 10, yPos + 7);
          yPos += 12;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
        }
        
        // Alternating row background
        if (index % 2 === 0) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, yPos - 3, pageWidth - (margin * 2), 8, 'F');
        }
        
        const count = memberDugnadCounts[member.id] || 0;
        const missing = dugnadThreshold - count;
        
        pdf.setTextColor(31, 41, 55);
        pdf.text(member.full_name?.substring(0, 35) || 'Ukjent', margin + 5, yPos + 2);
        
        pdf.setTextColor(107, 114, 128);
        pdf.text(member.email?.substring(0, 35) || '-', margin + 70, yPos + 2);
        
        // Count column
        if (count > 0) {
          pdf.setTextColor(234, 179, 8); // Yellow
        } else {
          pdf.setTextColor(156, 163, 175); // Gray
        }
        pdf.text(count.toString(), pageWidth - margin - 23, yPos + 2);
        
        // Missing column (red)
        pdf.setTextColor(239, 68, 68);
        pdf.text(missing.toString(), pageWidth - margin - 5, yPos + 2);
        
        yPos += 8;
      });
      
      // Footer
      yPos = pageHeight - 15;
      pdf.setFontSize(8);
      pdf.setTextColor(...textGray);
      pdf.text(`Generert: ${new Date().toLocaleDateString('nb-NO')} kl. ${new Date().toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`, margin, yPos);
      pdf.text('Aktivlogg', pageWidth - margin - 15, yPos);
      
      // Save PDF
      const fileName = `dugnad-mangler-${currentYear}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.addToast(`PDF lastet ned: ${membersWithoutEnough.length} medlemmer mangler kravet`, 'success');
    } catch (error) {
      console.error('Failed to generate dugnad PDF:', error);
      toast.addToast('Kunne ikke generere PDF', 'error');
    } finally {
      setIsGeneratingDugnadPdf(false);
    }
  };

  // Generate PDF for members who HAVE met the dugnad requirement
  const generateDugnadCompletedPDF = async () => {
    setIsGeneratingDugnadPdf(true);
    
    try {
      const jsPDF = (await import('jspdf')).default;
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const currentYear = new Date().getFullYear();
      
      // Profile colors
      const primaryColor = [34, 197, 94]; // Green (#22C55E)
      const goldColor = [234, 179, 8]; // Yellow (#EAB308)
      const darkBg = [31, 41, 55]; // Dark gray (#1F2937)
      const textWhite = [255, 255, 255];
      const textGray = [156, 163, 175];
      
      // Header background
      pdf.setFillColor(...darkBg);
      pdf.rect(0, 0, pageWidth, 45, 'F');
      
      // Green accent line (success color)
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 45, pageWidth, 3, 'F');
      
      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.setTextColor(...textWhite);
      pdf.text('Dugnadsoversikt', margin, 20);
      
      // Subtitle
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.setTextColor(...goldColor);
      pdf.text(`Medlemmer med gullkrone-status - ${currentYear}`, margin, 30);
      
      // Organization name
      pdf.setFontSize(10);
      pdf.setTextColor(...textGray);
      pdf.text(user?.organization?.name || 'Organisasjon', margin, 38);
      
      // Info box
      let yPos = 58;
      pdf.setFillColor(240, 253, 244); // Light green background
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 20, 3, 3, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(22, 101, 52); // Dark green
      pdf.text(`Aktivitetstype: ${selectedDugnadActivity}`, margin + 5, yPos + 8);
      pdf.text(`Krav for gullkrone: ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()}${dugnadThreshold > 1 ? 'er' : ''}`, margin + 5, yPos + 15);
      
      // Count members
      const approvedMembers = members.filter(m => m.approved);
      const membersWithEnough = approvedMembers.filter(m => (memberDugnadCounts[m.id] || 0) >= dugnadThreshold);
      
      pdf.setTextColor(...goldColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${membersWithEnough.length} av ${approvedMembers.length} medlemmer har oppfylt kravet`, pageWidth - margin - 75, yPos + 12);
      
      yPos += 30;
      
      // Table header
      pdf.setFillColor(...darkBg);
      pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...textWhite);
      pdf.text('Navn', margin + 5, yPos + 7);
      pdf.text('E-post', margin + 70, yPos + 7);
      pdf.text('Antall', pageWidth - margin - 15, yPos + 7);
      
      yPos += 12;
      
      // Table rows
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      
      // Sort by dugnad count (descending - those with most first)
      const sortedMembers = [...membersWithEnough].sort((a, b) => 
        (memberDugnadCounts[b.id] || 0) - (memberDugnadCounts[a.id] || 0)
      );
      
      sortedMembers.forEach((member, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = 20;
          
          // Repeat header on new page
          pdf.setFillColor(...darkBg);
          pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(...textWhite);
          pdf.text('Navn', margin + 5, yPos + 7);
          pdf.text('E-post', margin + 70, yPos + 7);
          pdf.text('Antall', pageWidth - margin - 15, yPos + 7);
          yPos += 12;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
        }
        
        // Alternating row background
        if (index % 2 === 0) {
          pdf.setFillColor(240, 253, 244); // Light green
          pdf.rect(margin, yPos - 3, pageWidth - (margin * 2), 8, 'F');
        }
        
        const count = memberDugnadCounts[member.id] || 0;
        
        pdf.setTextColor(31, 41, 55);
        pdf.text(member.full_name?.substring(0, 35) || 'Ukjent', margin + 5, yPos + 2);
        
        pdf.setTextColor(107, 114, 128);
        pdf.text(member.email?.substring(0, 35) || '-', margin + 70, yPos + 2);
        
        // Count column (gold/green)
        pdf.setTextColor(234, 179, 8); // Gold
        pdf.text(count.toString(), pageWidth - margin - 12, yPos + 2);
        
        yPos += 8;
      });
      
      // Footer
      yPos = pageHeight - 15;
      pdf.setFontSize(8);
      pdf.setTextColor(...textGray);
      pdf.text(`Generert: ${new Date().toLocaleDateString('nb-NO')} kl. ${new Date().toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`, margin, yPos);
      pdf.text('Aktivlogg', pageWidth - margin - 15, yPos);
      
      // Save PDF
      const fileName = `dugnad-oppfylt-${currentYear}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.addToast(`PDF lastet ned: ${membersWithEnough.length} medlemmer har oppfylt kravet`, 'success');
    } catch (error) {
      console.error('Failed to generate dugnad PDF:', error);
      toast.addToast('Kunne ikke generere PDF', 'error');
    } finally {
      setIsGeneratingDugnadPdf(false);
    }
  };

  // Helper function to check if member has achieved gold crown status
  const hasDugnadGoldCrown = (memberId) => {
    return (memberDugnadCounts[memberId] || 0) >= dugnadThreshold;
  };

  // Get dugnad count for display
  const getDugnadCount = (memberId) => {
    return memberDugnadCounts[memberId] || 0;
  };

  // Enhanced filter sessions with all filters like TrainingLog.js
  const filteredSessions = sessions.filter(session => {
    // Member filter (specific member selection)
    if (sessionMemberFilter) {
      if (session.user_id !== sessionMemberFilter) return false;
    }
    
    // General search filter (aktivitet, sted, skyteleder, notater - IKKE medlemsnavn)
    if (sessionSearch) {
      const searchLower = sessionSearch.toLowerCase();
      const searchFields = [
        session.activity,
        session.training_type,
        session.location_name,
        session.verified_by,
        session.notes
      ].filter(Boolean);
      
      const matchesSearch = searchFields.some(field => 
        field.toString().toLowerCase().includes(searchLower)
      );
      
      if (!matchesSearch) return false;
    }

    // Date filter
    if (sessionDateFilter !== 'alle') {
      const sessionDate = new Date(session.start_time);
      if (!isWithinDateRange(sessionDate, sessionDateFilter)) return false;
    }

    // Activity types filter (multiple selection with checkboxes)
    if (selectedActivities.size > 0) {
      const activity = session.activity || session.training_type || '';
      if (!selectedActivities.has(activity)) return false;
    }
    
    // Legacy activity types filter (keep for backward compatibility)
    if (sessionActivityTypes.size > 0) {
      const activity = session.activity || session.training_type || '';
      if (!sessionActivityTypes.has(activity)) return false;
    }

    // Location filter
    const matchesLocation = sessionLocationFilter === '' || session.location_id === sessionLocationFilter;
    
    // Officer filter
    const matchesOfficer = sessionOfficerFilter === '' || session.verified_by_id === sessionOfficerFilter;
    
    // Status filter
    let matchesStatus = true;
    if (sessionStatusFilter === 'verified') {
      matchesStatus = session.verified === true;
    } else if (sessionStatusFilter === 'pending') {
      matchesStatus = session.verified === false || session.verified === null;
    }

    // Branch filter
    if (sessionBranchFilter !== 'alle') {
      const sessionBranch = getBranchFromLocation(session);
      if (!sessionBranch || sessionBranch.key !== sessionBranchFilter) return false;
    }
    
    return matchesLocation && matchesOfficer && matchesStatus;
  }).sort((a, b) => {
    // Apply sorting
    let aValue, bValue;
    
    switch (sortColumn) {
      case 'start_time':
        aValue = new Date(a.start_time);
        bValue = new Date(b.start_time);
        break;
      case 'user_name':
        aValue = a.user_name || '';
        bValue = b.user_name || '';
        break;
      case 'activity':
        aValue = a.activity || a.training_type || '';
        bValue = b.activity || b.training_type || '';
        break;
      case 'location_name':
        aValue = a.location_name || '';
        bValue = b.location_name || '';
        break;
      case 'verified_by':
        aValue = a.verified_by || '';
        bValue = b.verified_by || '';
        break;
      case 'verified':
        aValue = a.verified ? 1 : 0;
        bValue = b.verified ? 1 : 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Session pagination logic for Admin > LOGG
  const totalSessionPages = Math.ceil(filteredSessions.length / sessionsPerPage);
  const sessionStartIndex = (sessionPage - 1) * sessionsPerPage;
  const sessionEndIndex = sessionStartIndex + sessionsPerPage;
  const paginatedSessions = filteredSessions.slice(sessionStartIndex, sessionEndIndex);

  // Get filtered member's documents for PDF export
  const getFilteredMemberDocs = () => {
    if (!sessionMemberFilter) return [];
    
    const memberData = members.find(m => m.id === sessionMemberFilter);
    if (!memberData) return [];
    
    const availableDocs = [];
    
    // Helper function to safely check and add URLs
    const addDocUrl = (url, name) => {
      if (!url || typeof url !== 'string') return; // Skip null/undefined/non-string values
      
      let fullUrl = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        fullUrl = url.startsWith('/api') ? `${window.location.origin}${url}` : `${API_BASE}${url}`;
      }
      availableDocs.push({
        url: fullUrl,
        name: name,
        filename: url.split('/').pop()
      });
    };
    
    // Startkort
    const startkortUrl = memberData.startkort_s3_url || memberData.startkort_url;
    if (startkortUrl) {
      addDocUrl(startkortUrl, 'Startkort');
    }
    
    // Medlemskort - filter out null/undefined values first
    const medlemskortUrls = (memberData.medlemskort_s3_urls || memberData.medlemskort_urls || []).filter(Boolean);
    medlemskortUrls.forEach((url, idx) => {
      addDocUrl(url, `Medlemskort ${medlemskortUrls.length > 1 ? idx + 1 : ''}`.trim());
    });
    
    // Våpenkort - filter out null/undefined values first
    const weaponCertUrls = (memberData.weapon_cert_s3_urls || memberData.weapon_cert_urls || []).filter(Boolean);
    weaponCertUrls.forEach((url, idx) => {
      addDocUrl(url, `Våpenkort/Diplom ${weaponCertUrls.length > 1 ? idx + 1 : ''}`.trim());
    });
    
    return availableDocs;
  };

  // Open PDF options dialog for Klubb-logg
  const openKlubbloggPdfDialog = (pdfType) => {
    if (!sessionMemberFilter) {
      toast.warning('Velg et enkelt medlem i filteret for å laste ned PDF med vedlegg.');
      // Direct download without documents
      if (pdfType === 'politi') {
        downloadOrgPolitiPDF([]);
      } else {
        downloadOrgStandardPDF([]);
      }
      return;
    }
    
    setKlubbloggPdfType(pdfType);
    const docs = getFilteredMemberDocs();
    setSelectedMemberDocs(docs.map(d => d.url)); // Pre-select all
    setShowKlubbloggPdfOptions(true);
  };

  // Helper function to merge documents into PDF (same as TrainingLog.js)
  const mergePDFWithDocuments = async (pdfBytes, selectedDocUrls) => {
    if (!selectedDocUrls || selectedDocUrls.length === 0) {
      return pdfBytes;
    }

    try {
      const { PDFDocument } = await import('pdf-lib');
      const mainPdf = await PDFDocument.load(pdfBytes);
      
      for (const docUrl of selectedDocUrls) {
        try {
          console.log(`📄 Processing document: ${docUrl}`);
          
          const isCloudFrontOrS3 = docUrl.includes('cloudfront.net') || docUrl.includes('amazonaws.com');
          const isCloudinary = docUrl.includes('cloudinary.com');
          const isExternalUrl = docUrl.startsWith('http://') || docUrl.startsWith('https://');
          
          let fetchUrl = docUrl;
          const fetchOptions = {};
          
          if (isCloudFrontOrS3) {
            fetchUrl = `${API_BASE}/api/documents/proxy?url=${encodeURIComponent(docUrl)}`;
            fetchOptions.headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
          } else if (isCloudinary) {
            const urlParts = docUrl.split('/');
            const filename = urlParts[urlParts.length - 1];
            fetchUrl = urlParts.slice(0, -1).join('/') + '/' + encodeURIComponent(filename);
          } else if (!isExternalUrl) {
            fetchOptions.headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
          }
          
          const response = await fetch(fetchUrl, fetchOptions);
          if (!response.ok) continue;
          
          const docBytes = await response.arrayBuffer();
          const contentType = response.headers.get('content-type');
          
          if (contentType && contentType.includes('pdf')) {
            const docPdf = await PDFDocument.load(docBytes);
            const copiedPages = await mainPdf.copyPages(docPdf, docPdf.getPageIndices());
            copiedPages.forEach(page => mainPdf.addPage(page));
          } else if (contentType && contentType.startsWith('image/')) {
            const page = mainPdf.addPage();
            const { width, height } = page.getSize();
            
            let image;
            if (contentType.includes('png')) {
              image = await mainPdf.embedPng(docBytes);
            } else if (contentType.includes('jpeg') || contentType.includes('jpg')) {
              image = await mainPdf.embedJpg(docBytes);
            } else continue;
            
            const imageAspect = image.width / image.height;
            const pageAspect = width / height;
            let drawWidth, drawHeight;
            
            if (imageAspect > pageAspect) {
              drawWidth = width * 0.9;
              drawHeight = drawWidth / imageAspect;
            } else {
              drawHeight = height * 0.9;
              drawWidth = drawHeight * imageAspect;
            }
            
            page.drawImage(image, {
              x: (width - drawWidth) / 2,
              y: (height - drawHeight) / 2,
              width: drawWidth,
              height: drawHeight,
            });
          }
        } catch (docError) {
          console.error(`❌ Error merging document:`, docError);
        }
      }
      
      return await mainPdf.save();
    } catch (error) {
      console.error('❌ Error in PDF merge:', error);
      return pdfBytes;
    }
  };

  // Download Politi-PDF for organization (filtered sessions only) - supports document merging
  const downloadOrgPolitiPDF = async (docsToMerge = []) => {
    try {
      if (filteredSessions.length === 0) {
        toast.warning('Ingen treningsøkter å eksportere basert på gjeldende filter.');
        return;
      }

      toast.info('Genererer politi-godkjent PDF fra filtrerte økter...');

      // Only include verified sessions for police PDF
      const verifiedSessions = filteredSessions.filter(s => s.verified);
      
      if (verifiedSessions.length === 0) {
        toast.warning('Ingen verifiserte treningsøkter i det valgte filteret.');
        return;
      }

      // Get the selected activity types from filter (if any)
      const selectedActivityTypes = selectedActivities.size > 0 
        ? Array.from(selectedActivities) 
        : [];

      console.log('📋 Politi-PDF Export:', {
        sessions: verifiedSessions.length,
        activityTypes: selectedActivityTypes.length > 0 ? selectedActivityTypes : 'default NSF',
        branchFilter: sessionBranchFilter
      });

      // Send filtered session IDs, activity types, AND branch filter to backend for PDF generation
      const response = await axios.post(
        `${API_BASE}/api/pdf/generate-police-pdf`,
        {
          session_ids: verifiedSessions.map(s => s.id),
          user_name: verifiedSessions[0]?.user_name || 'Medlem',
          organization_name: user?.organization?.name || 'Organisasjon',
          activityTypes: selectedActivityTypes, // Send selected activity types
          branchFilter: sessionBranchFilter // Send selected shooting branch for member organization
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          responseType: 'arraybuffer' // Changed to arraybuffer for PDF merging
        }
      );

      // Merge with documents if any selected
      let finalPdfBytes = response.data;
      if (docsToMerge && docsToMerge.length > 0) {
        toast.info(`Legger til ${docsToMerge.length} vedlegg...`);
        finalPdfBytes = await mergePDFWithDocuments(response.data, docsToMerge);
      }

      // Download the PDF
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const today = new Date().toISOString().split('T')[0];
      const userName = verifiedSessions[0]?.user_name?.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '-') || 'medlem';
      link.download = `politi-aktivitetslogg-${userName}-${today}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const docText = docsToMerge.length > 0 ? ` med ${docsToMerge.length} vedlegg` : '';
      toast.success(`Politi-godkjent aktivitetslogg lastet ned${docText}! ${verifiedSessions.length} verifiserte økter.`);
    } catch (error) {
      console.error('Failed to generate Politi PDF:', error);
      toast.error('Kunne ikke generere Politi-PDF. Prøv igjen senere.');
    }
  };

  // Download Standard-PDF for organization - supports document merging
  const downloadOrgStandardPDF = async (docsToMerge = []) => {
    try {
      if (filteredSessions.length === 0) {
        toast.warning('Ingen treningsøkter å eksportere basert på gjeldende filter.');
        return;
      }

      // Import jsPDF dynamically
      const { default: jsPDF } = await import('jspdf');
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Header
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('TRENINGSLOGG - NSF (Trening)', 105, 20, { align: 'center' });

      // Get member info if filtering on specific member
      let memberName = null;
      let memberBirthNumber = null;
      if (sessionMemberFilter) {
        const selectedMemberData = members.find(m => m.id === sessionMemberFilter);
        if (selectedMemberData) {
          memberName = selectedMemberData.full_name;
          memberBirthNumber = selectedMemberData.birth_number;
        }
      }

      // Organization and member info
      pdf.setFontSize(10);
      
      // If filtering on specific member, show member info prominently
      if (memberName) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Medlem:', 20, 40);
        pdf.setFont('helvetica', 'normal');
        pdf.text(memberName, 40, 40);
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Fødselsdato:', 20, 50);
        pdf.setFont('helvetica', 'normal');
        pdf.text(memberBirthNumber || 'Ikke oppgitt', 50, 50);
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Organisasjon:', 20, 60);
        pdf.setFont('helvetica', 'normal');
        pdf.text(user?.organization?.name || 'Klubb ikke tilgjengelig', 55, 60);
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Generert:', 20, 70);
        pdf.setFont('helvetica', 'normal');
        pdf.text(new Date().toLocaleDateString('no-NO'), 50, 70);
      } else {
        // Original layout for all members view
        pdf.setFont('helvetica', 'bold');
        pdf.text('Organisasjon:', 20, 40);
        pdf.setFont('helvetica', 'normal');
        pdf.text(user?.organization?.name || 'Klubb ikke tilgjengelig', 55, 40);
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Medlemsorganisasjon:', 20, 50);
        pdf.setFont('helvetica', 'normal');
        pdf.text('NSF', 70, 50);
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Generert:', 20, 60);
        pdf.setFont('helvetica', 'normal');
        pdf.text(new Date().toLocaleDateString('no-NO'), 50, 60);
      }

      // KRAVSTATUS seksjon - adjust Y position based on member view
      const statusY = memberName ? 85 : 75;
      pdf.setFont('helvetica', 'bold');
      pdf.text('ANTALL ØKTER:', 20, statusY);
      pdf.setTextColor(0, 128, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${filteredSessions.length} treningsøkter`, 20, statusY + 10);

      // Table header - adjust Y position based on member view
      let yPosition = memberName ? 115 : 105;
      pdf.setFillColor(255, 215, 0);
      pdf.rect(20, yPosition - 5, 170, 10, 'F');
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('Dato', 25, yPosition);
      pdf.text('Medlem', 55, yPosition);
      pdf.text('Aktivitet', 95, yPosition);
      pdf.text('Bane/sted', 130, yPosition);
      pdf.text('Verifisert', 165, yPosition);
      
      yPosition += 15;
      
      // Table rows
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      
      filteredSessions.forEach((session, index) => {
        if (yPosition > 245) {
          pdf.addPage();
          yPosition = 30;
        }
        
        if (index % 2 === 1) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(20, yPosition - 5, 170, 10, 'F');
        }
        
        pdf.text(new Date(session.start_time).toLocaleDateString('no-NO'), 25, yPosition);
        pdf.text((session.user_name || 'Ukjent').substring(0, 20), 55, yPosition);
        pdf.text((session.activity || 'Trening').substring(0, 18), 95, yPosition);
        pdf.text((session.location_name || 'Ukjent').substring(0, 18), 130, yPosition);
        pdf.text(session.verified ? 'Ja' : 'Nei', 165, yPosition);
        
        yPosition += 8;
      });

      // Signatur seksjon
      yPosition += 20;
      if (yPosition > 240) {
        pdf.addPage();
        yPosition = 30;
      }
      
      pdf.setDrawColor(0, 0, 0);
      pdf.rect(20, yPosition, 170, 40);
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text('SIGNATUR OG STEMPEL', 25, yPosition + 10);

      // Get PDF bytes for potential document merging
      const pdfBytes = pdf.output('arraybuffer');
      
      // Merge with documents if any selected
      let finalPdfBytes = pdfBytes;
      if (docsToMerge && docsToMerge.length > 0) {
        toast.info(`Legger til ${docsToMerge.length} vedlegg...`);
        finalPdfBytes = await mergePDFWithDocuments(pdfBytes, docsToMerge);
      }
      
      // Download the PDF
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const today = new Date().toISOString().split('T')[0];
      const orgName = (user?.organization?.name || 'Treningslogg').replace(/[^a-zA-Z0-9æøå]/g, '-');
      const filePrefix = memberName ? memberName.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '-') : orgName;
      link.download = `treningslogg-${filePrefix}-${today}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const docText = docsToMerge.length > 0 ? ` med ${docsToMerge.length} vedlegg` : '';
      toast.success(`Standard treningslogg lastet ned${docText}! ${filteredSessions.length} økter.`);
    } catch (error) {
      console.error('Failed to generate Standard PDF:', error);
      toast.error('Kunne ikke generere Standard-PDF. Prøv igjen senere.');
    }
  };

  const downloadFilteredTrainingLog = async (format = 'csv') => {
    try {
      // Bruker filteredSessions som allerede er beregnet
      const sessionData = filteredSessions.map(session => ({
        'Dato': new Date(session.start_time).toLocaleDateString('no-NO'),
        'Tid': new Date(session.start_time).toLocaleTimeString('no-NO', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        'Medlem': session.user_name || 'Ukjent',
        'Aktivitet': session.activity || 'Trening',
        'Bane': session.location_name || 'Ukjent lokasjon',
        'Standplassleder': session.verified_by || 'Ikke godkjent',
        'Status': session.verified ? 'Verifisert' : 'Ikke verifisert',
        'Varighet (min)': session.duration_minutes || 'Ikke registrert'
      }));

      if (sessionData.length === 0) {
        toast.warning('Ingen treningsøkter å eksportere basert på gjeldende filter.');
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      const orgName = (user?.organization?.name || 'Organisasjon').replace(/[^a-zA-Z0-9æøå]/g, '-');

      if (format === 'csv') {
        // CSV Export
        const headers = Object.keys(sessionData[0]);
        const csvContent = [
          headers.join(','), // Header rad
          ...sessionData.map(row => 
            headers.map(header => {
              const value = row[header] || '';
              // Escape commas and quotes in CSV
              return `"${value.toString().replace(/"/g, '""')}"`;
            }).join(',')
          )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.download = `treningslogg-${orgName}-${today}.csv`;
        link.href = URL.createObjectURL(blob);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Treningslogg eksportert som CSV! ${sessionData.length} økter lastet ned.`);
      } else if (format === 'pdf') {
        // PDF Export
        try {
          const jsPDFModule = await import('jspdf');
          const jsPDF = jsPDFModule.default;
          
          const pdf = new jsPDF();
          
          // Header
          pdf.setFontSize(18);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Treningslogg', 20, 20);
          
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${user?.organization?.name || 'Organisasjon'}`, 20, 30);
          pdf.text(`Generert: ${new Date().toLocaleDateString('no-NO')}`, 20, 40);
          pdf.text(`Antall økter: ${sessionData.length}`, 20, 50);
          
          // Table header
          let yPosition = 70;
          pdf.setFont('helvetica', 'bold');
          pdf.text('Dato', 20, yPosition);
          pdf.text('Tid', 45, yPosition);
          pdf.text('Medlem', 70, yPosition);
          pdf.text('Bane', 120, yPosition);
          pdf.text('Status', 160, yPosition);
          
          yPosition += 10;
          pdf.setFont('helvetica', 'normal');
          
          // Table rows
          sessionData.forEach((session, index) => {
            if (yPosition > 270) { // New page if needed
              pdf.addPage();
              yPosition = 20;
            }
            
            // Safe text rendering with fallbacks
            pdf.text(session['Dato'] || '', 20, yPosition);
            pdf.text(session['Tid'] || '', 45, yPosition);
            pdf.text((session['Medlem'] || '').substring(0, 20), 70, yPosition);
            pdf.text((session['Bane'] || '').substring(0, 15), 120, yPosition);
            pdf.text((session['Status'] || '').substring(0, 12), 160, yPosition);
            
            yPosition += 8;
          });
          
          pdf.save(`treningslogg-${orgName}-${today}.pdf`);
          toast.success(`Treningslogg eksportert som PDF! ${sessionData.length} økter lastet ned.`);
        } catch (pdfError) {
          console.error('PDF generation error:', pdfError);
          toast.error('Kunne ikke generere PDF. Prøv igjen eller kontakt support.');
          // Don't re-throw the error to avoid uncaught exception
        }
      }
    } catch (error) {
      console.error('Feil ved eksport av treningslogg:', error);
      const errorMessage = error?.message || error?.response?.data?.detail || 'Kunne ikke eksportere treningslogg. Prøv igjen.';
      toast.addToast(errorMessage, 'error');
    }
  };

  const approveAllPendingSessions = async (skipAmmoCheck = false) => {
    try {
      console.log('Starting approval process...', { sessions: sessions?.length });
      
      // Only approve today's pending sessions
      const todaysPendingSessions = sessions?.filter(s => {
        if (!s || !s.start_time) {
          console.warn('Invalid session found:', s);
          return false;
        }
        return !s.verified && isToday(new Date(s.start_time));
      }) || [];
      
      console.log('Today\'s pending sessions:', todaysPendingSessions.length);
      
      if (todaysPendingSessions.length === 0) {
        toast.info('Ingen treningsøkter fra i dag venter på godkjenning.');
        return;
      }
      
      // Check if any of today's sessions have ammo sales (ammo module must be enabled)
      console.log('Ammo check:', { 
        skipAmmoCheck, 
        ammoModuleEnabled, 
        todaySessionsCount: todaysPendingSessions.length,
        firstSession: todaysPendingSessions[0]
      });
      
      if (!skipAmmoCheck && ammoModuleEnabled) {
        const hasAmmoSales = todaysPendingSessions.some(session => {
          const hasAmmo = session.ammo_purchases && session.ammo_purchases.length > 0;
          console.log('Session ammo check:', { 
            sessionId: session.id, 
            ammo_purchases: session.ammo_purchases,
            hasAmmo 
          });
          return hasAmmo;
        });
        
        console.log('Has any ammo sales?', hasAmmoSales);
        
        if (!hasAmmoSales) {
          console.log('No ammo sales - showing reminder modal');
          // Show reminder modal
          setShowAmmoReminderModal(true);
          return; // Stop here, wait for user choice
        }
      } else {
        console.log('Skipping ammo check:', { skipAmmoCheck, ammoModuleEnabled });
      }
      
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Approve all today's pending sessions
      const approvalPromises = todaysPendingSessions.map(session => {
        console.log('Approving session:', session.id);
        return axios.put(`${API_BASE}/api/training/sessions/${session.id}/verify`, null, {
          params: { verified: true },
          headers
        });
      });
      
      await Promise.all(approvalPromises);
      
      await loadAdminData(); // Refresh data
      toast.success(`${todaysPendingSessions.length} treningsøkter fra i dag ble godkjent.`);
    } catch (error) {
      console.error('Failed to approve today\'s sessions:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Kunne ikke godkjenne dagens treningsøkter. Prøv igjen.';
      toast.addToast(errorMessage, 'error');
    }
  };

  const createTrainingLocation = async (locationData) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API_BASE}/api/organizations/${user?.organization_id}/locations`, locationData, { headers });
      await loadAdminData(); // Refresh data
    } catch (error) {
      console.error('Failed to create location:', error);
      toast.error('Kunne ikke opprette treningslokasjon. Prøv igjen.');
    }
  };

  // Settings Functions
  const loadSettingsData = () => {
    if (user?.organization) {
      setSettingsData({
        ...settingsData,
        organizationName: user.organization.name || '',
        organizationNumber: user.organization.organization_number || '',
        subscriptionPlan: user.organization.subscription_plan || 'start',
        description: user.organization.description || '',
        email: user.organization.email || '',
        phone: user.organization.phone || '',
        website: user.organization.website || '',
        address: user.organization.address || '',
        primaryColor: user.organization.primary_color || '#FFD700',
        secondaryColor: user.organization.secondary_color || '#1F2937',
        backgroundColor: user.organization.background_color || '#111827',
        logoUrl: user.organization.logo_url || '',
        // Prefer settings.* (new location written by upload endpoint) over legacy top-level
        bannerUrls: (user.organization.settings?.bannerUrls?.length
          ? user.organization.settings.bannerUrls
          : user.organization.banner_urls) || [],
        bannerLinks: (user.organization.settings?.bannerUrls?.length
          ? user.organization.settings.bannerLinks
          : user.organization.banner_links) || [],
        bannerExpiryDates: (user.organization.settings?.bannerUrls?.length
          ? user.organization.settings.bannerExpiryDates
          : user.organization.banner_expiry_dates) || [],
        bannerVisible: (user.organization.settings?.bannerUrls?.length
          ? user.organization.settings.bannerVisible
          : user.organization.banner_visible) || [],
        bannerRotationSeconds: user.organization.settings?.bannerRotationSeconds || user.organization.banner_rotation_seconds || 15,
        activeLanguage: user.organization.active_language || 'no',
        memberOrganizations: user.organization.member_organizations || [{clubName: '', organization: 'NSF - Norges Sportsskytterforbund'}],
        shootingBranches: user.organization.shooting_branches || {nsf: true, dfs: false, dssn: false, sws: false, nsu: false, nrof: false, nmsf: false, njff: false, nbif: false, nbsf: false, nssf: false},
        activityTypes: user.organization.activity_types || ['Organisert trening', 'Klubbstevne', 'Dugnad', 'NSF-øvelser'],
        dugnadFeatureEnabled: user.organization.dugnad_feature_enabled !== false, // Default to true
        dugnadActivity: user.organization.dugnad_activity || 'Dugnad',
        dugnadThreshold: user.organization.dugnad_threshold || 3,
        calendarEnabled: user.organization.calendar_enabled !== false, // Default to true
        calendarAttendanceEnabled: user.organization.calendar_attendance_enabled !== false,
        calendarAttendancePublic: user.organization.calendar_attendance_public !== false,
        calendarCapacityEnabled: user.organization.calendar_capacity_enabled === true,
        merkeskytingEnabled: true, // Overstyres av GET /api/merkeskyting/settings
        calendarTimezone: user.organization.calendar_timezone || 'Europe/Oslo',
        calendarDstEnabled: user.organization.calendar_dst_enabled !== false, // Default to true (follow DST)
        calendarDefaultView: user.organization.calendar_default_view || 'week',
        calendarEmbedDefaultView: user.organization.calendar_embed_default_view || 'week',
        messagesEnabled: user.organization.messages_enabled !== false, // Default to true
        clubOperationsEnabled: user.organization.club_operations_enabled === true // Default to false during development
      });
    }
  };

  const copyOrganizationId = () => {
    navigator.clipboard.writeText(user?.organization_id || '')
      .then(() => toast.success('Organisasjons-ID kopiert!'))
      .catch(() => toast.error('Kunne ikke kopiere ID'));
  };

  const resetColorToDefault = (colorType) => {
    const defaults = {
      primary: '#FFD700',
      secondary: '#1F2937',
      background: '#111827'
    };
    
    setSettingsData({
      ...settingsData,
      [`${colorType}Color`]: defaults[colorType]
    });
  };

  const handleColorChange = (colorType, value) => {
    setSettingsData({
      ...settingsData,
      [`${colorType}Color`]: value
    });
    
    // Immediately update CSS variables for live preview
    if (colorType === 'primary') {
      const root = document.documentElement;
      root.style.setProperty('--primary-color', value);
      
      // Calculate hover color (darker)
      const hoverColor = adjustColorBrightness(value, -20);
      root.style.setProperty('--primary-hover-color', hoverColor);
      
      // Calculate text color based on brightness
      const textColor = isColorLight(value) ? '#000000' : '#FFFFFF';
      root.style.setProperty('--primary-text-color', textColor);
      
      console.log('Live color preview updated:', {
        primary: value,
        hover: hoverColor,
        text: textColor
      });
    }
  };

  // Helper functions for color calculations
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const isColorLight = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return false;
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5;
  };

  const adjustColorBrightness = (hex, amount) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    
    const r = Math.max(0, Math.min(255, rgb.r + amount));
    const g = Math.max(0, Math.min(255, rgb.g + amount));
    const b = Math.max(0, Math.min(255, rgb.b + amount));
    
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const handleFileUpload = async (fileType, file) => {
    const isLogo = fileType === 'logo';
    const setUploading = isLogo ? setUploadingLogo : setUploadingBanner;
    
    setUploading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = isLogo ? 'logo' : 'banner';
      const response = await axios.post(
        `${API_BASE}/api/organizations/${user?.organization_id}/upload-${endpoint}`, 
        formData, 
        { 
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (isLogo) {
        setSettingsData({
          ...settingsData,
          logoUrl: response.data.url
        });
      } else {
        // For banners, add to array
        setSettingsData({
          ...settingsData,
          bannerUrls: [...settingsData.bannerUrls, response.data.url],
          bannerLinks: [...settingsData.bannerLinks, ''], // Add empty link for new banner
          bannerExpiryDates: [...settingsData.bannerExpiryDates, ''], // Add empty expiry date
          bannerVisible: [...settingsData.bannerVisible, true] // New banners visible by default
        });
      }
      
      toast.success(`${isLogo ? 'Logo' : 'Banner'} lastet opp!`);
    } catch (error) {
      console.error(`Failed to upload ${fileType}:`, error);
      toast.error(`Kunne ikke laste opp ${isLogo ? 'logo' : 'banner'}. Prøv igjen.`);
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (fileType, index = null) => {
    const isLogo = fileType === 'logo';
    
    // Ensure we have organization_id
    if (!user?.organization_id) {
      toast.error('Ingen organisasjons-ID funnet. Prøv å logge inn på nytt.');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      if (isLogo) {
        const endpoint = 'logo';
        await axios.delete(
          `${API_BASE}/api/organizations/${user.organization_id}/delete-${endpoint}`, 
          { headers }
        );
        
        setSettingsData({
          ...settingsData,
          logoUrl: ''
        });
        
        toast.success('Logo slettet!');
      } else {
        // For banners, delete specific index
        if (index !== null && settingsData.bannerUrls[index]) {
          const bannerUrl = settingsData.bannerUrls[index];
          const filename = bannerUrl.split('/').pop();
          
          console.log(`Deleting banner: org_id=${user.organization_id}, filename=${filename}`);
          
          await axios.delete(
            `${API_BASE}/api/organizations/${user.organization_id}/delete-banner/${filename}`, 
            { headers }
          );
          
          // Remove from arrays
          const newBannerUrls = settingsData.bannerUrls.filter((_, i) => i !== index);
          const newBannerLinks = settingsData.bannerLinks.filter((_, i) => i !== index);
          const newBannerExpiryDates = settingsData.bannerExpiryDates.filter((_, i) => i !== index);
          const newBannerVisible = settingsData.bannerVisible.filter((_, i) => i !== index);
          
          setSettingsData({
            ...settingsData,
            bannerUrls: newBannerUrls,
            bannerLinks: newBannerLinks,
            bannerExpiryDates: newBannerExpiryDates,
            bannerVisible: newBannerVisible
          });
          
          // Refresh user data so banner disappears from homepage too
          if (refreshUser) {
            await refreshUser();
          }
          
          toast.success('Banner slettet!');
        }
      }
    } catch (error) {
      console.error(`Failed to delete ${fileType}:`, error);
      toast.error(`Kunne ikke slette ${isLogo ? 'logo' : 'banner'}. Prøv igjen.`);
    }
  };

  const addActivityType = () => {
    if (newActivityType.trim() && !settingsData.activityTypes.includes(newActivityType.trim())) {
      setSettingsData({
        ...settingsData,
        activityTypes: [...settingsData.activityTypes, newActivityType.trim()]
      });
      setNewActivityType('');
    }
  };

  const removeActivityType = (activityType) => {
    // Prevent deletion of core activity types
    if (activityType === 'Organisert trening' || activityType === 'Klubbstevne') {
      alert('Denne aktivitetstypen kan ikke slettes da den er påkrevd for politirapporter.');
      return;
    }
    
    setSettingsData({
      ...settingsData,
      activityTypes: settingsData.activityTypes.filter(type => type !== activityType)
    });
  };

  const handleShootingBranchChange = (branch, checked) => {
    setSettingsData({
      ...settingsData,
      shootingBranches: {
        ...settingsData.shootingBranches,
        [branch]: checked
      }
    });
  };

  const exportLanguageFile = async () => {
    try {
      // Comprehensive language file containing ALL frontend text
      const languageData = {
        // Navigation
        "nav": {
          "home": "Hjem",
          "scanner": "Skann", 
          "log": "Logg",
          "profile": "Profil",
          "admin": "Admin",
          "logout": "Logg ut"
        },
        
        // Landing Page
        "landing": {
          "title": "Aktivlogg",
          "subtitle": "Digital treningslogg for pistolklubber",
          "loginButton": "Logg inn",
          "registerButton": "Registrer deg",
          "features": {
            "title": "Funktioner",
            "qrScanning": "QR-kode skanning",
            "trainingLog": "Treningslogg",
            "memberManagement": "Medlemsadministrasjon",
            "reports": "Rapporter"
          }
        },
        
        // Authentication
        "auth": {
          "login": {
            "title": "Logg inn",
            "email": "E-post",
            "password": "Passord",
            "loginButton": "Logg inn",
            "forgotPassword": "Glemt passord?",
            "noAccount": "Har du ikke konto?",
            "register": "Registrer deg"
          },
          "register": {
            "title": "Registrer deg",
            "fullName": "Fullt navn",
            "email": "E-post",
            "password": "Passord",
            "confirmPassword": "Bekreft passord",
            "registerButton": "Registrer deg",
            "hasAccount": "Har du allerede konto?",
            "login": "Logg inn"
          }
        },
        
        // Home/Dashboard
        "home": {
          "welcome": "Velkommen",
          "quickActions": "Hurtighandlinger",
          "scanQR": "Skann QR-kode",
          "registerTraining": "Registrer trening",
          "viewLog": "Se logg",
          "recentSessions": "Siste treningsøkter",
          "noSessions": "Ingen treningsøkter funnet"
        },
        
        // Scanner
        "scanner": {
          "title": "QR-kode skanner",
          "instruction": "Skann QR-koden på treningslokasjonen",
          "scanning": "Skanner...",
          "success": "Trening registrert!",
          "error": "Kunne ikke registrere trening",
          "invalidCode": "Ugyldig QR-kode"
        },
        
        // Training Log
        "trainingLog": {
          "title": "Treningslogg",
          "registerTraining": "Registrer trening",
          "date": "Dato",
          "time": "Tid",
          "location": "Lokasjon",
          "activity": "Aktivitet",
          "officer": "Standplassleder",
          "status": "Status",
          "actions": "Handlinger",
          "verified": "Verifisert",
          "pending": "Venter",
          "rejected": "Avvist",
          "noSessions": "Ingen treningsøkter funnet",
          "export": "Eksporter",
          "filter": "Filtrer"
        },
        
        // Profile
        "profile": {
          "title": "Profil",
          "personalInfo": "Personlig informasjon",
          "fullName": "Fullt navn",
          "email": "E-post",
          "phone": "Telefon",
          "birthDate": "Fødselsdato",
          "skyterID": "Skytter-ID",
          "documents": "Dokumenter",
          "membershipCard": "Medlemskort",
          "weaponCertificate": "Våpensertifikat",
          "uploadDocument": "Last opp dokument",
          "deleteDocument": "Slett dokument",
          "changePassword": "Endre passord",
          "currentPassword": "Nåværende passord",
          "newPassword": "Nytt passord",
          "confirmPassword": "Bekreft passord",
          "save": "Lagre"
        },
        
        // Admin
        "admin": {
          "title": "Administrasjon",
          "subtitle": "Administrer medlemmer, treningsøkter og systeminnstillinger",
          "tabs": {
            "overview": "Dagens",
            "members": "Medlemmer",
            "sessions": "Logg",
            "locations": "QR-koder",
            "settings": "Innstillinger"
          },
          
          // Today's Overview
          "today": {
            "title": "Dagens registreringer",
            "approveAll": "Godkjenn dagens treninger",
            "member": "Medlem",
            "time": "Tid",
            "location": "Lokasjon",
            "status": "Status",
            "approve": "Godkjenn",
            "reject": "Avvis"
          },
          
          // Members Management
          "members": {
            "title": "Medlemmer",
            "count": "Medlemmer",
            "search": "Søk etter medlem...",
            "filters": {
              "all": "Alle",
              "skyteleder": "Skyteleder",
              "member": "Medlem",
              "pending": "Ventende"
            },
            "quickApprove": "Hurtiggodkjenning",
            "name": "Navn",
            "email": "E-post",
            "role": "Rolle",
            "joinDate": "Medlem siden",
            "actions": "Handlinger",
            "edit": "Rediger",
            "delete": "Slett",
            "approve": "Godkjenn",
            "makeSkyteleder": "Gjør til skyteleder",
            "removeSkyteleder": "Fjern skyteleder"
          },
          
          // Sessions Log
          "sessions": {
            "title": "Treningslogg",
            "organizationInfo": "treningsøkter totalt",
            "filters": {
              "allLocations": "Alle baner",
              "allOfficers": "Alle standplassledere", 
              "allStatuses": "Alle statuser",
              "verifiedOnly": "Kun verifiserte",
              "unverifiedOnly": "Kun ikke-verifiserte"
            },
            "registerTraining": "Registrer trening",
            "date": "Dato",
            "member": "Medlem",
            "activity": "Aktivitet",
            "location": "Bane",
            "officer": "Standplassleder",
            "status": "Status",
            "actions": "Handlinger"
          },
          
          // QR Locations
          "locations": {
            "title": "QR-koder og treningslokasjon",
            "addLocation": "Legg til ny lokasjon",
            "name": "Navn",
            "description": "Beskrivelse", 
            "actions": "Handlinger",
            "viewQR": "Se QR-kode",
            "downloadPoster": "Last ned plakat",
            "edit": "Rediger",
            "delete": "Slett"
          },
          
          // Settings
          "settings": {
            "title": "Organisasjonsinnstillinger",
            "subtitle": "Konfigurer organisasjonsinformasjon og innstillinger",
            
            "basicInfo": {
              "title": "Grunnleggende informasjon",
              "organizationName": "Organisasjonsnavn",
              "organizationNumber": "Organisasjonsnummer",
              "subscriptionPlan": "Abonnementsplan",
              "memberUsage": "Medlemsbruk",
              "description": "Beskrivelse",
              "plans": {
                "start": "Start (Opp til 100 medlemmer)",
                "unlimited": "Ubegrenset (Ubegr. antall medlemmer)"
              }
            },
            
            "contactInfo": {
              "title": "Kontaktinformasjon",
              "email": "E-post",
              "phone": "Telefon", 
              "website": "Nettside",
              "address": "Fakturaadresse"
            },
            
            "visualBranding": {
              "title": "Visuell logo og farger",
              "logoUpload": "Logo for PDF-eksport",
              "uploadLogo": "Last opp logo",
              "deleteLogo": "Slett logo",
              "primaryColor": "Primær farge",
              "secondaryColor": "Sekundær farge",
              "backgroundColor": "Bakgrunnsfarge",
              "resetToDefault": "Tilbakestill til standard"
            },
            
            "salesBanner": {
              "title": "Salgsbanner på forsiden",
              "description": "Last opp et banner som vises midt på forsiden når medlemmer logger inn. Dette kan brukes for å finansiere kostnaden til Aktivlogg gjennom sponsorer.",
              "uploadBanner": "Last opp banner",
              "deleteBanner": "Slett banner"
            },
            
            "shootingBranches": {
              "title": "Skyttergrener og aktiviteter",
              "activeShootingBranches": "Aktive skyttergrener",
              "nsf": "NSF - Norges Sportsskytterforbund",
              "nsfDescription": "Tradisjonell sportsskyting, pistol og rifle",
              "dfs": "DFS - Dynamisk Feltskytting", 
              "dfsDescription": "Praktisk skyting og konkurranser",
              "dssn": "DSSN - Dynamisk Sportsskyting Norge",
              "dssnDescription": "Dynamisk skyting og konkurranser",
              "activityTypes": "Aktivitetstyper",
              "addActivityType": "Legg til ny aktivitetstype"
            },
            
            "languageSettings": {
              "title": "Språkinnstillinger",
              "activeLanguage": "Aktiv språk",
              "languages": {
                "no": "Norsk (Bokmål)",
                "en": "English",
                "sv": "Svenska", 
                "da": "Dansk"
              },
              "exportLanguageFiles": "Eksporter språkfiler",
              "exportDescription": "Last ned komplett språkfil for oversettelse. Filen inneholder alle tekster i applikasjonen og kan brukes til å lage oversettelser til andre språk.",
              "downloadNorwegian": "Last ned norsk.json",
              "uploadTranslation": "Last opp oversettelse",
              "uploadDescription": "Last opp oversatt språkfil for å legge til et nytt språk i applikasjonen.",
              "uploadLanguageFile": "Last opp språkfil (.json)"
            },
            
            "buttons": {
              "cancel": "Avbryt",
              "saveAll": "Lagre alle innstillinger",
              "saving": "Lagrer..."
            }
          }
        },
        
        // Manual Training Registration
        "manualRegistration": {
          "title": "Manuell treningsregistrering",
          "member": "Medlem",
          "selectMember": "Velg medlem",
          "location": "Treningslokasjon", 
          "selectLocation": "Velg lokasjon",
          "activity": "Aktivitet",
          "selectActivity": "Velg aktivitet",
          "officer": "Standplassleder",
          "selectOfficer": "Velg standplassleder",
          "date": "Dato",
          "time": "Tid",
          "notes": "Notater",
          "cancel": "Avbryt",
          "register": "Registrer trening"
        },
        
        // Member Edit Modal
        "memberEdit": {
          "title": "Rediger medlem",
          "fullName": "Fullt navn",
          "email": "E-post",
          "newPassword": "Nytt passord",
          "role": "Rolle",
          "birthDate": "Fødselsdato",
          "cancel": "Avbryt",
          "save": "Lagre endringer",
          "roles": {
            "member": "Medlem",
            "skyteleder": "Skyteleder"
          }
        },
        
        // Common UI Elements
        "common": {
          "loading": "Laster...",
          "save": "Lagre",
          "cancel": "Avbryt", 
          "delete": "Slett",
          "edit": "Rediger",
          "add": "Legg til",
          "search": "Søk",
          "filter": "Filtrer",
          "export": "Eksporter",
          "upload": "Last opp",
          "download": "Last ned",
          "approve": "Godkjenn",
          "reject": "Avvis",
          "close": "Lukk",
          "confirm": "Bekreft",
          "yes": "Ja",
          "no": "Nei",
          "ok": "OK",
          "error": "Feil",
          "success": "Suksess",
          "warning": "Advarsel",
          "info": "Informasjon"
        },
        
        // Date and Time
        "dateTime": {
          "days": {
            "monday": "mandag",
            "tuesday": "tirsdag", 
            "wednesday": "onsdag",
            "thursday": "torsdag",
            "friday": "fredag",
            "saturday": "lørdag",
            "sunday": "søndag"
          },
          "months": {
            "january": "januar",
            "february": "februar",
            "march": "mars", 
            "april": "april",
            "may": "mai",
            "june": "juni",
            "july": "juli",
            "august": "august",
            "september": "september",
            "october": "oktober", 
            "november": "november",
            "december": "desember"
          },
          "today": "i dag",
          "yesterday": "i går",
          "tomorrow": "i morgen"
        },
        
        // Status Messages
        "messages": {
          "trainingRegistered": "Trening registrert!",
          "trainingApproved": "Trening godkjent!",
          "trainingRejected": "Trening avvist!",
          "memberUpdated": "Medlem oppdatert!",
          "settingsSaved": "Innstillinger lagret!",
          "fileUploaded": "Fil lastet opp!",
          "fileDeleted": "Fil slettet!",
          "passwordChanged": "Passord endret!",
          "organizationUpdated": "Organisasjon oppdatert!",
          "locationCreated": "Lokasjon opprettet!",
          "locationDeleted": "Lokasjon slettet!",
          "qrCodeGenerated": "QR-kode generert!",
          "pdfDownloaded": "PDF lastet ned!",
          "languageFileExported": "Språkfil eksportert!",
          "errorOccurred": "En feil oppstod. Prøv igjen.",
          "unauthorized": "Ikke autorisert",
          "networkError": "Nettverksfeil. Sjekk tilkoblingen.",
          "invalidInput": "Ugyldig input",
          "requiredField": "Dette feltet er påkrevd",
          "emailInvalid": "Ugyldig e-postadresse",
          "passwordTooShort": "Passordet må være minst 6 tegn",
          "passwordsDoNotMatch": "Passordene stemmer ikke overens",
          "fileTooBig": "Filen er for stor",
          "invalidFileType": "Ugyldig filtype",
          "sessionExpired": "Økten har utløpt. Logg inn på nytt."
        }
      };

      const jsonStr = JSON.stringify(languageData, null, 2);
      
      // Alternative method using FileSaver-like approach
      const bytes = new TextEncoder().encode(jsonStr);
      const blob = new Blob([bytes], { type: 'application/json;charset=utf-8' });
      
      // Create download link with better compatibility
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.href = url;
      link.download = 'norsk.json';
      link.style.display = 'none';
      
      // Force download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      console.log('Language file exported successfully with', Object.keys(languageData).length, 'sections');
      toast.success('Språkfil norsk.json er lastet ned til din nedlastningsmappe!');
      
    } catch (error) {
      console.error('Error exporting language file:', error);
      toast.error('Feil ved nedlasting av språkfil. Prøv å tillate nedlastinger i nettleseren din.');
    }
  };

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Comprehensive organization data update with ALL settings fields
      const orgData = {
        name: settingsData.organizationName,
        organization_number: settingsData.organizationNumber,
        subscription_plan: settingsData.subscriptionPlan,
        description: settingsData.description,
        email: settingsData.email,
        phone: settingsData.phone,
        website: settingsData.website,
        address: settingsData.address,
        primary_color: settingsData.primaryColor,
        secondary_color: settingsData.secondaryColor,
        background_color: settingsData.backgroundColor,
        logo_url: settingsData.logoUrl,
        banner_urls: settingsData.bannerUrls,
        banner_links: settingsData.bannerLinks,
        banner_visible: settingsData.bannerVisible, // Fix: Include banner visibility array
        banner_expiry_dates: settingsData.bannerExpiryDates,
        banner_rotation_seconds: settingsData.bannerRotationSeconds,
        // Also sync to settings.* (new location that Home.js prefers when present)
        "settings.bannerUrls": settingsData.bannerUrls,
        "settings.bannerLinks": settingsData.bannerLinks,
        "settings.bannerVisible": settingsData.bannerVisible,
        "settings.bannerExpiryDates": settingsData.bannerExpiryDates,
        "settings.bannerRotationSeconds": settingsData.bannerRotationSeconds,
        active_language: settingsData.activeLanguage,
        shooting_branches: settingsData.shootingBranches,
        activity_types: settingsData.activityTypes,
        member_organizations: settingsData.memberOrganizations, // Add member organizations
        dugnad_feature_enabled: settingsData.dugnadFeatureEnabled,
        dugnad_activity: settingsData.dugnadActivity,
        dugnad_threshold: settingsData.dugnadThreshold,
        calendar_enabled: settingsData.calendarEnabled,
        calendar_attendance_enabled: settingsData.calendarAttendanceEnabled !== false,
        calendar_attendance_public: settingsData.calendarAttendancePublic !== false,
        calendar_capacity_enabled: settingsData.calendarCapacityEnabled === true,
        calendar_timezone: settingsData.calendarTimezone,
        calendar_dst_enabled: settingsData.calendarDstEnabled,
        calendar_default_view: settingsData.calendarDefaultView,
        calendar_embed_default_view: settingsData.calendarEmbedDefaultView,
        messages_enabled: settingsData.messagesEnabled,
        club_operations_enabled: settingsData.clubOperationsEnabled
      };

      // Persister Merkeskyting-toggle separat (egen kolleksjon)
      try {
        await axios.patch(`${API_BASE}/api/merkeskyting/settings/enabled`, {
          enabled: settingsData.merkeskytingEnabled !== false,
        }, { headers });
      } catch (e) {
        console.warn('Kunne ikke lagre Merkeskyting-toggle', e);
      }

      await axios.put(`${API_BASE}/api/organizations/${user?.organization_id}`, orgData, { headers });
      
      // Refresh user data to update organization colors immediately
      await refreshUser();
      
      toast.success('Alle innstillinger lagret!');
    } catch (err) {
      console.error('Failed to save all settings:', err);
      toast.error('Kunne ikke lagre innstillinger. Prøv igjen.');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Load settings data when organization data is available
  React.useEffect(() => {
    if (user?.organization) {
      loadSettingsData();
    }
  }, [user?.organization]);
  
  // Sync dugnad settings from organization to local state
  React.useEffect(() => {
    if (user?.organization?.dugnad_activity) {
      setSelectedDugnadActivity(user.organization.dugnad_activity);
    }
    if (user?.organization?.dugnad_threshold) {
      setDugnadThreshold(user.organization.dugnad_threshold);
    }
  }, [user?.organization?.dugnad_activity, user?.organization?.dugnad_threshold]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  // Calculate pending approvals count (only non-verified sessions from today)
  const todaysPendingCount = sessions?.filter(session => {
    if (!session) return false;
    // Only count sessions that are NOT verified (pending approval) AND from today
    const sessionDate = new Date(session.start_time);
    return !session.verified && isToday(sessionDate);
  })?.length || 0;

  // Calculate pending members count (members waiting for approval - EXCLUDES deactivated and deleted)
  const pendingMembersCount = members?.filter(member => !member.approved && member.active !== false && member.is_deleted !== true)?.length || 0;

  // Use trainingStats from API for accurate counts (not limited by sessions array size)
  const totalTrainingSessions = trainingStats.total || sessions?.length || 0;
  const weekTrainingSessions = trainingStats.this_week || 0;
  const todayTrainingSessions = trainingStats.today || 0;
  
  const totalMembersCount = members?.length || 0;

  // Define tabs based on user role - matching role access matrix
  const isAdmin = user?.role === 'admin' || user?.role === 'org_admin';
  const isSuperAdmin = user?.role === 'super_admin' || user?.original_role === 'super_admin';
  const isSkyteleder = user?.role === 'range_officer' || user?.role === 'org_admin';
  
  const tabs = [
    // Dagens - Admin, Skyteleder and Super-Admin
    ...((isAdmin || isSkyteleder || isSuperAdmin) ? [{
      id: 'overview', 
      label: 'Dagens', 
      mobileLabel: 'Dagens',
      icon: Clock, 
      count: todaysPendingCount 
    }] : []),
    // Medlemmer - Admin, Skyteleder and Super-Admin
    ...((isAdmin || isSkyteleder || isSuperAdmin) ? [{ 
      id: 'members', 
      label: 'Medlemmer', 
      mobileLabel: 'Medl.',
      icon: Users,
      count: pendingMembersCount 
    }] : []),
    // Klubb-logg - Admin, Skyteleder and Super-Admin
    ...((isAdmin || isSkyteleder || isSuperAdmin) ? [{ 
      id: 'sessions', 
      label: 'Klubb-logg', 
      mobileLabel: 'Klubb-logg',
      icon: Target,
      count: sessions.filter(s => !s.verified).length // Count unverified sessions
    }] : []),
    // Meldinger - Admin, Skyteleder and Super-Admin (when module enabled)
    ...((user?.organization?.messages_enabled !== false) && (isAdmin || isSkyteleder || isSuperAdmin) ? [{ 
      id: 'messages', 
      label: 'Meldinger', 
      mobileLabel: 'Meldinger',
      icon: StickyNote,
      count: 0 // No count needed for admin view
    }] : []),
    // Ammo-salg - Admin, Skyteleder (org_admin, range_officer) and Super-Admin (when module enabled)
    ...(ammoModuleEnabled && (isAdmin || isSkyteleder || isSuperAdmin) ? [{ 
      id: 'ammo', 
      label: 'Ammo-salg', 
      mobileLabel: 'Ammo',
      icon: AmmoBulletsIcon 
    }] : []),
    // Kalender - Admin, Skyteleder and Super-Admin - Direct link to calendar page
    ...((isAdmin || isSkyteleder || isSuperAdmin) ? [{ 
      id: 'calendar', 
      label: 'Kalender', 
      mobileLabel: 'Kalender',
      icon: Calendar,
      href: '/calendar'  // Direct navigation instead of showing a section
    }] : []),
    // QR-koder - Only Admin and Super-Admin
    ...((isAdmin || isSuperAdmin) ? [
      { 
        id: 'locations', 
        label: 'QR-koder', 
        mobileLabel: 'QR',
        icon: QrCode 
      }
    ] : []),
    // Innstillinger - Admin, Skyteleder and Super-Admin (for banner management)
    ...((isAdmin || isSkyteleder || isSuperAdmin) ? [
      { 
        id: 'settings', 
        label: 'Innstillinger', 
        mobileLabel: 'Innst.',
        icon: Settings 
      }
    ] : []),
    // Statistikk - Admin, Skyteleder and Super-Admin
    ...((isAdmin || isSkyteleder || isSuperAdmin) ? [{
      id: 'statistics', 
      label: 'Statistikk', 
      mobileLabel: 'Stats',
      icon: BarChart3 
    }] : [])
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('admin.title', 'Administrasjon')}</h1>
          <p className="text-gray-400">{t('admin.subtitle', 'Administrer medlemmer, treningsøkter og systeminnstillinger')}</p>
        </div>
      </div>

      {/* Demo Status Banner - Only shown for demo organizations */}
      {user?.organization?.is_demo && user?.organization?.subscription_status === 'demo' && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span className="text-yellow-500 font-medium">Demo-periode aktiv</span>
            </div>
            <span className="text-gray-400">
              Utløper: {new Date(user.organization.demo_expires_at).toLocaleDateString('no-NO')}
            </span>
          </div>
        </div>
      )}

      {/* Quick Stats Info Boxes - Slim and responsive - HIDDEN on Dagens tab. HIDDEN on mobile (rendered at bottom below tab content) */}
      {activeTab !== 'overview' && (
        <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="admin-info-cards-desktop">
          {/* Total Training Sessions */}
          <div className="bg-gradient-to-br from-green-600/20 to-green-600/10 border border-green-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-600/30 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-green-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Totalt antall treningsøkter</p>
              <p className="text-xl font-bold text-white">{totalTrainingSessions}</p>
            </div>
          </div>

          {/* Week Training Sessions */}
          <div className="bg-gradient-to-br from-blue-600/20 to-blue-600/10 border border-blue-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-600/30 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Ukens treningsøkter</p>
              <p className="text-xl font-bold text-white">{weekTrainingSessions}</p>
            </div>
          </div>

          {/* Today Training Sessions */}
          <div className="bg-gradient-to-br from-purple-600/20 to-purple-600/10 border border-purple-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-600/30 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-purple-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Dagens treningsøkter</p>
              <p className="text-xl font-bold text-white">{todayTrainingSessions}</p>
            </div>
          </div>

          {/* Total Members */}
          <div className="bg-gradient-to-br from-orange-600/20 to-orange-600/10 border border-orange-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-orange-600/30 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-orange-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Totalt antall medlemmer</p>
              <p className="text-xl font-bold text-white">{totalMembersCount}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs - Scrollable pill bar (drag-to-scroll + arrows, native touch swipe on mobile) */}
      <div className="mb-2" data-testid="admin-tab-bar-wrap">
        <ScrollableTabBar
          className="gap-2 md:gap-3 -mx-1 px-2 pt-2 pb-2 md:pb-1"
          testId="admin-tab-bar"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const baseClasses = `snap-start shrink-0 flex flex-col items-center justify-center gap-1 rounded-xl border transition-all relative px-3 py-2 min-w-[72px] md:min-w-[88px] ${
              isActive
                ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400 shadow-[0_0_0_1px_rgba(234,179,8,0.4)]'
                : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:text-white hover:border-gray-500'
            }`;
            const iconEl = <Icon className="h-5 w-5 md:h-6 md:w-6" />;
            const labelEl = (
              <span className="text-[11px] md:text-xs font-medium whitespace-nowrap leading-none">
                <span className="md:hidden">{tab.mobileLabel}</span>
                <span className="hidden md:inline">{tab.label}</span>
              </span>
            );
            const countBadge = tab.count > 0 ? (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center font-bold">
                {tab.count > 99 ? '99+' : tab.count}
              </span>
            ) : null;

            if (tab.href) {
              return (
                <a
                  key={tab.id}
                  href={tab.href}
                  className={baseClasses}
                  data-testid={`admin-tab-${tab.id}`}
                >
                  {iconEl}
                  {labelEl}
                  {countBadge}
                </a>
              );
            }

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={baseClasses}
                data-testid={`admin-tab-${tab.id}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {iconEl}
                {labelEl}
                {countBadge}
              </button>
            );
          })}
        </ScrollableTabBar>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Ammunisjonslager i full bredde - kun hvis ammo-modulen er aktivert */}
          {ammoModuleEnabled && (
            <AmmoStockFullWidth defaultExpanded={true} />
          )}

          {/* Overview Header with Filters */}
          {/* Dagens skyteleder + «Jeg kommer»-påmeldte på samme linje */}
          {user?.organization?.calendar_enabled !== false && (todaySkyteledere.length > 0 || (todayAttendance?.enabled && todayAttendance?.has_events_today)) && (
            <div className="mb-4 flex flex-col sm:flex-row gap-3" data-testid="dagens-info-row">
              {todaySkyteledere.length > 0 && (
              <div className="flex-1 min-w-0 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 flex-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span className="text-sm text-blue-400 font-medium">Dagens skyteleder:</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(todaySkyteledere.reduce((acc, sl) => {
                      if (!acc[sl.name]) acc[sl.name] = [];
                      acc[sl.name].push(sl);
                      return acc;
                    }, {})).map(([name, events], idx) => (
                      <span key={idx} className="text-sm text-white bg-blue-500/20 px-2 py-0.5 rounded" data-testid={`dagens-skyteleder-overview-${idx}`}>
                        {name}
                        {events.map((ev, i) => (
                          <span key={i} className="text-blue-300 ml-1">
                            {i > 0 && ' · '}
                            {ev.location}{ev.start_time && ` ${ev.start_time}-${ev.end_time}`}
                          </span>
                        ))}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              )}
              {(todayAttendance?.enabled && todayAttendance?.has_events_today) && (
                <button type="button" onClick={() => setShowTodayAttendees(true)}
                  className="sm:w-auto shrink-0 flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/40 rounded-lg hover:bg-green-500/20 transition-colors text-left touch-manipulation"
                  data-testid="dagens-attendance-box">
                  <span className="text-3xl font-black text-green-300 leading-none" data-testid="dagens-attendance-count">{todayAttendance.unique_members}</span>
                  <span className="text-sm text-green-200 leading-tight">
                    <span className="block font-semibold">Påmeldt i dag</span>
                    <span className="block text-xs text-green-300/80">«Jeg kommer» – trykk for liste</span>
                  </span>
                </button>
              )}
            </div>
          )}
          {showTodayAttendees && <AttendeesModal today onClose={() => setShowTodayAttendees(false)} />}

          {/* Treningsregistreringer header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4 gap-2">
              <h2 className="text-xl font-bold text-white leading-tight">
                <span className="md:hidden">Trenings-<br/>registreringer</span>
                <span className="hidden md:inline">Treningsregistreringer</span>
              </h2>
              <div className="flex gap-2">
                {sessions.filter(s => !s.verified && isToday(new Date(s.start_time))).length > 0 && (
                  <button
                    onClick={() => approveAllPendingSessions()}
                    className="px-3 md:px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors text-sm"
                  >
                    <span className="md:hidden">Godkjenn</span>
                    <span className="hidden md:inline">Godkjenn dagens treninger</span>
                  </button>
                )}
                <button 
                  onClick={() => setShowManualRegistration(true)}
                  className="px-3 md:px-4 py-2 bg-green-500 text-gray-900 rounded-lg font-medium hover:bg-green-400 transition-colors text-sm"
                >
                  <span className="hidden md:inline">+ </span>Registrer aktivitet
                </button>
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-1.5 md:gap-4 mb-4">
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                <button
                  onClick={() => setOverviewDateFilter('today')}
                  className={`px-2 py-1 md:px-3 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    overviewDateFilter === 'today'
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  I dag
                </button>
                <button
                  onClick={() => setOverviewDateFilter('week')}
                  className={`px-2 py-1 md:px-3 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    overviewDateFilter === 'week'
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Denne uken
                </button>
                <button
                  onClick={() => setOverviewDateFilter('all')}
                  className={`px-2 py-1 md:px-3 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    overviewDateFilter === 'all'
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Alle ikke-godkjente
                </button>
              </div>
              
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                <button
                  onClick={() => setOverviewStatusFilter('all')}
                  className={`px-2 py-1 md:px-3 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    overviewStatusFilter === 'all'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Alle statuser
                </button>
                <button
                  onClick={() => setOverviewStatusFilter('pending')}
                  className={`px-2 py-1 md:px-3 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    overviewStatusFilter === 'pending'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Venter godkjenning
                </button>
                <button
                  onClick={() => setOverviewStatusFilter('approved')}
                  className={`px-2 py-1 md:px-3 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    overviewStatusFilter === 'approved'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Godkjente
                </button>
              </div>
            </div>
          </div>

          {/* Filtered Training Sessions */}
          {(() => {
            // Calculate filtered sessions
            const filteredSessions = sessions.filter(session => {
              // Date filtering
              let matchesDate = false;
              if (overviewDateFilter === 'today') {
                // For "Dagens registreringer": show ALL today's registrations + ALL non-verified registrations
                matchesDate = isToday(new Date(session.start_time)) || !session.verified;
              } else if (overviewDateFilter === 'week') {
                matchesDate = isThisWeek(new Date(session.start_time));
              } else if (overviewDateFilter === 'all') {
                matchesDate = !session.verified; // Only show non-verified for "all"
              }
              
              // Status filtering
              let matchesStatus = false;
              if (overviewStatusFilter === 'all') {
                matchesStatus = true;
              } else if (overviewStatusFilter === 'pending') {
                matchesStatus = !session.verified;
              } else if (overviewStatusFilter === 'approved') {
                matchesStatus = session.verified;
              }
              
              return matchesDate && matchesStatus;
            });
            
            // Calculate count for TODAY ONLY registrations (for counter display)
            const todaySessionsCount = overviewDateFilter === 'today' 
              ? sessions.filter(session => isToday(new Date(session.start_time))).length 
              : 0;

            // Hide "Dagens registreringer" section if empty and on today filter
            if (overviewDateFilter === 'today' && filteredSessions.length === 0) {
              return null;
            }

            // Check if there are any unverified sessions to show attention-grabbing border
            const hasUnverifiedSessions = filteredSessions.some(session => !session.verified);
            const showAttentionBorder = overviewDateFilter === 'today' && hasUnverifiedSessions;

            return (
              <div className={`bg-gray-800 rounded-lg p-3 md:p-6 -mx-2 md:mx-0 ${showAttentionBorder ? 'border-2 border-yellow-400 shadow-lg shadow-yellow-400/20' : ''}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">
                {overviewDateFilter === 'today' && (
                  <>
                    Dagens registreringer {todaySessionsCount > 0 && <span className="text-yellow-400">({todaySessionsCount})</span>}
                  </>
                )}
                {overviewDateFilter === 'week' && 'Registreringer denne uken'}
                {overviewDateFilter === 'all' && 'Alle ikke-godkjente registreringer'}
              </h3>
              <div className="text-sm text-gray-400">
                {overviewDateFilter === 'today' && new Date().toLocaleDateString('no-NO', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                {overviewDateFilter === 'week' && 'Uke ' + getWeekNumber()}
                {overviewDateFilter === 'all' && 'Alle datoer'}
              </div>
            </div>
            
            {/* Kolonneoverskrifter - Skjult på mobil */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
              <div className="col-span-3">Medlem</div>
              <div className="col-span-4">Dato / Sted</div>
              <div className="col-span-2">Skyteleder</div>
              <div className="col-span-3 text-right">Status / Handlinger</div>
            </div>
            
            <div className="space-y-3">
              {filteredSessions
                .map((session) => (
                <div key={session.id} className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors">
                  {/* Desktop/Tablet visning */}
                  <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                    {/* Medlem - 3 kolonner */}
                    <div className="col-span-3">
                      <p className="font-medium text-white flex items-center gap-2">
                        {session.is_trial ? (
                          <>
                            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">PRØVE</span>
                            {session.trial_name || 'Prøveskyter'}
                          </>
                        ) : (
                          session.user_name
                        )}
                      </p>
                    </div>
                    
                    {/* Dato og sted - 4 kolonner */}
                    <div className="col-span-4">
                      <p className="text-gray-400 text-sm">
                        {new Date(session.start_time).toLocaleDateString('no-NO')} • {session.location_name}
                        {session.activity && ` • ${session.activity}`}
                      </p>
                    </div>
                    
                    {/* Skyteleder - 2 kolonner */}
                    <div className="col-span-2">
                      <p className="text-yellow-400 text-sm">
                        {session.verified_by || 'Ikke tildelt'}
                      </p>
                    </div>
                    
                    {/* Status og handlinger - 3 kolonner */}
                    <div className="col-span-3 flex items-center justify-end space-x-3">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        session.verified
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {session.verified ? 'Godkjent' : 'Venter'}
                      </span>
                      
                      {/* Quick Actions */}
                      <div className="flex space-x-1">
                          <button
                            onClick={() => verifySession(session.id, true)}
                            disabled={session.verified}
                            className={`p-2 rounded transition-colors ${
                              session.verified 
                                ? 'text-gray-500 cursor-not-allowed' 
                                : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                            }`}
                            title="Godkjenn"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => verifySession(session.id, false)}
                            disabled={!session.verified}
                            className={`p-2 rounded transition-colors ${
                              !session.verified 
                                ? 'text-gray-500 cursor-not-allowed' 
                                : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                            }`}
                            title="Avvis"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          
                          {/* Ammo Sale Button - Only show if module is enabled */}
                          {ammoModuleEnabled && (
                            <button
                              onClick={() => {
                                setSelectedSessionForAmmo(session);
                                setShowAmmoSaleModal(true);
                              }}
                              className={`p-2 rounded transition-colors ${
                                sessionAmmoSales[session.id]
                                  ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10'
                                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-500/10'
                              }`}
                              title="Registrer ammo-salg"
                            >
                              <svg 
                                className="h-4 w-4"
                                fill={sessionAmmoSales[session.id] ? '#FFD700' : '#9CA3AF'}
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 1720.46 1622.88"
                              >
                                <path d="M239.43,0C56.46,0,19.96,196.91,18.46,308.13h438.7C453.42,194.7,429.65,0,239.43,0Z"/>
                                <path d="M437.17,345.42H38.43c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69H0v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                                <path d="M861.85,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                                <path d="M1059.59,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                                <path d="M1484.28,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                                <path d="M1682.02,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  
                  {/* Mobil visning - Kortbasert design */}
                  <div className="md:hidden">
                    {/* Header med navn og status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {session.is_trial && (
                          <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold shrink-0">PRØVE</span>
                        )}
                        <p className="font-medium text-white truncate">
                          {session.is_trial ? (session.trial_name || 'Prøveskyter') : session.user_name}
                        </p>
                        {!session.is_trial && <MultiClubBadge otherClubs={multiClubMap[session.user_id]} />}
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full font-medium shrink-0 ml-2 ${
                        session.verified
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {session.verified ? 'Godkjent' : 'Venter'}
                      </span>
                    </div>
                    
                    {/* Info-grid */}
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Dato</p>
                        <p className="text-gray-300">{new Date(session.start_time).toLocaleDateString('no-NO')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Sted</p>
                        <p className="text-gray-300 truncate">{session.location_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Skyteleder</p>
                        <p className="text-yellow-400">{session.verified_by || 'Ikke tildelt'}</p>
                      </div>
                      {session.activity && (
                        <div>
                          <p className="text-gray-500 text-xs uppercase">Aktivitet</p>
                          <p className="text-gray-300 truncate">{session.activity}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Handlinger */}
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-600">
                      <button
                        onClick={() => verifySession(session.id, true)}
                        disabled={session.verified}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          session.verified 
                            ? 'bg-gray-600 text-gray-500 cursor-not-allowed' 
                            : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        }`}
                      >
                        <CheckCircle className="h-4 w-4" />
                        Godkjenn
                      </button>
                      <button
                        onClick={() => verifySession(session.id, false)}
                        disabled={!session.verified}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          !session.verified 
                            ? 'bg-gray-600 text-gray-500 cursor-not-allowed' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        }`}
                      >
                        <X className="h-4 w-4" />
                        Avvis
                      </button>
                      {ammoModuleEnabled && (
                        <button
                          onClick={() => {
                            setSelectedSessionForAmmo(session);
                            setShowAmmoSaleModal(true);
                          }}
                          className={`p-2 rounded-lg transition-colors ${
                            sessionAmmoSales[session.id]
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-gray-600 text-gray-400 hover:bg-gray-500'
                          }`}
                          title="Registrer ammo-salg"
                        >
                          <svg 
                            className="h-4 w-4"
                            fill={sessionAmmoSales[session.id] ? '#FFD700' : '#9CA3AF'}
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 1720.46 1622.88"
                          >
                            <path d="M239.43,0C56.46,0,19.96,196.91,18.46,308.13h438.7C453.42,194.7,429.65,0,239.43,0Z"/>
                            <path d="M437.17,345.42H38.43c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69H0v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                            <path d="M861.85,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                            <path d="M1059.59,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                            <path d="M1484.28,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                            <path d="M1682.02,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {sessions.filter(session => {
                const sessionDate = new Date(session.start_time).toDateString();
                const today = new Date().toDateString();
                return sessionDate === today;
              }).length === 0 && (
                <div className="text-center text-gray-400 py-12">
                  <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h4 className="text-lg font-semibold text-white mb-2">Ingen registreringer i dag</h4>
                  <p className="text-sm">Treningsøkter registrert i dag vil vises her.</p>
                  <p className="text-sm mt-2">Bruk QR-scanner eller manuell registrering for å legge til økter.</p>
                </div>
              )}
            </div>
              </div>
            );
          })()}

          {/* Recent Activity (Previous days) */}
          {sessions.filter(session => {
            const sessionDate = new Date(session.start_time).toDateString();
            const today = new Date().toDateString();
            return sessionDate !== today;
          }).length > 0 && (
            <div className="bg-gray-800 rounded-lg p-3 md:p-6 -mx-2 md:mx-0 mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Tidligere registreringer</h3>
              
              {/* Kolonneoverskrifter - Skjult på mobil */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div className="col-span-3">Medlem</div>
                <div className="col-span-4">Dato / Sted</div>
                <div className="col-span-2">Skyteleder</div>
                <div className="col-span-3 text-right">Status</div>
              </div>
              
              <div className="space-y-3">
                {sessions
                  .filter(session => {
                    const sessionDate = new Date(session.start_time).toDateString();
                    const today = new Date().toDateString();
                    return sessionDate !== today;
                  })
                  .slice(0, 5)
                  .map((session) => (
                  <div key={session.id} className="p-3 bg-gray-700 rounded-lg">
                    {/* Desktop visning */}
                    <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                      {/* Medlem - 3 kolonner */}
                      <div className="col-span-3">
                        <p className="text-white font-medium flex items-center gap-2">
                          {session.is_trial && (
                            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">PRØVE</span>
                          )}
                          {session.is_trial ? (session.trial_name || 'Prøveskyter') : session.user_name}
                          {!session.is_trial && <MultiClubBadge otherClubs={multiClubMap[session.user_id]} />}
                        </p>
                      </div>
                      
                      {/* Dato / Sted - 4 kolonner */}
                      <div className="col-span-4">
                        <p className="text-gray-400 text-sm">
                          {new Date(session.start_time).toLocaleDateString('no-NO')} • {session.location_name}
                        </p>
                      </div>
                      
                      {/* Skyteleder - 2 kolonner */}
                      <div className="col-span-2">
                        <p className="text-yellow-400 text-sm">
                          {session.verified_by || 'Ikke tildelt'}
                        </p>
                      </div>
                      
                      {/* Status - 3 kolonner */}
                      <div className="col-span-3 flex items-center justify-end space-x-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          session.verified
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {session.verified ? 'Godkjent' : 'Venter'}
                        </span>
                        
                        {/* Ammo Sale Icon - Only show if module is enabled */}
                        {ammoModuleEnabled && (
                          <button
                            onClick={() => {
                              setSelectedSessionForAmmo(session);
                              setShowAmmoSaleModal(true);
                            }}
                            className={`p-1 transition-colors ${
                              sessionAmmoSales[session.id]
                                ? 'text-yellow-400 hover:text-yellow-300'
                                : 'text-gray-400 hover:text-gray-300'
                            }`}
                            title="Registrer ammo-salg"
                          >
                            <svg 
                              className="h-4 w-4"
                              fill={sessionAmmoSales[session.id] ? '#FFD700' : '#9CA3AF'}
                              xmlns="http://www.w3.org/2000/svg" 
                              viewBox="0 0 1720.46 1622.88"
                            >
                              <path d="M239.43,0C56.46,0,19.96,196.91,18.46,308.13h438.7C453.42,194.7,429.65,0,239.43,0Z"/>
                              <path d="M437.17,345.42H38.43c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69H0v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                              <path d="M861.85,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                              <path d="M1059.59,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                              <path d="M1484.28,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                              <path d="M1682.02,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                            </svg>
                          </button>
                        )}
                        
                        <button
                          onClick={() => verifySession(session.id, !session.verified)}
                          className={`p-1 rounded transition-colors ${
                            session.verified 
                              ? 'text-red-400 hover:text-red-300' 
                              : 'text-green-400 hover:text-green-300'
                          }`}
                        >
                          {session.verified ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Mobil visning - Kortbasert design */}
                    <div className="md:hidden">
                      {/* Header med navn og status */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {session.is_trial && (
                            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold shrink-0">PRØVE</span>
                          )}
                          <p className="font-medium text-white truncate">
                            {session.is_trial ? (session.trial_name || 'Prøveskyter') : session.user_name}
                          </p>
                          {!session.is_trial && <MultiClubBadge otherClubs={multiClubMap[session.user_id]} />}
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full shrink-0 ml-2 ${
                          session.verified
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {session.verified ? 'Godkjent' : 'Venter'}
                        </span>
                      </div>
                      
                      {/* Info */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs uppercase">Dato</p>
                          <p className="text-gray-300">{new Date(session.start_time).toLocaleDateString('no-NO')}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase">Sted</p>
                          <p className="text-gray-300 truncate">{session.location_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase">Skyteleder</p>
                          <p className="text-yellow-400">{session.verified_by || 'Ikke tildelt'}</p>
                        </div>
                      </div>
                      
                      {/* Handlinger - mobil har Avvis venstre og Ammo høyre */}
                      <div className="flex items-center justify-end gap-2 pt-2 mt-2 border-t border-gray-600">
                        {ammoModuleEnabled && (
                          <button
                            onClick={() => {
                              setSelectedSessionForAmmo(session);
                              setShowAmmoSaleModal(true);
                            }}
                            className={`order-2 p-2 rounded-lg transition-colors ${
                              sessionAmmoSales[session.id]
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-gray-600 text-gray-400'
                            }`}
                            title="Registrer ammo-salg"
                          >
                            <svg 
                              className="h-4 w-4"
                              fill={sessionAmmoSales[session.id] ? '#FFD700' : '#9CA3AF'}
                              xmlns="http://www.w3.org/2000/svg" 
                              viewBox="0 0 1720.46 1622.88"
                            >
                              <path d="M239.43,0C56.46,0,19.96,196.91,18.46,308.13h438.7C453.42,194.7,429.65,0,239.43,0Z"/>
                              <path d="M437.17,345.42H38.43c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69H0v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                              <path d="M861.85,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                              <path d="M1059.59,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                              <path d="M1484.28,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                              <path d="M1682.02,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => verifySession(session.id, !session.verified)}
                          className={`order-1 mr-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            session.verified 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}
                        >
                          {session.verified ? (
                            <>
                              <X className="h-4 w-4" />
                              Avvis
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              Godkjenn
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && (
        <div className="bg-gray-800 rounded-lg p-3 md:p-6 -mx-2 md:mx-0">
          <div className="flex justify-between items-center mb-6 gap-2">
            <h3 className="text-lg font-semibold text-white">Medlemmer ({totalMembers})</h3>
            <div className="flex flex-wrap gap-1.5 md:gap-2 md:space-x-2">
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="px-2 py-1 md:px-4 md:py-2 text-xs md:text-sm bg-yellow-500 text-gray-900 rounded-md md:rounded-lg font-medium hover:bg-yellow-400 transition-colors"
              >
                + Legg til medlem
              </button>
              {members.some(m => !m.approved) && (
                <button
                  onClick={approveAllPendingMembers}
                  className="px-2 py-1 md:px-4 md:py-2 text-xs md:text-sm bg-green-600 text-white rounded-md md:rounded-lg font-medium hover:bg-green-500 transition-colors"
                >
                  Godkjenn alle ventende
                </button>
              )}
            </div>
          </div>

          {/* Search and Filter Controls */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Søk etter navn eller e-post..."
                value={memberSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            
            {/* Filters - Responsive layout for mobile */}
            <div className="flex flex-wrap gap-2 md:space-x-2">
              {['all', 'admin', 'skyteleder', 'member', 'pending', 'deactivated', 'paused'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => handleFilterChange(filter)}
                  data-testid={`member-filter-${filter}`}
                  className={`px-2 md:px-3 py-1 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    memberFilter === filter
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {filter === 'all' && 'Alle'}
                  {filter === 'admin' && 'Admin'}
                  {filter === 'skyteleder' && 'Skyteleder'}
                  {filter === 'member' && 'Medlem'}
                  {filter === 'pending' && 'Ventende'}
                  {filter === 'deactivated' && 'Deaktiverte'}
                  {filter === 'paused' && 'Pauset'}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  {/* Checkbox column - Only visible in bulk delete mode */}
                  {bulkDeleteMode && (
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedMembers.length === filteredMembers.length && filteredMembers.length > 0}
                        onChange={toggleAllMembers}
                        className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                      />
                    </th>
                  )}
                  {/* Sortable: Navn */}
                  <th 
                    className="px-1 md:px-6 py-3 text-left text-[10px] md:text-xs font-medium text-gray-300 uppercase cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort('full_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Navn</span>
                      {memberSortField === 'full_name' && (
                        <span className="text-yellow-500">
                          {memberSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                      {(user?.organization?.dugnad_feature_enabled !== false) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setShowDugnadCol(v => !v); }}
                          className="md:hidden inline-flex items-center justify-center w-5 h-5 rounded bg-gray-800/60 text-gray-300 hover:text-yellow-400 hover:bg-gray-800 transition-colors ml-3"
                          title={showDugnadCol ? 'Skjul dugnad-krone' : 'Vis dugnad-krone'}
                          data-testid="toggle-dugnad-col"
                        >
                          {showDugnadCol ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable: E-post */}
                  <th 
                    className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>E-post</span>
                      {memberSortField === 'email' && (
                        <span className="text-yellow-500">
                          {memberSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable: Skytter ID */}
                  <th 
                    className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort('skytter_id')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Skytter ID</span>
                      {memberSortField === 'skytter_id' && (
                        <span className="text-yellow-500">
                          {memberSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable: Sist innlogget */}
                  <th 
                    className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort('last_login')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Sist innlogget</span>
                      {memberSortField === 'last_login' && (
                        <span className="text-yellow-500">
                          {memberSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable: Rolle - with collapse toggle on mobile */}
                  <th 
                    className={`${showRoleCol ? '' : 'w-0 p-0'} px-1 md:px-6 py-3 text-left text-[10px] md:text-xs font-medium text-gray-300 uppercase cursor-pointer hover:bg-gray-600 transition-colors`}
                    onClick={() => handleSort('role')}
                  >
                    <div className="flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setShowRoleCol(v => !v); }}
                        className="md:hidden inline-flex items-center justify-center w-5 h-5 rounded bg-gray-800/60 text-gray-300 hover:text-yellow-400 hover:bg-gray-800 transition-colors mr-1"
                        title={showRoleCol ? 'Skjul Rolle-kolonne' : 'Vis Rolle-kolonne'}
                        data-testid="toggle-role-col"
                      >
                        {showRoleCol ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                      {showRoleCol && <span>Rolle</span>}
                      {showRoleCol && memberSortField === 'role' && (
                        <span className="text-yellow-500">
                          {memberSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* NOT Sortable: Handlinger - always right-aligned so icons stay at edge even when Rolle collapses */}
                  <th className="px-1 md:px-6 py-3 text-right text-[10px] md:text-xs font-medium text-gray-300 uppercase">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {paginatedMembers.map((member) => (
                  <tr key={member.id} className={selectedMembers.includes(member.id) ? 'bg-yellow-500/10' : ''}>
                    {/* Checkbox column - Only visible in bulk delete mode */}
                    {bulkDeleteMode && (
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(member.id)}
                          onChange={() => toggleMemberSelection(member.id)}
                          className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                        />
                      </td>
                    )}
                    <td className="px-1 md:px-6 py-4 whitespace-nowrap text-sm text-white">
                      <div className="flex items-center justify-between gap-1">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span>{member.full_name}</span>
                            <MultiClubBadge otherClubs={multiClubMap[member.id]} />
                            {/* Notat-indikator: gul, mer synlig på medlemmer med internt notat */}
                            {member.internal_notes && member.internal_notes.trim() && (
                              <Tooltip
                                text={`Internt notat:\n${member.internal_notes.slice(0, 160)}${member.internal_notes.length > 160 ? '…' : ''}${member.internal_notes_updated_by ? `\n— ${member.internal_notes_updated_by}` : ''}`}
                                variant="gray"
                              >
                                <span
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-yellow-500/20 border border-yellow-500/60 flex-shrink-0"
                                  data-testid={`member-internal-note-icon-${member.id}`}
                                  aria-label="Har internt notat"
                                >
                                  <StickyNote className="w-3.5 h-3.5 text-yellow-300" />
                                </span>
                              </Tooltip>
                            )}
                            {member.is_paused && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500 text-gray-900 font-bold tracking-wide whitespace-nowrap"
                                title={`Pause-årsak: ${member.pause_reason || 'Ingen oppgitt'}`}
                                data-testid={`paused-badge-${member.id}`}
                              >
                                PAUSET
                              </span>
                            )}
                          </div>
                          {member.is_paused && (
                            <span className="text-[11px] text-yellow-300/90 mt-0.5 italic">
                              Venter på reaktivering av ADMIN
                            </span>
                          )}
                        </div>
                        {/* Dugnad crown icon - right-aligned after name. Skjult på mobil med mindre showDugnadCol=true */}
                        {(user?.organization?.dugnad_feature_enabled !== false) && (
                          <span className={showDugnadCol ? 'flex' : 'hidden md:flex'}>
                          {hasDugnadGoldCrown(member.id) ? (
                            <Tooltip 
                              text={`🏆 Gullkrone oppnådd!\n${getDugnadCount(member.id)} av ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()} i ${new Date().getFullYear()}.\nDette medlemmet har fullført kravet for året.`}
                              variant="gold"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="flex-shrink-0 drop-shadow-lg">
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#EAB308"/>
                                <path d="M19 19H5v-2h14v2z" fill="#EAB308"/>
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" stroke="#CA8A04" strokeWidth="0.5" fill="none"/>
                              </svg>
                            </Tooltip>
                          ) : getDugnadCount(member.id) > 0 ? (
                            <Tooltip 
                              text={`⏳ På vei mot gullkrone!\n${getDugnadCount(member.id)} av ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()} i ${new Date().getFullYear()}.\nMangler ${dugnadThreshold - getDugnadCount(member.id)} for å oppnå gullkrone.`}
                              variant="gray"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="flex-shrink-0 opacity-60">
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#9CA3AF"/>
                                <path d="M19 19H5v-2h14v2z" fill="#9CA3AF"/>
                              </svg>
                            </Tooltip>
                          ) : null}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      <div className="flex items-center justify-between">
                        <span>{member.email}</span>
                        <a
                          href={`mailto:${member.email}`}
                          className="text-yellow-500 hover:text-yellow-400 transition-colors ml-2"
                          title={`Send e-post til ${member.full_name}`}
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {member.skytter_id || '-'}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {member.last_login 
                        ? new Date(member.last_login).toLocaleDateString('nb-NO', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Aldri logget inn'
                      }
                    </td>
                    <td className={`${showRoleCol ? '' : 'w-0 p-0 md:table-cell hidden'} px-1 md:px-6 py-4 md:whitespace-nowrap`}>
                      <div className="flex flex-wrap md:flex-nowrap items-center gap-1 md:gap-2 md:space-x-2">
                        {/* Role Tag */}
                        <span className={`px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs rounded-full font-medium ${
                          member.role === 'admin'
                            ? 'bg-purple-500/20 text-purple-400'
                            : member.role === 'org_admin'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {member.role === 'admin' ? 'Admin' : member.role === 'org_admin' ? 'Skyteleder' : 'Medlem'}
                        </span>
                        
                        {/* Status-tag: Deaktivert har PRIORITET over Venter for å unngå forveksling */}
                        {member.active === false ? (
                          <span
                            className="px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs rounded-full font-bold bg-red-600 text-white tracking-wide"
                            data-testid={`deactivated-badge-${member.id}`}
                          >
                            Deaktivert
                          </span>
                        ) : !member.approved ? (
                          <span className="px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs rounded-full bg-red-500/20 text-red-400">
                            Venter
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-1 md:px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex justify-end space-x-1 md:space-x-2">
                        {/* Locked account icon - Only show if member is locked */}
                        {lockedAccounts.some(lockout => lockout.email === member.email) && (
                          <button
                            onClick={() => {
                              const lockout = lockedAccounts.find(l => l.email === member.email);
                              setSelectedLockedMember({ ...member, lockout });
                              setShowUnlockModal(true);
                            }}
                            className="text-red-600 hover:text-red-500 transition-colors"
                            title="Konto låst - Klikk for detaljer"
                          >
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                        
                        {/* Skyteleder toggle (shield) – kun Admin/Super Admin kan endre roller */}
                        {(user?.role === 'super_admin' || user?.role === 'admin' || impersonationContext?.originalRole === 'super_admin') ? (
                        <button
                          onClick={() => guardRoleChange(member, member.role !== 'org_admin' ? 'org_admin' : 'member', () => toggleAdminStatus(member.id, member.role !== 'org_admin'))}
                          className={`transition-colors ${
                            member.role === 'org_admin' 
                              ? 'text-green-400 hover:text-green-300' 
                              : 'text-gray-500 hover:text-gray-400'
                          }`}
                          title={member.role === 'org_admin' ? 'Fjern skyteleder-rettigheter' : 'Gi skyteleder-rettigheter'}
                          data-testid={`role-skyteleder-toggle-${member.id}`}
                        >
                          <Shield className="h-5 w-5" />
                        </button>
                        ) : (
                          <span className={member.role === 'org_admin' ? 'text-green-400/60' : 'text-gray-600'} title="Bare Admin kan endre roller">
                            <Shield className="h-5 w-5" />
                          </span>
                        )}

                        {/* Organization Admin toggle (star icon) - Only visible for super_admin and admin */}
                        {(user?.role === 'super_admin' || user?.role === 'admin' || impersonationContext?.originalRole === 'super_admin') && (
                          <button
                            onClick={() => guardRoleChange(member, member.role !== 'admin' ? 'admin' : 'member', () => toggleOrgAdminStatus(member.id, member.role !== 'admin'))}
                            data-testid={`role-admin-toggle-${member.id}`}
                            className={`transition-colors ${
                              member.role === 'admin' 
                                ? 'text-yellow-400 hover:text-yellow-300' 
                                : 'text-gray-500 hover:text-gray-400'
                            }`}
                            title={member.role === 'admin' ? 'Fjern admin-rettigheter' : 'Gi admin-rettigheter (full tilgang til QR-koder og Innstillinger)'}
                          >
                            <Star className="h-5 w-5" fill={member.role === 'admin' ? 'currentColor' : 'none'} />
                          </button>
                        )}
                        
                        {/* Approval / Pause toggle (sirkel-ikon)
                            Status-fargekoder:
                            - GRØNN  = aktivt godkjent medlem (approved=true, !is_paused)
                            - GUL    = pauset av admin (is_paused=true)
                            - RØD    = ikke godkjent / deaktivert (approved=false)
                            Klikk:
                            - GRØNN  + admin/super_admin: åpner Pause-popup med valg
                            - GRØNN  + skyteleder: ingen interaktivitet (kun visning)
                            - GUL    + admin/super_admin: reaktiverer (gul → grønn)
                            - GUL    + skyteleder: viser pause-årsak i tooltip
                            - RØD: gammel oppførsel - godkjenn medlem
                         */}
                        {(() => {
                          const isPaused = !!member.is_paused;
                          const isApproved = !!member.approved;

                          // PAUSED state (GUL)
                          if (isPaused) {
                            const pausedTitle = canManagePause
                              ? `🟡 PAUSET: ${member.pause_reason || 'Ingen årsak'}\n\nKlikk for å reaktivere medlemmet (gul → grønn).`
                              : `🟡 PAUSET\nÅrsak vist for medlemmet: ${member.pause_reason || 'Ingen årsak'}\n\nKun ADMIN kan reaktivere.`;
                            return (
                              <button
                                onClick={() => canManagePause && unpauseMember(member)}
                                disabled={!canManagePause}
                                className={`transition-colors ${canManagePause ? 'text-yellow-400 hover:text-yellow-300' : 'text-yellow-400 cursor-help'}`}
                                title={pausedTitle}
                                data-testid={`member-pause-status-${member.id}`}
                              >
                                <CheckCircle className="h-5 w-5" fill="currentColor" fillOpacity="0.25" />
                              </button>
                            );
                          }

                          // APPROVED state (GRØNN) - klikk åpner pause-modal for admin
                          if (isApproved) {
                            if (canManagePause) {
                              return (
                                <button
                                  onClick={() => openPauseModal(member)}
                                  className="text-green-400 hover:text-green-300 transition-colors"
                                  title="Aktivt medlem - klikk for å sette på PAUSE eller deaktivere"
                                  data-testid={`member-active-${member.id}`}
                                >
                                  <CheckCircle className="h-5 w-5" />
                                </button>
                              );
                            }
                            // Skyteleder ser bare grønn status uten interaksjon
                            return (
                              <span
                                className="text-green-400 cursor-default inline-block"
                                title="Aktivt medlem (kun ADMIN kan endre status)"
                              >
                                <CheckCircle className="h-5 w-5" />
                              </span>
                            );
                          }

                          // NOT APPROVED state (RØD) - gammel oppførsel: godkjenn
                          return (
                            <button
                              onClick={() => approveUser(member.id, true)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                              title="Godkjenn medlem"
                              data-testid={`member-pending-${member.id}`}
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          );
                        })()}
                        
                        {/* Edit button */}
                        <button 
                          onClick={() => editMember(member)}
                          className="text-blue-400 hover:text-blue-300"
                          title="Rediger medlem"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        
                        {/* Change Email button - Only for admin and super_admin */}
                        {(user?.role === 'admin' || user?.role === 'super_admin' || impersonationContext?.originalRole === 'super_admin') && (
                          <button 
                            onClick={() => openEmailChangeModal(member)}
                            className="text-yellow-400 hover:text-yellow-300"
                            title="Endre e-postadresse"
                          >
                            <Mail className="h-5 w-5" />
                          </button>
                        )}
                        
                        {/* Removed delete button - moved to edit modal */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination - Only shown if > 100 members */}
          {shouldPaginate && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-400">
                Viser {startIndex + 1}-{Math.min(startIndex + membersPerPage, totalMembers)} av {totalMembers} medlemmer
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setMemberPage(Math.max(1, memberPage - 1))}
                  disabled={memberPage === 1}
                  className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Forrige
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, memberPage - 2)) + i;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setMemberPage(pageNum)}
                      className={`px-3 py-2 rounded-lg text-sm ${
                        memberPage === pageNum
                          ? 'bg-yellow-500 text-gray-900'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setMemberPage(Math.min(totalPages, memberPage + 1))}
                  disabled={memberPage === totalPages}
                  className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Neste
                </button>
              </div>
            </div>
          )}

          {/* Bulk Member Delete Section - Exact copy of Klubb-logg style */}
          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-gray-300 font-medium text-sm">Administrer oppføringer</h3>
                {!bulkDeleteMode ? (
                  <button
                    onClick={() => {
                      setBulkDeleteMode(true);
                      setSelectedMembers([]);
                    }}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-gray-300 text-xs rounded transition-colors"
                  >
                    Velg for sletting
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400">
                      {selectedMembers.length} valgt
                    </span>
                    <button
                      onClick={toggleAllMembers}
                      className="text-blue-400 hover:text-blue-300 text-xs"
                    >
                      {selectedMembers.length === filteredMembers.length ? 'Fjern alle' : 'Velg alle'}
                    </button>
                    <button
                      onClick={() => {
                        setBulkDeleteMode(false);
                        setSelectedMembers([]);
                      }}
                      className="text-gray-400 hover:text-gray-300 text-xs"
                    >
                      Avbryt
                    </button>
                    <button
                      onClick={handleBulkDeleteMembers}
                      disabled={selectedMembers.length === 0}
                      className="px-2 py-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                    >
                      Slett ({selectedMembers.length})
                    </button>
                  </div>
                )}
              </div>
              
              {/* Audit log toggle - shows all changes, not just deletions */}
              <button
                onClick={() => {
                  if (!showDeletionLogs) {
                    loadMemberAuditLogs();
                  }
                  setShowDeletionLogs(!showDeletionLogs);
                }}
                className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
              >
                {showDeletionLogs ? 'Skjul' : 'Vis'} endringslogg
              </button>
            </div>
            
            {/* Audit log - Shows all changes (creates, updates, deletes) */}
            {showDeletionLogs && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                {loadingAuditLogs ? (
                  <p className="text-xs text-gray-500 italic">Laster endringslogg...</p>
                ) : memberAuditLogs.length === 0 && deletionLogs.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">Ingen endringer registrert</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {/* Combine audit logs and deletion logs, sort by date */}
                    {[...memberAuditLogs, ...deletionLogs.map(dl => ({
                      id: dl.id,
                      user_name: dl.deleted_user_name,
                      user_email: dl.deleted_user_email,
                      action: 'deleted',
                      changed_by_name: dl.deleted_by_name,
                      changed_at: dl.deleted_at,
                      description: `Medlem slettet. ${dl.training_sessions_deleted || 0} treningsøkter fjernet.`
                    }))].sort((a, b) => {
                      const dateA = new Date(a.changed_at || a.deleted_at);
                      const dateB = new Date(b.changed_at || b.deleted_at);
                      return dateB - dateA;
                    }).slice(0, 20).map((log, index) => {
                      // Determine badge color and text based on action
                      let badgeClass = '';
                      let badgeText = '';
                      switch (log.action) {
                        case 'deleted':
                          badgeClass = 'bg-red-700 text-white font-bold';
                          badgeText = '🗑️ HARDT SLETTET';
                          break;
                        case 'soft_deleted':
                          badgeClass = 'bg-gray-600 text-white font-bold';
                          badgeText = '🗑️ SLETTET';
                          break;
                        case 'restored':
                          badgeClass = 'bg-emerald-600 text-white font-bold';
                          badgeText = '♻️ GJENOPPRETTET';
                          break;
                        case 'approved':
                          badgeClass = 'bg-green-600 text-white font-bold';
                          badgeText = '✅ GODKJENT';
                          break;
                        case 'deactivated':
                          badgeClass = 'bg-red-600 text-white font-bold';
                          badgeText = '🔴 DEAKTIVERT';
                          break;
                        case 'reactivated':
                          badgeClass = 'bg-green-500 text-white font-bold';
                          badgeText = '🟢 REAKTIVERT';
                          break;
                        case 'paused':
                          badgeClass = 'bg-yellow-500 text-gray-900 font-bold';
                          badgeText = '⏸️ PAUSET';
                          break;
                        case 'unpaused':
                          badgeClass = 'bg-blue-500 text-white font-bold';
                          badgeText = '▶️ GJENOPPTATT';
                          break;
                        case 'role_changed':
                          badgeClass = 'bg-purple-600 text-white font-bold';
                          badgeText = '👤 ROLLE';
                          break;
                        case 'password_reset':
                          badgeClass = 'bg-yellow-500 text-gray-900 font-bold';
                          badgeText = '🔑 PASSORD';
                          break;
                        case 'created':
                          badgeClass = 'bg-blue-600 text-white font-bold';
                          badgeText = '✨ NYTT MEDLEM';
                          break;
                        case 'email_changed':
                          badgeClass = 'bg-indigo-600 text-white font-bold';
                          badgeText = '✉️ E-POST';
                          break;
                        case 'name_changed':
                          badgeClass = 'bg-cyan-600 text-white font-bold';
                          badgeText = '📝 NAVN';
                          break;
                        case 'birth_number_changed':
                          badgeClass = 'bg-pink-600 text-white font-bold';
                          badgeText = '🎂 FØDSELSNR';
                          break;
                        case 'member_number_changed':
                          badgeClass = 'bg-teal-600 text-white font-bold';
                          badgeText = '🆔 MEDLEMSNR';
                          break;
                        case 'notes_updated':
                          badgeClass = 'bg-amber-600 text-white font-bold';
                          badgeText = '🗒️ NOTAT';
                          break;
                        case 'weapon_certs_updated':
                          badgeClass = 'bg-orange-700 text-white font-bold';
                          badgeText = '🔫 VÅPENKORT';
                          break;
                        case 'membership_card_updated':
                          badgeClass = 'bg-sky-600 text-white font-bold';
                          badgeText = '💳 MEDLEMSKORT';
                          break;
                        case 'startkort_updated':
                          badgeClass = 'bg-fuchsia-600 text-white font-bold';
                          badgeText = '🎯 STARTKORT';
                          break;
                        case 'diploma_updated':
                          badgeClass = 'bg-yellow-600 text-white font-bold';
                          badgeText = '🏆 DIPLOM';
                          break;
                        default:
                          badgeClass = 'bg-gray-500 text-white font-medium';
                          badgeText = '✏️ ENDRET';
                      }
                      
                      return (
                        <div key={log.id || index} className="bg-gray-900/30 rounded-lg p-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
                                {badgeText}
                              </span>
                              <span className="text-gray-200 text-sm font-medium">{log.user_name}</span>
                              <span className="text-gray-500 text-xs">{log.user_email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span>av {log.changed_by_name}</span>
                              <span>•</span>
                              <span>
                                {new Date(log.changed_at).toLocaleDateString('nb-NO', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: '2-digit'
                                })}
                                {' '}
                                {new Date(log.changed_at).toLocaleTimeString('nb-NO', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                          {log.description && (
                            <p className="text-xs text-gray-400 mt-1 pl-1">{log.description}</p>
                          )}
                          {log.changes && Object.keys(log.changes).length > 0 && (
                            <div className="mt-1 pl-1 text-xs text-gray-500">
                              {Object.entries(log.changes).slice(0, 3).map(([field, values]) => (
                                <span key={field} className="mr-2">
                                  <span className="text-gray-400">{field}:</span>{' '}
                                  <span className="text-red-400/70 line-through">{String(values.old || '-')}</span>{' → '}
                                  <span className="text-green-400/70">{String(values.new || '-')}</span>
                                </span>
                              ))}
                              {Object.keys(log.changes).length > 3 && (
                                <span className="text-gray-500">+{Object.keys(log.changes).length - 3} flere</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {(memberAuditLogs.length + deletionLogs.length) > 20 && (
                      <p className="text-xs text-gray-500 italic text-center pt-1">
                        Viser 20 av {memberAuditLogs.length + deletionLogs.length} endringer
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="bg-gray-800 rounded-lg p-3 md:p-6 -mx-2 md:mx-0">
          {/* Today's Skyteleder from Calendar */}
          {user?.organization?.calendar_enabled !== false && todaySkyteledere.length > 0 && (
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span className="text-sm text-blue-400 font-medium">Dagens skyteleder:</span>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(todaySkyteledere.reduce((acc, sl) => {
                    if (!acc[sl.name]) acc[sl.name] = [];
                    acc[sl.name].push(sl);
                    return acc;
                  }, {})).map(([name, events], idx) => (
                    <span key={idx} className="text-sm text-white bg-blue-500/20 px-2 py-0.5 rounded">
                      {name}
                      {events.map((ev, i) => (
                        <span key={i} className="text-blue-300 ml-1">
                          {i > 0 && ' · '}
                          {ev.location && `(${ev.location})`}{ev.start_time && ` ${ev.start_time}-${ev.end_time}`}
                        </span>
                      ))}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Header with organization info */}
          {/* Header with organization info */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold text-white">Komplett Treningslogg</h2>
                <p className="text-gray-400 text-sm">Alle treningsregistreringer for {user?.organization?.name || 'organisasjonen'}</p>
              </div>
              
              {/* Mobile: Buttons on separate line */}
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <span className="text-xs text-gray-400 w-full md:w-auto">
                  Viser {Math.min(sessionStartIndex + 1, filteredSessions.length)}-{Math.min(sessionEndIndex, filteredSessions.length)} av {filteredSessions.length}
                </span>
                
                {/* Removed old bulk delete UI - replaced with "Administrer oppføringer" section below */}
                
                {/* Export dropdown */}
                <div className="relative">
                  <button 
                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                    className="px-3 md:px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors flex items-center text-sm"
                  >
                    <Download className="hidden md:block h-4 w-4 mr-2" />
                    Last ned Treningslogg
                    <div className="ml-2">▼</div>
                  </button>
                
                {showExportDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-gray-700 rounded-lg shadow-lg border border-gray-600 z-50 min-w-[280px]">
                    <button 
                      onClick={() => {
                        downloadFilteredTrainingLog('csv');
                        setShowExportDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-white hover:bg-gray-600 rounded-t-lg flex items-center"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      CSV Format
                    </button>
                    <button 
                      onClick={() => {
                        downloadFilteredTrainingLog('pdf');
                        setShowExportDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-white hover:bg-gray-600 flex items-center border-t border-gray-600"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      PDF Format
                    </button>
                    <button 
                      onClick={() => {
                        openKlubbloggPdfDialog('politi');
                        setShowExportDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-white hover:bg-gray-600 flex items-center border-t border-gray-600"
                    >
                      <FileText className="h-4 w-4 mr-2 text-yellow-400" />
                      <span>Politi-PDF <span className="text-gray-400 text-xs">(Bruk kun for enkeltmedlem)</span></span>
                    </button>
                    <button 
                      onClick={() => {
                        openKlubbloggPdfDialog('standard');
                        setShowExportDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-white hover:bg-gray-600 rounded-b-lg flex items-center border-t border-gray-600"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      <span>Standard-PDF <span className="text-gray-400 text-xs">(Bruk kun for enkeltmedlem)</span></span>
                    </button>
                  </div>
                )}
              </div>
                
                <button 
                  onClick={() => setShowManualRegistration(true)}
                  className="px-3 md:px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors text-sm"
                >
                  <span className="hidden md:inline">+ </span>Registrer aktivitet
                </button>
                
                {/* Bulk Approve Toggle Button */}
                <button 
                  onClick={() => {
                    setBulkApproveMode(!bulkApproveMode);
                    if (bulkApproveMode) {
                      // Exiting bulk mode - clear selections
                      setSelectedSessionsForApproval(new Set());
                    }
                  }}
                  className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-colors text-sm flex items-center space-x-2 ${
                    bulkApproveMode 
                      ? 'bg-red-600 text-white hover:bg-red-500' 
                      : 'bg-green-600 text-white hover:bg-green-500'
                  }`}
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>{bulkApproveMode ? 'Avbryt bulk-godkjenning' : 'Bulk godkjenn treninger'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Collapsible Advanced Filtering Section */}
          <div className="bg-gray-700 rounded-lg border border-gray-600 mb-6 relative z-20">
            {/* Universal: Compact filter toggle for all screen sizes */}
            <div className="block">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="w-full p-3 flex items-center justify-between text-white hover:bg-gray-600 transition-colors"
              >
                <span className="font-medium text-lg">Filtrer treningslogger</span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Clear all filters
                      setSessionMemberFilter('');
                      setSessionSearch('');
                      setSessionDateFilter('alle');
                      setCustomDateFrom('');
                      setCustomDateTo('');
                      setSelectedActivities(new Set());
                      setSessionActivityTypes(new Set());
                      setSessionLocationFilter('');
                      setSessionOfficerFilter('');
                      setSessionStatusFilter('');
                      setSessionBranchFilter('alle');
                      setSessionPage(1);
                    }}
                    className="text-red-400 hover:text-red-300 text-sm font-medium px-2 py-1 rounded"
                  >
                    Nullstill
                  </button>
                  {showMobileFilters ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </button>
            </div>
            
            {/* Filter Content - Collapsible for all screen sizes */}
            <div className={`${showMobileFilters ? 'block' : 'hidden'} p-4`}>
              {/* Member and Search Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Member Filter Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Medlem</label>
                  <select
                    value={sessionMemberFilter}
                    onChange={(e) => setSessionMemberFilter(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Alle medlemmer</option>
                    {(() => {
                      // Get ALL members from organization (not just those with sessions)
                      const allMembers = members
                        .filter(m => m.approved === true) // Only show approved members
                        .map(member => ({
                          id: member.id,
                          name: member.full_name || 'Ukjent'
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name, 'no'));
                      
                      return allMembers.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ));
                    })()}
                  </select>
                </div>

                {/* General Search Bar */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Fritekst søk</label>
                  <input
                    type="text"
                    placeholder="Søk i aktivitet, sted, skyteleder, notater..."
                    value={sessionSearch}
                    onChange={(e) => setSessionSearch(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              {/* Time Period Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Tidsperiode</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                  {[
                    { value: 'alle', label: 'Alle' },
                    { value: '6months', label: 'Siste 6 måneder' },
                    { value: '1year', label: 'Siste år' },
                    { value: '2years', label: 'Siste 2 år' },
                    { value: 'custom', label: 'Egendefinert' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSessionDateFilter(option.value)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        sessionDateFilter === option.value
                          ? 'bg-yellow-500 text-gray-900'
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                
                {/* Custom Date Range */}
                {sessionDateFilter === 'custom' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-gray-700/50 rounded-lg">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Fra dato</label>
                      <input
                        type="date"
                        value={customDateFrom}
                        onChange={(e) => setCustomDateFrom(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Til dato</label>
                      <input
                        type="date"
                        value={customDateTo}
                        onChange={(e) => setCustomDateTo(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Branch Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Skyttergrener</label>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    // Define all possible shooting branches with labels and colors
                    const allBranches = [
                      { value: 'nsf', label: 'NSF', color: 'bg-blue-500' },
                      { value: 'dfs', label: 'DFS', color: 'bg-purple-500' },
                      { value: 'dssn', label: 'DSSN', color: 'bg-green-500' },
                      { value: 'sws', label: 'SWS', color: 'bg-orange-500' },
                      { value: 'nsu', label: 'NSU', color: 'bg-red-500' },
                      { value: 'nrof', label: 'NROF', color: 'bg-indigo-500' },
                      { value: 'nmsf', label: 'NMSF', color: 'bg-pink-500' },
                      { value: 'njff', label: 'NJFF', color: 'bg-yellow-600' },
                      { value: 'nbif', label: 'NBIF', color: 'bg-teal-500' },
                      { value: 'nbsf', label: 'NBSF', color: 'bg-cyan-500' },
                      { value: 'nssf', label: 'NSSF', color: 'bg-sky-500' }
                    ];
                    
                    // Filter to only show active branches from organization settings
                    const activeBranches = allBranches.filter(branch => 
                      user?.organization?.shooting_branches?.[branch.value] === true
                    );
                    
                    // Always include "Alle grener" option first
                    const branchOptions = [
                      { value: 'alle', label: 'Alle grener', color: 'bg-gray-600' },
                      ...activeBranches
                    ];
                    
                    return branchOptions.map((branch) => (
                      <button
                        key={branch.value}
                        onClick={() => setSessionBranchFilter(branch.value)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          sessionBranchFilter === branch.value
                            ? `${branch.color} text-white`
                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        }`}
                      >
                        {branch.label}
                      </button>
                    ));
                  })()}
                </div>
              </div>

              {/* Activity Types Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Aktiviteter (multiple valg)</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {(() => {
                    // Get activities from organization settings (not just from existing sessions)
                    const organizationActivities = user?.organization?.activity_types || ['Organisert trening'];
                    
                    return organizationActivities.map((activity) => {
                      const isSelected = selectedActivities.has(activity);
                      return (
                        <label
                          key={activity}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newSelected = new Set(selectedActivities);
                              if (e.target.checked) {
                                newSelected.add(activity);
                              } else {
                                newSelected.delete(activity);
                              }
                              setSelectedActivities(newSelected);
                            }}
                            className="h-4 w-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                          />
                          <span className="truncate">{activity}</span>
                        </label>
                      );
                    });
                  })()}
                </div>
                {selectedActivities.size > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {selectedActivities.size} aktivitet{selectedActivities.size !== 1 ? 'er' : ''} valgt
                    </span>
                    <button
                      onClick={() => setSelectedActivities(new Set())}
                      className="text-xs text-red-400 hover:text-red-300 underline"
                    >
                      Nullstill
                    </button>
                  </div>
                )}
              </div>

              {/* Existing Filters Row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Location Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Sted/Bane</label>
                  <select
                    value={sessionLocationFilter}
                    onChange={(e) => setSessionLocationFilter(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Alle baner</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Range Officer Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Skyteleder</label>
                  <select
                    value={sessionOfficerFilter}
                    onChange={(e) => setSessionOfficerFilter(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Alle skyteleder</option>
                    {members
                      .filter(m => m.role === 'org_admin')
                      .map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.full_name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={sessionStatusFilter}
                    onChange={(e) => setSessionStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Alle</option>
                    <option value="verified">Godkjente</option>
                    <option value="pending">Ventende godkjenning</option>
                  </select>
                </div>
              </div>

              {/* Dugnad Statistics Section - Only show if feature enabled */}
              {(user?.organization?.dugnad_feature_enabled !== false) && (
              <div className="mt-4 pt-4 border-t border-gray-600">
                <button
                  onClick={() => {
                    setShowDugnadStats(!showDugnadStats);
                    if (!showDugnadStats) calculateDugnadCounts();
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    showDugnadStats
                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l3 6h6l-5 4.5 2 6.5-6-4-6 4 2-6.5L3 8h6z"/>
                  </svg>
                  Dugnad-statistikk
                  {showDugnadStats && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  )}
                </button>
                
                {showDugnadStats && (
                  <div className="mt-3 p-4 bg-gray-800/50 rounded-lg border border-gray-600">
                    {/* Current settings info and PDF buttons row */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <div className="text-sm text-gray-400">
                        <span className="text-yellow-400 font-medium">{selectedDugnadActivity}</span>
                        <span className="mx-2">•</span>
                        <span>Krav: <span className="text-white">{dugnadThreshold}</span> for gullkrone</span>
                        <span className="ml-2 text-xs">(Endre i Innstillinger)</span>
                      </div>
                      
                      {/* PDF Download buttons - right aligned */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={generateDugnadMissingPDF}
                          disabled={isGeneratingDugnadPdf}
                          className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-600 disabled:cursor-wait text-gray-900 font-medium rounded-lg transition-colors text-sm"
                          title="Last ned PDF-liste over medlemmer som mangler dugnadskravet"
                        >
                          {isGeneratingDugnadPdf ? (
                            <>
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Genererer...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4" />
                              Mangler krav (PDF)
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={generateDugnadCompletedPDF}
                          disabled={isGeneratingDugnadPdf}
                          className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-400 disabled:bg-green-600 disabled:cursor-wait text-white font-medium rounded-lg transition-colors text-sm"
                          title="Last ned PDF-liste over medlemmer som har oppfylt dugnadskravet"
                        >
                          {isGeneratingDugnadPdf ? (
                            <>
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Genererer...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4" />
                              Oppfyller krav (PDF)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Legend row */}
                    <div className="flex items-center gap-3 text-sm mb-4">
                      <span className="flex items-center gap-1 text-yellow-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="currentColor"/>
                          <path d="M19 19H5v-2h14v2z" fill="currentColor"/>
                        </svg>
                        Gullkrone = ≥{dugnadThreshold}
                      </span>
                      <span className="flex items-center gap-1 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="opacity-50">
                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="currentColor"/>
                          <path d="M19 19H5v-2h14v2z" fill="currentColor"/>
                        </svg>
                        Grå krone = &lt;{dugnadThreshold}
                      </span>
                    </div>
                    
                    {/* Date Filter Toggle */}
                    <div className="mb-4">
                      <button
                        onClick={() => setShowDugnadDateFilter(!showDugnadDateFilter)}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                      >
                        <Calendar className="h-4 w-4" />
                        Filtrer tidsperiode
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          className={`transition-transform ${showDugnadDateFilter ? 'rotate-180' : ''}`}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      
                      {showDugnadDateFilter && (
                        <div className="mt-3 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {[
                              { value: 'year', label: `I år (${new Date().getFullYear()})` },
                              { value: '30', label: 'Siste 30 dager' },
                              { value: '90', label: 'Siste 90 dager' },
                              { value: '365', label: 'Siste 365 dager' },
                              { value: 'all', label: 'Alle' },
                              { value: 'custom', label: 'Egendefinert' },
                            ].map(option => (
                              <button
                                key={option.value}
                                onClick={() => setDugnadDateFilter(option.value)}
                                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                  dugnadDateFilter === option.value
                                    ? 'bg-yellow-500 text-gray-900 font-medium'
                                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                          
                          {dugnadDateFilter === 'custom' && (
                            <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-600">
                              <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-400">Fra:</label>
                                <input
                                  type="date"
                                  value={dugnadCustomStartDate}
                                  onChange={(e) => setDugnadCustomStartDate(e.target.value)}
                                  className="px-3 py-1.5 bg-gray-600 border border-gray-500 rounded-lg text-white text-sm"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-400">Til:</label>
                                <input
                                  type="date"
                                  value={dugnadCustomEndDate}
                                  onChange={(e) => setDugnadCustomEndDate(e.target.value)}
                                  className="px-3 py-1.5 bg-gray-600 border border-gray-500 rounded-lg text-white text-sm"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Dugnad member list - only show members with at least 1 dugnad */}
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-800">
                          <tr className="text-left text-gray-400">
                            <th className="pb-2">Status</th>
                            <th className="pb-2">Medlem</th>
                            <th className="pb-2 text-center">{selectedDugnadActivity}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {members
                            .filter(m => m.approved && (memberDugnadCounts[m.id] || 0) > 0) // Only show members with dugnads
                            .sort((a, b) => (memberDugnadCounts[b.id] || 0) - (memberDugnadCounts[a.id] || 0))
                            .map(member => {
                              const count = memberDugnadCounts[member.id] || 0;
                              const hasGold = count >= dugnadThreshold;
                              return (
                                <tr key={member.id} className="border-t border-gray-700">
                                  <td className="py-2">
                                    {hasGold ? (
                                      <Tooltip 
                                        text={`🏆 Gullkrone oppnådd!\n${count} av ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()} i ${new Date().getFullYear()}.\nDette medlemmet har fullført kravet for året.`}
                                        variant="gold"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="drop-shadow-lg">
                                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#EAB308"/>
                                          <path d="M19 19H5v-2h14v2z" fill="#EAB308"/>
                                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" stroke="#CA8A04" strokeWidth="0.5" fill="none"/>
                                        </svg>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip 
                                        text={`⏳ På vei mot gullkrone!\n${count} av ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()} i ${new Date().getFullYear()}.\nMangler ${dugnadThreshold - count} for å oppnå gullkrone.`}
                                        variant="gray"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="opacity-60">
                                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#6B7280"/>
                                          <path d="M19 19H5v-2h14v2z" fill="#6B7280"/>
                                        </svg>
                                      </Tooltip>
                                    )}
                                  </td>
                                  <td className={`py-2 ${hasGold ? 'text-white font-medium' : 'text-gray-400'}`}>
                                    {member.full_name}
                                  </td>
                                  <td className={`py-2 text-center ${hasGold ? 'text-yellow-400 font-bold' : 'text-gray-500'}`}>
                                    {count}
                                  </td>
                                </tr>
                              );
                            })}
                          {members.filter(m => m.approved && (memberDugnadCounts[m.id] || 0) > 0).length === 0 && (
                            <tr>
                              <td colSpan="3" className="py-4 text-center text-gray-400">
                                Ingen medlemmer har deltatt på {selectedDugnadActivity.toLowerCase()} i {new Date().getFullYear()}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-600 text-xs text-gray-400">
                      <p>
                        <strong>Totalt:</strong> {Object.values(memberDugnadCounts).filter(c => c >= dugnadThreshold).length} av {members.filter(m => m.approved).length} medlemmer har oppnådd gullkrone-status
                      </p>
                    </div>
                  </div>
                )}
              </div>
              )}

            </div>
          </div>

          {/* Bulk Approve Section - Only show when in bulk approve mode */}
          {bulkApproveMode && (
            <div className="bg-green-900/20 border border-green-600 rounded-lg p-3 mt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="text-green-300 font-medium text-sm">Bulk godkjenning aktiv</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-green-400">
                      {selectedSessionsForApproval.size} valgt
                    </span>
                    <button
                      onClick={selectAllForApproval}
                      disabled={paginatedSessions.filter(s => !s.verified).length === 0}
                      className="text-blue-400 hover:text-blue-300 text-xs disabled:text-gray-500"
                    >
                      Velg alle ikke-godkjente
                    </button>
                    <button
                      onClick={deselectAllForApproval}
                      disabled={selectedSessionsForApproval.size === 0}
                      className="text-gray-400 hover:text-gray-300 text-xs disabled:text-gray-500"
                    >
                      Tøm valg
                    </button>
                    {selectedSessionsForApproval.size > 0 && (
                      <button
                        onClick={bulkApproveSessions}
                        disabled={bulkApprovingInProgress}
                        className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white text-xs rounded transition-colors disabled:opacity-50 flex items-center space-x-1"
                      >
                        {bulkApprovingInProgress ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            <span>Godkjenner...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-3 w-3" />
                            <span>Godkjenn ({selectedSessionsForApproval.size})</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <p className="mt-1 text-xs text-green-300">
                Kryss av ikke-godkjente treninger og klikk "Godkjenn" for å godkjenne flere samtidig. Allerede godkjente treninger kan ikke velges.
              </p>
            </div>
          )}

          {/* Date display */}
          <div className="mb-4 text-center">
            <span className="text-yellow-500 font-medium text-lg">
              {new Date().toLocaleDateString('no-NO')}
            </span>
          </div>

          {/* Sessions Table */}
          <div className="overflow-x-auto overflow-y-visible relative" style={{ zIndex: 30 }}>
            <table className="min-w-full bg-gray-700 rounded-lg">
              <thead className="relative" style={{ zIndex: 10 }}>
                <tr className="bg-gray-600">
                  {/* Checkbox column - only show when in bulk delete mode */}
                  {bulkDeleteMode && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={paginatedSessions.length > 0 && paginatedSessions.every(s => selectedSessions.has(s.id))}
                        onChange={() => {
                          if (paginatedSessions.length > 0 && paginatedSessions.every(s => selectedSessions.has(s.id))) {
                            const newSelected = new Set(selectedSessions);
                            paginatedSessions.forEach(s => newSelected.delete(s.id));
                            setSelectedSessions(newSelected);
                          } else {
                            selectAllSessions();
                          }
                        }}
                        className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 focus:ring-2"
                      />
                    </th>
                  )}
                  
                  {/* Checkbox column - only show when in bulk approve mode */}
                  {bulkApproveMode && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={paginatedSessions.filter(s => !s.verified).length > 0 && 
                                   paginatedSessions.filter(s => !s.verified).every(s => selectedSessionsForApproval.has(s.id))}
                          onChange={() => {
                            const nonVerified = paginatedSessions.filter(s => !s.verified);
                            if (nonVerified.length > 0 && nonVerified.every(s => selectedSessionsForApproval.has(s.id))) {
                              deselectAllForApproval();
                            } else {
                              selectAllForApproval();
                            }
                          }}
                          className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                        />
                        <span className="text-xs text-gray-400">
                          ({selectedSessionsForApproval.size})
                        </span>
                      </div>
                    </th>
                  )}
                  
                  {/* Sortable Dato Column */}
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-500 transition-colors"
                    onClick={() => handleColumnSort('start_time')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Dato</span>
                      {sortColumn === 'start_time' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable Medlem Column */}
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-500 transition-colors"
                    onClick={() => handleColumnSort('user_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Medlem</span>
                      {sortColumn === 'user_name' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable Aktivitet Column */}
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-500 transition-colors"
                    onClick={() => handleColumnSort('activity')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Aktivitet</span>
                      {sortColumn === 'activity' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable Bane Column */}
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-500 transition-colors"
                    onClick={() => handleColumnSort('location_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Bane</span>
                      {sortColumn === 'location_name' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable Skyteleder Column */}
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-500 transition-colors"
                    onClick={() => handleColumnSort('verified_by')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Skyteleder</span>
                      {sortColumn === 'verified_by' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable Status Column */}
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-500 transition-colors"
                    onClick={() => handleColumnSort('verified')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'verified' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Non-sortable Actions Column */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="relative" style={{ zIndex: 40 }}>
                {paginatedSessions.map((session, index) => (
                  <tr 
                    key={session.id} 
                    className={`border-b border-gray-600 hover:bg-gray-600 transition-colors ${
                      session.verified ? 'bg-green-900/10' : 'bg-red-900/10'
                    }`}
                  >
                    {/* Selection Checkbox - only show when in bulk delete mode */}
                    {bulkDeleteMode && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedSessions.has(session.id)}
                          onChange={() => toggleSessionSelection(session.id)}
                          className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 focus:ring-2"
                        />
                      </td>
                    )}
                    
                    {/* Selection Checkbox - only show when in bulk approve mode */}
                    {bulkApproveMode && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedSessionsForApproval.has(session.id)}
                          onChange={() => toggleSessionForApproval(session.id)}
                          disabled={session.verified}
                          className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500 focus:ring-2 disabled:opacity-30 disabled:cursor-not-allowed"
                        />
                      </td>
                    )}
                    
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {new Date(session.start_time).toLocaleDateString('no-NO')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-white font-medium">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {session.is_trial && (
                            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">PRØVE</span>
                          )}
                          {session.is_trial ? (session.trial_name || 'Prøveskyter') : session.user_name}
                          {!session.is_trial && <MultiClubBadge otherClubs={multiClubMap[session.user_id]} />}
                          {/* Notat-indikator: gul, mer synlig på rader med admin_notes */}
                          {session.admin_notes && session.admin_notes.trim() && (
                            <Tooltip
                              text={`Internt notat:\n${session.admin_notes.slice(0, 160)}${session.admin_notes.length > 160 ? '…' : ''}${session.admin_notes_updated_by ? `\n— ${session.admin_notes_updated_by}` : ''}`}
                              variant="gray"
                            >
                              <span
                                className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-yellow-500/20 border border-yellow-500/60 flex-shrink-0"
                                data-testid={`session-admin-note-icon-${session.id}`}
                                aria-label="Har internt notat"
                              >
                                <StickyNote className="w-3.5 h-3.5 text-yellow-300" />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        {/* Dugnad crown icon - right-aligned after name (only if feature enabled) */}
                        {(user?.organization?.dugnad_feature_enabled !== false) && !session.is_trial && (
                          hasDugnadGoldCrown(session.user_id) ? (
                            <Tooltip 
                              text={`🏆 Gullkrone oppnådd!\n${getDugnadCount(session.user_id)} av ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()} i ${new Date().getFullYear()}.\nDette medlemmet har fullført kravet for året.`}
                              variant="gold"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="flex-shrink-0 drop-shadow-lg">
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#EAB308"/>
                                <path d="M19 19H5v-2h14v2z" fill="#EAB308"/>
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" stroke="#CA8A04" strokeWidth="0.5" fill="none"/>
                              </svg>
                            </Tooltip>
                          ) : getDugnadCount(session.user_id) > 0 ? (
                            <Tooltip 
                              text={`⏳ På vei mot gullkrone!\n${getDugnadCount(session.user_id)} av ${dugnadThreshold} ${selectedDugnadActivity.toLowerCase()} i ${new Date().getFullYear()}.\nMangler ${dugnadThreshold - getDugnadCount(session.user_id)} for å oppnå gullkrone.`}
                              variant="gray"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="flex-shrink-0 opacity-60">
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#9CA3AF"/>
                                <path d="M19 19H5v-2h14v2z" fill="#9CA3AF"/>
                              </svg>
                            </Tooltip>
                          ) : null
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center space-x-2">
                        <span>{session.activity || 'Trening'}</span>
                        {(() => {
                          const branch = getBranchFromLocation(session);
                          return branch ? (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${branch.color} ${branch.textColor}`}>
                              {branch.name}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {session.location_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {session.verified_by || 'Ikke godkjent'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        session.verified
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {session.verified ? 'Verifisert' : 'Ikke verifisert'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingSession(session);
                            
                            // Ensure activity is valid - use first activity type if session.activity is invalid
                            const validActivity = user?.organization?.activity_types?.includes(session.activity) 
                              ? session.activity 
                              : (user?.organization?.activity_types?.[0] || 'Trening');
                            
                            setEditSessionData({
                              date: new Date(session.start_time).toISOString().split('T')[0],
                              activity: validActivity,
                              location_id: session.location_id || '',
                              range_officer_name: session.verified_by || '',
                              shooting_branch: session.location_discipline || 'nsf',
                              user_id: session.user_id || '',
                              admin_notes: session.admin_notes || ''
                            });
                            setShowEditSessionModal(true);
                          }}
                          className="p-1 rounded transition-colors text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          title="Rediger"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => verifySession(session.id, true)}
                          disabled={session.verified}
                          className={`p-1 rounded transition-colors ${
                            session.verified 
                              ? 'text-gray-500 cursor-not-allowed' 
                              : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                          }`}
                          title="Godkjenn"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => verifySession(session.id, false)}
                          disabled={!session.verified}
                          className={`p-1 rounded transition-colors ${
                            !session.verified 
                              ? 'text-gray-500 cursor-not-allowed' 
                              : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                          }`}
                          title="Avvis"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteSession(session.id)}
                          className="p-1 rounded transition-colors text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          title="Slett økt"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        
                        {/* Ammo Sale Button */}
                        {ammoModuleEnabled && (
                          <button
                            onClick={() => {
                              setSelectedSessionForAmmo(session);
                              setShowAmmoSaleModal(true);
                            }}
                            className={`p-1 rounded transition-colors ${
                              sessionAmmoSales[session.id]
                                ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10'
                                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-500/10'
                            }`}
                            title="Registrer ammo-salg"
                          >
                            <svg 
                              className="h-4 w-4"
                              fill={sessionAmmoSales[session.id] ? '#FFD700' : '#9CA3AF'}
                              xmlns="http://www.w3.org/2000/svg" 
                              viewBox="0 0 1720.46 1622.88"
                            >
                              <path d="M239.43,0C56.46,0,19.96,196.91,18.46,308.13h438.7C453.42,194.7,429.65,0,239.43,0Z"/>
                              <path d="M437.17,345.42H38.43c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69H0v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                              <path d="M861.85,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                              <path d="M1059.59,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                              <path d="M1484.28,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                              <path d="M1682.02,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredSessions.length === 0 && (
              <div className="text-center text-gray-400 py-12">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold text-white mb-2">Ingen treningsøkter funnet</h3>
                <p className="text-sm">
                  {sessions.length === 0 
                    ? 'Ingen treningsøkter registrert ennå.' 
                    : 'Prøv å endre filterinnstillingene for å se flere økter.'
                  }
                </p>
              </div>
            )}

            {/* Session Pagination for Admin > LOGG */}
            {totalSessionPages > 1 && (
              <div className="flex items-center justify-between bg-gray-700 px-6 py-3 border-t border-gray-600 rounded-b-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setSessionPage(sessionPage - 1)}
                    disabled={sessionPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-500 text-sm font-medium rounded-md text-gray-300 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Forrige
                  </button>
                  <button
                    onClick={() => setSessionPage(sessionPage + 1)}
                    disabled={sessionPage === totalSessionPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-500 text-sm font-medium rounded-md text-gray-300 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Neste
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-400">
                      Viser <span className="font-medium">{sessionStartIndex + 1}</span> til{' '}
                      <span className="font-medium">{Math.min(sessionEndIndex, filteredSessions.length)}</span> av{' '}
                      <span className="font-medium">{filteredSessions.length}</span> treningsøkter
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setSessionPage(sessionPage - 1)}
                        disabled={sessionPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-500 bg-gray-600 text-sm font-medium text-gray-300 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Forrige</span>
                        ←
                      </button>
                      {Array.from({ length: totalSessionPages }, (_, i) => i + 1).map((page) => {
                        if (totalSessionPages <= 7 || page <= 3 || page >= totalSessionPages - 2 || Math.abs(page - sessionPage) <= 1) {
                          return (
                            <button
                              key={page}
                              onClick={() => setSessionPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === sessionPage
                                  ? 'z-10 bg-yellow-600 border-yellow-600 text-black'
                                  : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === 4 && sessionPage > 4) {
                          return <span key={page} className="px-4 py-2 text-gray-400">...</span>;
                        } else if (page === totalSessionPages - 3 && sessionPage < totalSessionPages - 3) {
                          return <span key={page} className="px-4 py-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                      <button
                        onClick={() => setSessionPage(sessionPage + 1)}
                        disabled={sessionPage === totalSessionPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-500 bg-gray-600 text-sm font-medium text-gray-300 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Neste</span>
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Administrer oppføringer Section - Simple delete controls like TrainingLog.js */}
          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-gray-300 font-medium text-sm">Administrer oppføringer</h3>
                {!bulkDeleteMode ? (
                  <button
                    onClick={() => setBulkDeleteMode(true)}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-gray-300 text-xs rounded transition-colors"
                  >
                    Velg for sletting
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400">
                      {selectedSessions.size} valgt
                    </span>
                    <button
                      onClick={selectAllSessions}
                      disabled={paginatedSessions.length === 0}
                      className="text-blue-400 hover:text-blue-300 text-xs disabled:text-gray-500"
                    >
                      Velg alle på siden
                    </button>
                    <button
                      onClick={clearAllSelections}
                      disabled={selectedSessions.size === 0}
                      className="text-gray-400 hover:text-gray-300 text-xs disabled:text-gray-500"
                    >
                      Tøm valg
                    </button>
                    {selectedSessions.size > 0 && (
                      <button
                        onClick={() => setShowBulkDeleteConfirm(true)}
                        className="px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded transition-colors"
                      >
                        Slett ({selectedSessions.size})
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setBulkDeleteMode(false);
                        setSelectedSessions(new Set());
                      }}
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded transition-colors"
                    >
                      Ferdig
                    </button>
                  </div>
                )}
              </div>
            </div>
            {bulkDeleteMode && (
              <p className="mt-1 text-xs text-gray-400">
                Kryss av oppføringer og klikk "Slett" for å fjerne flere samtidig.
              </p>
            )}
          </div>

          {/* Deletion Log Section - Expandable log of deleted sessions */}
          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 mt-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  if (!showDeletionLog) {
                    loadDeletedSessions();
                    loadMemberAuditLogs();
                  }
                  setShowDeletionLog(!showDeletionLog);
                }}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
              >
                <h3 className="font-medium text-sm">Vis logg over endringer</h3>
                <svg 
                  className={`h-4 w-4 transition-transform ${showDeletionLog ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showDeletionLog && (
              <div className="mt-3 border-t border-gray-600 pt-3">
                {loadingDeletedSessions ? (
                  <div className="text-center py-4">
                    <div className="text-gray-400 text-sm">Henter endringshistorikk...</div>
                  </div>
                ) : deletedSessions.length === 0 && memberAuditLogs.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="text-gray-400 text-sm">Ingen endringer funnet</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-400 mb-3">
                      Viser {deletedSessions.length + memberAuditLogs.length} oppføring{(deletedSessions.length + memberAuditLogs.length) !== 1 ? 'er' : ''}
                    </div>
                    
                    {/* Audit history list - both deleted and edited sessions */}
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {/* Member audit logs (creates, updates, role changes, approvals) */}
                      {memberAuditLogs.length > 0 && (
                        <>
                          <div className="text-xs text-yellow-400 font-medium mt-1 mb-1 uppercase tracking-wider">Medlemsendringer</div>
                          {memberAuditLogs.map((log, index) => (
                            <div 
                              key={`audit-${log.id || index}`} 
                              className="bg-gray-700/50 border border-gray-600 rounded p-3 text-sm"
                            >
                              <div className="flex items-center space-x-2 mb-1">
                                <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                  log.action === 'created' ? 'bg-green-500/20 text-green-400' :
                                  log.action === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                                  log.action === 'role_changed' ? 'bg-purple-500/20 text-purple-400' :
                                  log.action === 'password_reset' ? 'bg-orange-500/20 text-orange-400' :
                                  log.action === 'deleted' ? 'bg-red-500/20 text-red-400' :
                                  'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {log.action === 'created' ? 'OPPRETTET' :
                                   log.action === 'approved' ? 'GODKJENT' :
                                   log.action === 'role_changed' ? 'ROLLE' :
                                   log.action === 'password_reset' ? 'PASSORD' :
                                   log.action === 'deleted' ? 'SLETTET' :
                                   'ENDRET'}
                                </span>
                                <span className="text-white font-medium">{log.user_name || 'Ukjent'}</span>
                                {log.user_email && <span className="text-gray-500 text-xs">({log.user_email})</span>}
                              </div>
                              <div className="text-xs text-gray-300">{log.description}</div>
                              {log.changes && Object.keys(log.changes).length > 0 && (
                                <div className="mt-1 text-xs space-y-0.5">
                                  {Object.entries(log.changes).filter(([k]) => k !== 'updated_at' && k !== 'password' && k !== 'password_hash').map(([field, change]) => (
                                    <div key={field} className="text-gray-400">
                                      <span className="text-gray-500">{field}:</span>{' '}
                                      {change.old != null && change.old !== '***' && <><span className="line-through text-red-400/70">{String(change.old)}</span>{' → '}</>}
                                      <span className="text-green-400/70">{String(change.new)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 mt-1">
                                {log.changed_by_name && `Av ${log.changed_by_name} • `}
                                {log.timestamp && new Date(log.timestamp).toLocaleDateString('no-NO', {
                                  year: 'numeric', month: 'short', day: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Training session audit logs (edits and deletions) */}
                      {deletedSessions.length > 0 && (
                        <>
                          <div className="text-xs text-yellow-400 font-medium mt-3 mb-1 uppercase tracking-wider">Treningsøkt-endringer</div>
                        </>
                      )}
                      {deletedSessions.map((record, index) => (
                        <div 
                          key={`${record.type}-${record.session_id}-${index}`} 
                          className="bg-gray-700/50 border border-gray-600 rounded p-3 text-sm"
                        >
                          {record.type === 'deleted' ? (
                            // Deleted session
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded font-medium">
                                    SLETTET
                                  </span>
                                  <span className="text-white font-medium">
                                    {record.user_name || 'Ukjent medlem'}
                                  </span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-gray-300">
                                    {record.date}
                                  </span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-gray-300">
                                    {record.location_name || 'Ukjent lokasjon'}
                                  </span>
                                </div>
                                
                                <div className="text-xs text-red-300">
                                  Slettet av <strong>{record.deleted_by}</strong> den{' '}
                                  {new Date(record.deleted_at).toLocaleDateString('no-NO', {
                                    year: 'numeric',
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              </div>
                              
                              <button
                                onClick={() => {
                                  if (window.confirm(`Er du sikker på at du vil gjenopprette treningsøkten for ${record.user_name || 'ukjent medlem'}?`)) {
                                    restoreSession(record.session_id);
                                  }
                                }}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                title="Gjenopprett denne oppføringen"
                              >
                                Angre sletting
                              </button>
                            </div>
                          ) : (
                            // Edited session
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded font-medium">
                                  REDIGERT
                                </span>
                                <span className="text-white font-medium">
                                  {record.user_name || 'Ukjent medlem'}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-300">
                                  {record.date}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-300">
                                  {record.location_name || 'Ukjent lokasjon'}
                                </span>
                              </div>
                              
                              <div className="text-xs text-blue-300 mb-1">
                                Redigert av <strong>{record.edited_by}</strong> den{' '}
                                {new Date(record.edited_at).toLocaleDateString('no-NO', {
                                  year: 'numeric',
                                  month: 'long', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                              
                              {/* Show what changed */}
                              {record.changes && Object.keys(record.changes).length > 0 && (
                                <div className="mt-2 space-y-1 text-xs">
                                  {record.changes.date && (
                                    <div className="text-gray-300">
                                      <span className="text-gray-400">Dato:</span>{' '}
                                      <span className="line-through text-red-400">{record.changes.date.old}</span>
                                      {' → '}
                                      <span className="text-green-400">{record.changes.date.new}</span>
                                    </div>
                                  )}
                                  {record.changes.activity && (
                                    <div className="text-gray-300">
                                      <span className="text-gray-400">Aktivitet:</span>{' '}
                                      <span className="line-through text-red-400">{record.changes.activity.old}</span>
                                      {' → '}
                                      <span className="text-green-400">{record.changes.activity.new}</span>
                                    </div>
                                  )}
                                  {record.changes.location && (
                                    <div className="text-gray-300">
                                      <span className="text-gray-400">Bane:</span>{' '}
                                      <span className="line-through text-red-400">{record.changes.location.old}</span>
                                      {' → '}
                                      <span className="text-green-400">{record.changes.location.new}</span>
                                    </div>
                                  )}
                                  {record.changes.range_officer && (
                                    <div className="text-gray-300">
                                      <span className="text-gray-400">Skyteleder:</span>{' '}
                                      <span className="line-through text-red-400">{record.changes.range_officer.old}</span>
                                      {' → '}
                                      <span className="text-green-400">{record.changes.range_officer.new}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="space-y-4">
          {/* Header with send button */}
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-yellow-500" />
              Meldinger
            </h2>
            <button
              onClick={() => {
                setShowMessageForm(true);
                fetchMessageRecipients();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors font-medium"
              data-testid="new-message-btn"
            >
              <Plus size={18} />
              Ny melding
            </button>
          </div>

          {/* Messages List */}
          <div className="bg-gray-800 rounded-lg p-4">
            {loadingMessages ? (
              <div className="text-center py-8 text-gray-400">Laster meldinger...</div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">Ingen meldinger sendt ennå</p>
                <p className="text-sm text-gray-500 mt-1">Klikk "Ny melding" for å sende din første melding</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages
                  .slice((adminMessagesPage - 1) * ADMIN_MESSAGES_PER_PAGE, adminMessagesPage * ADMIN_MESSAGES_PER_PAGE)
                  .map((msg) => (
                  <div 
                    key={msg.id} 
                    className="bg-gray-700/50 rounded-lg p-4 border border-gray-600 overflow-hidden"
                    data-testid={`message-item-${msg.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white">{msg.subject}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <User size={14} />
                            {msg.sender_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {msg.recipient_count} mottaker{msg.recipient_count !== 1 ? 'e' : ''}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(msg.created_at).toLocaleDateString('nb-NO', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Videresend-knapp */}
                        <button
                          onClick={() => {
                            setMessageSubject(msg.subject);
                            setMessageContent(msg.content);
                            setSelectedRecipients(new Set());
                            setShowMessageForm(true);
                            fetchMessageRecipients();
                          }}
                          className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded transition-colors"
                          title="Videresend melding"
                          data-testid={`forward-message-${msg.id}`}
                        >
                          <Send size={18} />
                        </button>
                        {/* Kopier-knapp */}
                        <button
                          onClick={() => {
                            // Fjern HTML-tags og kopier kun teksten
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = msg.content;
                            const textContent = tempDiv.textContent || tempDiv.innerText || '';
                            navigator.clipboard.writeText(textContent).then(() => {
                              toast.success('Meldingsinnhold kopiert til utklippstavlen');
                            }).catch(() => {
                              toast.error('Kunne ikke kopiere til utklippstavlen');
                            });
                          }}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                          title="Kopier meldingsinnhold"
                          data-testid={`copy-message-${msg.id}`}
                        >
                          <Copy size={18} />
                        </button>
                        {/* Slett-knapp */}
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Slett melding"
                          data-testid={`delete-message-${msg.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    <div 
                      className="mt-3 text-gray-300 text-sm prose prose-invert max-w-none overflow-hidden"
                      style={{ overflowWrap: 'break-word', wordBreak: 'normal' }}
                      dangerouslySetInnerHTML={{ __html: msg.content }}
                    />
                  </div>
                ))}
                
                {/* Pagination controls for admin messages */}
                {messages.length > ADMIN_MESSAGES_PER_PAGE && (
                  <div className="flex items-center justify-center gap-4 pt-4 mt-4 border-t border-gray-600">
                    <button
                      onClick={() => setAdminMessagesPage(prev => Math.max(1, prev - 1))}
                      disabled={adminMessagesPage === 1}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        adminMessagesPage === 1
                          ? 'bg-gray-700/30 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                      data-testid="admin-messages-prev-page"
                    >
                      <ChevronLeft size={16} />
                      Forrige
                    </button>
                    
                    <span className="text-sm text-gray-400">
                      Side {adminMessagesPage} av {Math.ceil(messages.length / ADMIN_MESSAGES_PER_PAGE)}
                    </span>
                    
                    <button
                      onClick={() => setAdminMessagesPage(prev => Math.min(Math.ceil(messages.length / ADMIN_MESSAGES_PER_PAGE), prev + 1))}
                      disabled={adminMessagesPage >= Math.ceil(messages.length / ADMIN_MESSAGES_PER_PAGE)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        adminMessagesPage >= Math.ceil(messages.length / ADMIN_MESSAGES_PER_PAGE)
                          ? 'bg-gray-700/30 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                      data-testid="admin-messages-next-page"
                    >
                      Neste
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Message Audit Log */}
          <div className="mt-4">
            <button onClick={() => { setShowMessageAuditLog(!showMessageAuditLog); if (!showMessageAuditLog) fetchMessageAuditLog(); }}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-2"
              data-testid="messages-auditlog-toggle">
              {showMessageAuditLog ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <Clock size={16} />
              Vis endringslogg
            </button>
            {showMessageAuditLog && (
              <div className="bg-gray-800 rounded-lg max-h-72 overflow-y-auto">
                {messageAuditLogs.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">Ingen loggoppføringer ennå</div>
                ) : (
                  <div className="divide-y divide-gray-700">
                    {messageAuditLogs.map(log => (
                      <div key={log.id} className="p-3 text-sm" data-testid={`msg-audit-${log.id}`}>
                        <span className="text-white">{log.details}</span>
                        <div className="flex gap-3 text-xs text-gray-500 mt-1">
                          <span>{log.performed_by_name}</span>
                          <span>{new Date(log.timestamp).toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                          <span className={log.action === 'deleted' ? 'text-red-400' : 'text-green-400'}>
                            {log.action === 'created' ? 'Opprettet' : log.action === 'deleted' ? 'Slettet' : log.action}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* New Message Modal */}
          {showMessageForm && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
              <div className="bg-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-visible flex flex-col">
                {/* Modal Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-700">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-yellow-500" />
                    Ny melding
                  </h3>
                  <button
                    onClick={() => {
                      setShowMessageForm(false);
                      setMessageSubject('');
                      setMessageContent('');
                      setSelectedRecipients(new Set());
                    }}
                    className="text-gray-400 hover:text-white p-1"
                    data-testid="close-message-form"
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-visible p-4 space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Emne *</label>
                    <input
                      type="text"
                      value={messageSubject}
                      onChange={(e) => setMessageSubject(e.target.value)}
                      placeholder="Skriv emne for meldingen..."
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      data-testid="message-subject-input"
                    />
                  </div>

                  {/* Rich Text Content using ReactQuill */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Melding *</label>
                    <div className="bg-gray-700 rounded-lg overflow-visible message-quill-editor">
                      <ReactQuill
                        theme="snow"
                        value={messageContent}
                        onChange={setMessageContent}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Skriv meldingen din her..."
                        className="text-white"
                        data-testid="message-content-editor"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Tips: Bruk verktøylinjen for å formatere tekst, legge til lenker og lister</p>
                  </div>

                  {/* Scheduled Send Option */}
                  <div className="border border-gray-600 rounded-lg p-4 bg-gray-700/30">
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="checkbox"
                        id="scheduled-send"
                        checked={scheduledSend}
                        onChange={(e) => {
                          setScheduledSend(e.target.checked);
                          if (!e.target.checked) setScheduledDateTime('');
                        }}
                        className="w-4 h-4 rounded border-gray-500 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-800"
                        data-testid="scheduled-send-checkbox"
                      />
                      <label htmlFor="scheduled-send" className="flex items-center gap-2 text-gray-300 cursor-pointer">
                        <CalendarClock size={18} className="text-yellow-500" />
                        Planlegg sending til et senere tidspunkt
                      </label>
                    </div>
                    
                    {scheduledSend && (
                      <div className="ml-7">
                        <label className="block text-sm text-gray-400 mb-1">Velg dato og tidspunkt</label>
                        <input
                          type="datetime-local"
                          value={scheduledDateTime}
                          onChange={(e) => setScheduledDateTime(e.target.value)}
                          min={new Date().toISOString().slice(0, 16)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          data-testid="scheduled-datetime-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Meldingen vil bli synlig for mottakerne på valgt tidspunkt</p>
                      </div>
                    )}
                  </div>

                  {/* Recipients Selection */}
                  <div>
                    <div className="flex flex-col gap-2 mb-2">
                      <div className="flex justify-between items-center">
                        <label className="block text-sm font-medium text-gray-300">
                          Mottakere * ({selectedRecipients.size} valgt)
                        </label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={toggleAllRecipients}
                            className="text-xs px-3 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                            data-testid="select-all-recipients"
                          >
                            {selectedRecipients.size === messageRecipients.length ? 'Fjern alle' : 'Velg alle'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedRecipients(new Set())}
                            className="text-xs px-3 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                            data-testid="clear-recipients"
                          >
                            Fjern avhukinger
                          </button>
                        </div>
                      </div>
                      {/* Quick Filter Buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => {
                            const adminIds = messageRecipients
                              .filter(r => ['admin', 'org_admin', 'skyteleder'].includes(r.role))
                              .map(r => r.id);
                            setSelectedRecipients(new Set(adminIds));
                          }}
                          className="text-xs px-3 py-1.5 bg-purple-600/30 text-purple-300 rounded hover:bg-purple-600/50 border border-purple-500/30 flex items-center gap-1"
                          data-testid="filter-admins"
                        >
                          <Shield size={12} />
                          Admin/Skyteledere
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const memberIds = messageRecipients
                              .filter(r => r.role === 'member')
                              .map(r => r.id);
                            setSelectedRecipients(new Set(memberIds));
                          }}
                          className="text-xs px-3 py-1.5 bg-gray-600/30 text-gray-300 rounded hover:bg-gray-600/50 border border-gray-500/30 flex items-center gap-1"
                          data-testid="filter-members"
                        >
                          <Users size={12} />
                          Kun medlemmer
                        </button>
                      </div>
                    </div>
                    <div className="max-h-[250px] overflow-y-auto border border-gray-600 rounded-lg bg-gray-700/50">
                      {messageRecipients.length === 0 ? (
                        <div className="p-4 text-center text-gray-400">Laster mottakere...</div>
                      ) : (
                        messageRecipients.map((recipient) => (
                          <label
                            key={recipient.id}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-600/50 cursor-pointer border-b border-gray-600/50 last:border-0"
                            data-testid={`recipient-${recipient.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedRecipients.has(recipient.id)}
                              onChange={() => toggleRecipient(recipient.id)}
                              className="w-4 h-4 rounded border-gray-500 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-800"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-white text-sm truncate">{recipient.full_name}</div>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              recipient.role === 'admin' || recipient.role === 'org_admin' 
                                ? 'bg-purple-500/20 text-purple-300'
                                : recipient.role === 'skyteleder' 
                                  ? 'bg-blue-500/20 text-blue-300'
                                  : 'bg-gray-500/20 text-gray-300'
                            }`}>
                              {recipient.role === 'admin' || recipient.role === 'org_admin' ? 'Admin' 
                                : recipient.role === 'skyteleder' ? 'Skyteleder' 
                                : 'Medlem'}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end gap-3 p-4 border-t border-gray-700 bg-gray-800/50">
                  <button
                    onClick={() => {
                      setShowMessageForm(false);
                      setMessageSubject('');
                      setMessageContent('');
                      setSelectedRecipients(new Set());
                      setScheduledSend(false);
                      setScheduledDateTime('');
                    }}
                    className="px-4 py-2 text-gray-400 hover:text-white"
                    data-testid="cancel-message"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !messageSubject.trim() || !messageContent.trim() || selectedRecipients.size === 0 || (scheduledSend && !scheduledDateTime)}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        {scheduledSend ? 'Planlegger...' : 'Sender...'}
                      </>
                    ) : (
                      <>
                        {scheduledSend ? <CalendarClock size={18} /> : <Send size={18} />}
                        {scheduledSend ? 'Planlegg sending' : 'Send melding'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ammo Sales Tab */}
      {activeTab === 'ammo' && (
        <div className="space-y-6">
          {/* Ammunisjonslager i full bredde øverst */}
          <AmmoStockFullWidth defaultExpanded={true} />

          {/* Ammo-salg oversikt med expand/collapse */}
          <div className="bg-gray-800 rounded-lg p-6">
            <AmmoSalesHeader />
            <div className="mt-6">
              <AmmoSalesList 
                sessions={sessions}
                onOpenAmmoModal={(session) => {
                  setSelectedSessionForAmmo(session);
                  setShowAmmoSaleModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'locations' && (
        <div className="bg-gray-800 rounded-lg p-3 md:p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-white">QR-kode Administrasjon</h2>
              <p className="text-gray-400 text-sm mt-1">Administrer QR-koder for skytebaner og treningslokasjoner</p>
            </div>
            <button 
              onClick={() => setShowLocationForm(true)}
              className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
            >
              <Plus className="h-4 w-4 inline mr-2" />
              Legg til skytebane
            </button>
          </div>

          {/* Moved sales banner to after locations - see below */}

          {/* Kort-basert layout som i det opprinnelige designet */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location) => {
              // Bestem bransje informasjon og farge
              const discipline = location.discipline || 'nsf';
              const disciplineInfo = getDisciplineInfo(discipline);
              
              return (
                <div key={location.id} className="bg-gray-700 rounded-lg p-6 relative">
                  {/* Action buttons øverst til høyre */}
                  <div className="absolute top-4 right-4 flex space-x-1">
                    <button
                      onClick={() => {
                        setSelectedLocation({
                          ...location,
                          qr_code_data: location.qr_code_data || location.id
                        });
                        setShowPosterGenerator(true);
                      }}
                      className="p-2 text-green-400 hover:text-green-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                      title="Se QR-kode"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        // Show warning modal for existing QR-codes
                        setQRWarningLocation(location);
                        setShowQRWarningModal(true);
                      }}
                      className="p-2 text-blue-400 hover:text-blue-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                      title="Rediger skytebane"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Er du sikker på at du vil slette skytebanen "${location.name}"?`)) {
                          deleteLocation(location.id);
                        }
                      }}
                      className="p-2 text-red-400 hover:text-red-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                      title="Slett skytebane"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* QR Code ikon øverst til venstre */}
                  <div className="flex items-center mb-4">
                    <div className="bg-yellow-500/20 p-2 rounded">
                      <QrCode className="h-6 w-6 text-yellow-400" />
                    </div>
                  </div>

                  {/* Skytebane navn */}
                  <h3 className="text-lg font-semibold text-white mb-2 pr-16">
                    {location.name}
                  </h3>

                  {/* QR-kode ID */}
                  <div className="mb-3">
                    <p className="text-gray-400 text-sm mb-1">QR-kode:</p>
                    <p className="text-white font-mono text-sm">
                      {location.qr_code_data || location.id}
                    </p>
                  </div>

                  {/* Bransje badge */}
                  <div className="mb-3">
                    <span 
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-bold text-white"
                      style={{ 
                        backgroundColor: `${disciplineInfo.color}55`, 
                        border: `1.5px solid ${disciplineInfo.color}`,
                        textShadow: '0 1px 2px rgba(0,0,0,0.6)'
                      }}
                      data-testid={`discipline-badge-${disciplineInfo.name.toLowerCase()}`}
                    >
                      <Target className="h-3 w-3 mr-1 text-white" />
                      {disciplineInfo.name}
                    </span>
                  </div>

                  {/* Støttede aktivitetstyper */}
                  {location.supported_activity_types && location.supported_activity_types.length > 0 && (
                    <div className="mb-4">
                      <p className="text-gray-400 text-xs mb-2">Støttede aktivitetstyper:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {location.supported_activity_types.map((activityType, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                          >
                            {activityType}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* QR-kode tekst seksjon med generator knapp */}
                  <div className="bg-gray-800 rounded p-3 border border-gray-600">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-gray-400 text-xs">QR-kode tekst:</p>
                      <button
                        onClick={() => {
                          setQRGeneratorLocation(location);
                          setShowQRGenerator(true);
                        }}
                        className="text-yellow-400 hover:text-yellow-300 flex items-center text-xs"
                        title="Åpne QR-plakat generator"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        QR-plakat generator
                      </button>
                    </div>
                    <p className="text-white font-mono text-xs break-all">
                      {location.qr_code_data || location.id}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {locations.length === 0 && (
            <div className="text-center text-gray-400 py-12">
              <MapPin className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold text-white mb-2">Ingen skytebaner funnet</h3>
              <p className="mb-4">Opprett en skytebane for å generere QR-koder.</p>
              <button 
                onClick={() => setShowLocationForm(true)}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
              >
                <Plus className="h-4 w-4 inline mr-2" />
                Legg til skytebane
              </button>
            </div>
          )}

          {/* Tips Section */}
          <div className="mt-8 bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="text-2xl">💡</div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">Tips:</h4>
                <p className="text-gray-300 text-sm mb-3">
                  Disse kodene kan brukes til å generere QR-koder for hver bane. Bruk QR-kode teksten over i en QR-kodegenerator som f.eks. 
                  <a href="https://qr-generator.org" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline ml-1">
                    QR Code Generator
                  </a>.
                </p>
                
                <div className="space-y-2 text-sm text-gray-300">
                  <h5 className="font-semibold text-white">Slik bruker medlemmer QR-kodene:</h5>
                  <ol className="list-decimal list-inside space-y-1 ml-4">
                    <li>Medlemmet åpner QR-skanneren i appen</li>
                    <li>Skanner QR-koden på skytebanen</li>
                    <li>Treningsøkten registreres automatisk med riktig disiplin</li>
                    <li>Admin godkjenner økten</li>
                  </ol>
                </div>

                <div className="mt-4 space-y-2 text-sm text-gray-300">
                  <h5 className="font-semibold text-white">Tips for QR-kode generering:</h5>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>QR-koder starter med disiplin (nsf-, dfs-, dssn-)</li>
                    <li>Bruk beskrivende navn som "innendors-25m" eller "feltbane-klasse2"</li>
                    <li>Aktiver kun de disiplinene som faktisk kan utføres på banen</li>
                    <li>Print ut QR-kodene og fest dem på skytebanen</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Sales Banner for Physical Posters - Only for Admins - Moved below locations */}
          <div className="mt-8 bg-gradient-to-r from-blue-900/80 to-purple-900/80 border border-blue-600/50 rounded-xl p-6 relative overflow-hidden">
            {/* Background pattern/texture */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10"></div>
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between">
              {/* Left side - Text content */}
              <div className="flex-1 mb-4 md:mb-0 md:pr-8">
                <h3 className="text-xl font-bold text-white mb-3">Profesjonelle QR-kodeskilt</h3>
                
                <p className="text-blue-100 mb-4 text-sm leading-relaxed">
                  Få produsert fysiske plakater med din unike QR-kode som medlemmene kan scanne. 
                  Perfect for vegger, skytebaner og treningsområder. Profesjonell utskrift på holdbart materiale.
                </p>
                
                <div className="flex flex-wrap gap-4 text-xs text-blue-200">
                  <div className="flex items-center">
                    <span className="h-2 w-2 bg-blue-400 rounded-full mr-2"></span>
                    Holdbart materiale
                  </div>
                  <div className="flex items-center">
                    <span className="h-2 w-2 bg-blue-400 rounded-full mr-2"></span>
                    Flere størrelser
                  </div>
                  <div className="flex items-center">
                    <span className="h-2 w-2 bg-blue-400 rounded-full mr-2"></span>
                    Rask levering
                  </div>
                </div>
              </div>
              
              {/* Right side - Image and CTA */}
              <div className="flex-shrink-0 text-center">
                <div className="mb-4">
                  <img 
                    src="/images/bestill-plakat-grafikk.png" 
                    alt="QR-kode plakat eksempel"
                    className="h-24 w-auto mx-auto opacity-90"
                  />
                </div>
                
                <a
                  href="https://aktivlogg.no/bestill-skilt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <span className="mr-2">🎯</span>
                  Bestill skilt
                  <span className="ml-2">→</span>
                </a>
                
                <p className="text-blue-300 text-xs mt-2">Åpnes i nytt vindu</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-8">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Organisasjonsinnstillinger</h2>
              <p className="text-gray-400">Konfigurer organisasjonsinformasjon og innstillinger</p>
            </div>
            
            {/* Save Button */}
            <button 
              onClick={saveSettings}
              disabled={settingsLoading}
              className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
            >
              {settingsLoading ? 'Lagrer...' : 'LAGRE'}
            </button>
          </div>

          {/* Basic Information */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-building2 w-5 h-5">
                <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                <path d="M10 6h4"></path>
                <path d="M10 10h4"></path>
                <path d="M10 14h4"></path>
                <path d="M10 18h4"></path>
              </svg>
              Grunnleggende informasjon
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Skytterlag/skytterklubb-navn *</label>
                <div className="space-y-2">
                  {(settingsData.memberOrganizations || [{clubName: '', organization: 'NSF - Norges Sportsskytterforbund'}]).map((org, index) => (
                    <div key={index} className="flex gap-2">
                      <input 
                        type="text"
                        className="flex-1 bg-gray-700 rounded-md px-3 py-2 text-white"
                        placeholder="F.eks. Svolvær Pistolklubb"
                        value={org.clubName}
                        onChange={(e) => {
                          const newOrgs = [...settingsData.memberOrganizations];
                          newOrgs[index].clubName = e.target.value;
                          setSettingsData({...settingsData, memberOrganizations: newOrgs});
                        }}
                      />
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newOrgs = settingsData.memberOrganizations.filter((_, i) => i !== index);
                            setSettingsData({...settingsData, memberOrganizations: newOrgs});
                          }}
                          className="px-3 py-2 bg-red-500/20 text-red-400 rounded-md hover:bg-red-500/30 transition-colors"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Organisasjons-navn *</label>
                <div className="space-y-2">
                  {(settingsData.memberOrganizations || [{clubName: '', organization: 'NSF - Norges Sportsskytterforbund'}]).map((org, index) => (
                    <div key={index} className="flex gap-2">
                      <select 
                        className="flex-1 bg-gray-700 rounded-md px-3 py-2 text-white"
                        value={org.organization}
                        onChange={(e) => {
                          const newOrgs = [...settingsData.memberOrganizations];
                          newOrgs[index].organization = e.target.value;
                          setSettingsData({...settingsData, memberOrganizations: newOrgs});
                        }}
                      >
                        <option value="NSF - Norges Sportsskytterforbund">NSF - Norges Sportsskytterforbund</option>
                        <option value="DFS - Det Frivillige Skyttervesen">DFS - Det Frivillige Skyttervesen</option>
                        <option value="DSSN - Dynamisk Sportsskyting Norge">DSSN - Dynamisk Sportsskyting Norge</option>
                        <option value="SWS - Scandinavian Western Shooters">SWS - Scandinavian Western Shooters</option>
                        <option value="NSU - Norsk Svartkruttunion">NSU - Norsk Svartkruttunion</option>
                        <option value="NROF - Norske Reserveoffiserers Forbund">NROF - Norske Reserveoffiserers Forbund</option>
                        <option value="NMSF - Norges Metallsilhuett Forbund">NMSF - Norges Metallsilhuett Forbund</option>
                        <option value="NJFF - Norges Jeger- og Fiskerforbund">NJFF - Norges Jeger- og Fiskerforbund</option>
                        <option value="NBIF - Norges bedriftsidrettsforbund">NBIF - Norges bedriftsidrettsforbund</option>
                        <option value="NBSF - Norges Benkeskytterforbund">NBSF - Norges Benkeskytterforbund</option>
                        <option value="NSSF - Norges skiskytterforbund">NSSF - Norges skiskytterforbund</option>
                      </select>
                      {index === settingsData.memberOrganizations.length - 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newOrgs = [...settingsData.memberOrganizations, {clubName: '', organization: 'NSF - Norges Sportsskytterforbund'}];
                            setSettingsData({...settingsData, memberOrganizations: newOrgs});
                          }}
                          className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-400 transition-colors font-bold text-xl"
                          title="Legg til organisasjon"
                        >
                          +
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Abonnementsplan</label>
                <select 
                  className="w-full bg-gray-700 rounded-md px-3 py-2 text-white"
                  value={settingsData.subscriptionPlan}
                  onChange={(e) => setSettingsData({...settingsData, subscriptionPlan: e.target.value})}
                >
                  <option value="start">Start (Opp til 100 medlemmer)</option>
                  <option value="ubegrenset">Ubegrenset (Ubegr. antall medlemmer)</option>
                </select>
                <div className="mt-3 p-3 bg-gray-700/50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-400">Medlemsbruk</span>
                    <span className="text-xs text-gray-300">
                      {stats.totalMembers} / {settingsData.subscriptionPlan === 'ubegrenset' ? '∞' : '100'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all bg-green-500" 
                      style={{
                        width: settingsData.subscriptionPlan === 'ubegrenset' 
                          ? '100%' 
                          : `${Math.min(100, (stats.totalMembers / 100) * 100)}%`
                      }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Velg abonnementsplan som passer for organisasjonen</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Organisasjonsnummer</label>
                <input 
                  type="text" 
                  className="w-full bg-gray-700 rounded-md px-3 py-2 text-white" 
                  placeholder="Skriv inn organisasjonsnummer"
                  value={settingsData.organizationNumber}
                  onChange={(e) => setSettingsData({...settingsData, organizationNumber: e.target.value})}
                />
                <p className="text-xs text-gray-400 mt-1">Organisasjonsnummer for klubben</p>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Beskrivelse</label>
                <textarea 
                  className="w-full bg-gray-700 rounded-md px-3 py-2 h-24 text-white" 
                  placeholder="Kort beskrivelse av organisasjonen"
                  value={settingsData.description}
                  onChange={(e) => setSettingsData({...settingsData, description: e.target.value})}
                ></textarea>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mail w-5 h-5">
                <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
              </svg>
              Kontaktinformasjon
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">E-post</label>
                <input 
                  type="email" 
                  className="w-full bg-gray-700 rounded-md px-3 py-2 text-white" 
                  placeholder="post@klubb.no" 
                  value={settingsData.email}
                  onChange={(e) => setSettingsData({...settingsData, email: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nettside</label>
                <input 
                  type="url" 
                  className="w-full bg-gray-700 rounded-md px-3 py-2 text-white" 
                  placeholder="https://klubb.no" 
                  value={settingsData.website}
                  onChange={(e) => setSettingsData({...settingsData, website: e.target.value})}
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Fakturaadresse</label>
                <textarea 
                  className="w-full bg-gray-700 rounded-md px-3 py-2 h-20 text-white" 
                  placeholder="Klubbgata 1&#10;1234 Klubbby"
                  value={settingsData.address}
                  onChange={(e) => setSettingsData({...settingsData, address: e.target.value})}
                ></textarea>
              </div>
            </div>
          </div>

          {/* Member Registration Link */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user-plus w-5 h-5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <line x1="19" x2="19" y1="8" y2="14"></line>
                <line x1="22" x2="16" y1="11" y2="11"></line>
              </svg>
              Medlemsregistrering
            </h3>
            
            <p className="text-gray-400 text-sm mb-4">
              Del denne linken med nye medlemmer for å la dem registrere seg til din klubb
            </p>
            
            <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
              <label className="block text-sm font-medium text-gray-300 mb-2">Registreringslink</label>
              {/* URL field - full width on mobile */}
              <div className="bg-gray-900 rounded-md px-4 py-3 text-yellow-500 font-mono text-sm break-all mb-3">
                {user?.organization?.slug 
                  ? `${window.location.origin}/register/${user.organization.slug}`
                  : 'Laster...'
                }
              </div>
              {/* Buttons on separate line */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    if (!user?.organization?.slug) {
                      toast.error('Kunne ikke kopiere - organisasjonsdata mangler');
                      return;
                    }
                    const registrationLink = `${window.location.origin}/register/${user.organization.slug}`;
                    navigator.clipboard.writeText(registrationLink).then(() => {
                      toast.success('Registreringslink kopiert til utklippstavle!');
                    }).catch(err => {
                      console.error('Failed to copy:', err);
                      toast.error('Kunne ikke kopiere link');
                    });
                  }}
                  disabled={!user?.organization?.slug}
                  className="bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 p-3 rounded-md transition-colors flex-shrink-0"
                  title="Kopier link"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                  </svg>
                </button>
                <button
                  onClick={async () => {
                    if (!user?.organization?.slug) {
                      toast.error('Kunne ikke generere PDF - organisasjonsdata mangler');
                      return;
                    }
                    
                    try {
                      const QRCode = (await import('qrcode')).default;
                      const jsPDF = (await import('jspdf')).default;
                      
                      const registrationLink = `${window.location.origin}/register/${user.organization.slug}`;
                      
                      // Generate QR code as data URL (larger for better quality)
                      const qrDataUrl = await QRCode.toDataURL(registrationLink, {
                        width: 600,
                        margin: 0,
                        color: {
                          dark: '#000000',
                          light: '#FFFFFF'
                        }
                      });
                      
                      // Create PDF
                      const pdf = new jsPDF({
                        orientation: 'portrait',
                        unit: 'mm',
                        format: 'a4'
                      });
                      
                      const pageWidth = 210;
                      const pageHeight = 297;
                      
                      // Add header text (centered, at top)
                      pdf.setFont('helvetica', 'bold');
                      pdf.setFontSize(20);
                      pdf.setTextColor(0, 0, 0);
                      const headerText = 'Skann QR-koden for å registrere deg første gangen.';
                      const headerLines = pdf.splitTextToSize(headerText, 160);
                      let yPosition = 35;
                      headerLines.forEach(line => {
                        const headerWidth = pdf.getTextWidth(line);
                        pdf.text(line, (pageWidth - headerWidth) / 2, yPosition);
                        yPosition += 10;
                      });
                      
                      // Add QR code without border (improved mobile scanning)
                      const qrSize = 120; // mm (larger)
                      const qrX = (pageWidth - qrSize) / 2;
                      const qrY = yPosition + 15;
                      
                      // Add QR code directly (no border for better mobile scanning)
                      pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
                      
                      // Add black box with text below QR
                      const boxY = qrY + qrSize + 10;
                      const boxWidth = qrSize;
                      const boxHeight = 25;
                      const boxX = qrX;
                      
                      // Draw black rounded rectangle
                      pdf.setFillColor(0, 0, 0);
                      pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 3, 3, 'F');
                      
                      // Add text inside box - "Først registrer" (yellow) + "deg her" (white)
                      pdf.setFont('helvetica', 'bold');
                      pdf.setFontSize(22);
                      
                      // Calculate center position for combined text
                      const yellowText = 'Først registrer ';
                      const whiteText = 'deg her';
                      const yellowWidth = pdf.getTextWidth(yellowText);
                      const whiteWidth = pdf.getTextWidth(whiteText);
                      const totalWidth = yellowWidth + whiteWidth;
                      const startX = (pageWidth - totalWidth) / 2;
                      const textY = boxY + (boxHeight / 2) + 3;
                      
                      // Yellow text
                      pdf.setTextColor(255, 215, 0); // Gold/Yellow
                      pdf.text(yellowText, startX, textY);
                      
                      // White text
                      pdf.setTextColor(255, 255, 255);
                      pdf.text(whiteText, startX + yellowWidth, textY);
                      
                      // Add URL at bottom (centered)
                      pdf.setFont('helvetica', 'normal');
                      pdf.setFontSize(11);
                      pdf.setTextColor(60, 60, 60);
                      const urlWidth = pdf.getTextWidth(registrationLink);
                      pdf.text(registrationLink, (pageWidth - urlWidth) / 2, pageHeight - 20);
                      
                      // Save PDF
                      const orgName = user.organization.name.replace(/[^a-zA-Z0-9]/g, '_');
                      pdf.save(`${orgName}_Registrering_QR.pdf`);
                      
                      toast.success('PDF lastet ned!');
                    } catch (error) {
                      console.error('Failed to generate PDF:', error);
                      toast.error('Kunne ikke generere PDF');
                    }
                  }}
                  disabled={!user?.organization?.slug}
                  className="bg-blue-500 hover:bg-blue-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-3 rounded-md transition-colors flex items-center gap-2"
                  title="Last ned PDF med QR-kode"
                >
                  <Download className="h-5 w-5" />
                  <span className="font-medium">Last ned QR-kode PDF</span>
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Medlemmer som bruker denne linken blir automatisk registrert til <strong className="text-white">{user?.organization?.name || 'din klubb'}</strong>
              </p>
            </div>
            
            <div className="mt-4 bg-blue-600/20 border border-blue-600/50 rounded-md p-3">
              <h4 className="text-blue-400 font-medium text-sm mb-2 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
                Slik bruker du registreringslinken:
              </h4>
              <ul className="text-blue-300 text-xs space-y-1">
                <li>• Del linken på e-post, SMS eller sosiale medier</li>
                <li>• Legg den på klubbens hjemmeside eller Facebook-side</li>
                <li>• Print QR-kode og heng opp i klubblokalet</li>
                <li>• Nye medlemmer godkjennes av deg før de får tilgang</li>
              </ul>
            </div>
          </div>


          {/* Visual Logo & Colors */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-palette w-5 h-5">
                <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"></circle>
                <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"></circle>
                <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"></circle>
                <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"></circle>
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"></path>
              </svg>
              Visuell logo og farger
            </h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Logo for PDF-eksport</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-gray-700 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center">
                    {settingsData.logoUrl ? (
                      <img 
                        src={settingsData.logoUrl} 
                        alt="Logo" 
                        className="w-full h-full object-contain rounded-lg"
                      />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-image w-6 h-6 text-gray-500">
                        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                        <circle cx="9" cy="9" r="2"></circle>
                        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-2">
                      <input 
                        type="file" 
                        className="hidden" 
                        id="logo-upload" 
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && handleFileUpload('logo', e.target.files[0])}
                      />
                      <label 
                        htmlFor="logo-upload" 
                        className={`cursor-pointer inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium rounded-md transition-colors ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload w-4 h-4 mr-2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7,10 12,15 17,10"></polyline>
                          <line x1="12" x2="12" y1="15" y2="3"></line>
                        </svg>
                        {uploadingLogo ? 'Laster opp...' : 'Last opp logo'}
                      </label>
                      
                      {settingsData.logoUrl && (
                        <button 
                          onClick={() => deleteFile('logo')}
                          className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash2 w-4 h-4 mr-2">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" x2="10" y1="11" y2="17"></line>
                            <line x1="14" x2="14" y1="11" y2="17"></line>
                          </svg>
                          Slett
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG eller SVG. Maks 2MB.</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Primær farge</label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="color" 
                      className="w-12 h-10 bg-gray-700 rounded-md border border-gray-600" 
                      value={settingsData.primaryColor}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                    />
                    <input 
                      type="text" 
                      className="flex-1 bg-gray-700 rounded-md px-3 py-2 font-mono text-sm text-white" 
                      placeholder="#FFD700" 
                      value={settingsData.primaryColor}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                    />
                    <button 
                      type="button" 
                      onClick={() => resetColorToDefault('primary')}
                      className="p-2 text-gray-400 hover:text-white transition-colors" 
                      title="Tilbakestill til standard"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-rotate-ccw w-4 h-4">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                        <path d="M3 3v5h5"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Sekundær farge</label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="color" 
                      className="w-12 h-10 bg-gray-700 rounded-md border border-gray-600" 
                      value={settingsData.secondaryColor}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                    />
                    <input 
                      type="text" 
                      className="flex-1 bg-gray-700 rounded-md px-3 py-2 font-mono text-sm text-white" 
                      placeholder="#1F2937" 
                      value={settingsData.secondaryColor}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                    />
                    <button 
                      type="button" 
                      onClick={() => resetColorToDefault('secondary')}
                      className="p-2 text-gray-400 hover:text-white transition-colors" 
                      title="Tilbakestill til standard"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-rotate-ccw w-4 h-4">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                        <path d="M3 3v5h5"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Bakgrunnsfarge</label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="color" 
                      className="w-12 h-10 bg-gray-700 rounded-md border border-gray-600" 
                      value={settingsData.backgroundColor}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                    />
                    <input 
                      type="text" 
                      className="flex-1 bg-gray-700 rounded-md px-3 py-2 font-mono text-sm text-white" 
                      placeholder="#111827" 
                      value={settingsData.backgroundColor}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                    />
                    <button 
                      type="button" 
                      onClick={() => resetColorToDefault('background')}
                      className="p-2 text-gray-400 hover:text-white transition-colors" 
                      title="Tilbakestill til standard"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-rotate-ccw w-4 h-4">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                        <path d="M3 3v5h5"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sales Banner */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-megaphone w-5 h-5">
                <path d="m3 11 18-5v12L3 14v-3z"></path>
                <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
              </svg>
              Salgsbannere på forsiden
            </h3>
            
            <div className="space-y-6">
              <p className="text-gray-400 text-sm">
                Last opp bannere som vises midt på forsiden når medlemmer logger inn. 
                Ved flere bannere roteres de automatisk. Legg til URL for å gjøre bannere klikkbare.
              </p>
              
              {/* Banner rotation settings */}
              <div className="p-4 bg-gray-700 rounded-lg">
                <h4 className="font-medium text-white mb-3">Banner-innstillinger</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Roteringstid mellom bannere (sekunder)
                    </label>
                    <input 
                      type="number"
                      min="3"
                      max="60"
                      className="w-32 bg-gray-600 rounded-md px-3 py-2 text-sm text-white"
                      value={settingsData.bannerRotationSeconds}
                      disabled={settingsData.bannerUrls.length <= 1}
                      onChange={(e) => setSettingsData({
                        ...settingsData, 
                        bannerRotationSeconds: parseInt(e.target.value) || 15
                      })}
                    />
                    {settingsData.bannerUrls.length <= 1 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Deaktivert - kun ett banner lastet opp
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Existing banners with drag-and-drop reordering */}
              {settingsData.bannerUrls.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-medium text-white">Aktive bannere ({settingsData.bannerUrls.length})</h4>
                  {settingsData.bannerUrls.length > 1 && (
                    <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-3">
                      <p className="text-blue-300 text-sm flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 3h6v6h-6z"></path>
                          <path d="M9 15h6v6h-6z"></path>
                        </svg>
                        Dra og slipp bannere for å endre rekkefølgen
                      </p>
                    </div>
                  )}
                  {settingsData.bannerUrls.map((bannerUrl, index) => (
                    <div 
                      key={index}
                      draggable="true"
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', index.toString());
                        e.currentTarget.style.opacity = '0.5';
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        e.currentTarget.classList.add('border-yellow-500', 'border-2');
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove('border-yellow-500', 'border-2');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-yellow-500', 'border-2');
                        
                        const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                        const droppedIndex = index;
                        
                        if (draggedIndex === droppedIndex) return;
                        
                        // Reorder all related arrays
                        const newUrls = [...settingsData.bannerUrls];
                        const newLinks = [...settingsData.bannerLinks];
                        const newExpiry = [...settingsData.bannerExpiryDates];
                        const newVisible = [...settingsData.bannerVisible];
                        
                        // Remove from old position
                        const [movedUrl] = newUrls.splice(draggedIndex, 1);
                        const [movedLink] = newLinks.splice(draggedIndex, 1);
                        const [movedExpiry] = newExpiry.splice(draggedIndex, 1);
                        const [movedVisible] = newVisible.splice(draggedIndex, 1);
                        
                        // Insert at new position
                        newUrls.splice(droppedIndex, 0, movedUrl);
                        newLinks.splice(droppedIndex, 0, movedLink);
                        newExpiry.splice(droppedIndex, 0, movedExpiry);
                        newVisible.splice(droppedIndex, 0, movedVisible);
                        
                        setSettingsData({
                          ...settingsData,
                          bannerUrls: newUrls,
                          bannerLinks: newLinks,
                          bannerExpiryDates: newExpiry,
                          bannerVisible: newVisible
                        });
                        
                        toast.info('Banner-rekkefølge endret. Husk å lagre endringer!');
                      }}
                      className="flex items-center gap-4 p-4 bg-gray-700 rounded-lg cursor-move hover:bg-gray-600 transition-all duration-200"
                    >
                      {/* Drag handle */}
                      <div className="flex-shrink-0 text-gray-400 hover:text-yellow-500 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="9" cy="5" r="1"></circle>
                          <circle cx="9" cy="12" r="1"></circle>
                          <circle cx="9" cy="19" r="1"></circle>
                          <circle cx="15" cy="5" r="1"></circle>
                          <circle cx="15" cy="12" r="1"></circle>
                          <circle cx="15" cy="19" r="1"></circle>
                        </svg>
                      </div>
                      
                      <div className="w-32 h-16 bg-gray-600 rounded-lg border flex items-center justify-center overflow-hidden">
                        <img 
                          src={bannerUrl} 
                          alt={`Banner ${index + 1}`} 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1 space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Banner URL (valgfri)</label>
                          <input 
                            type="url"
                            className="w-full bg-gray-600 rounded-md px-3 py-2 text-sm text-white" 
                            placeholder="https://eksempel.no"
                            value={settingsData.bannerLinks[index] || ''}
                            onChange={(e) => {
                              const newLinks = [...settingsData.bannerLinks];
                              newLinks[index] = e.target.value;
                              setSettingsData({...settingsData, bannerLinks: newLinks});
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Sluttdato (valgfri)</label>
                          <input 
                            type="date"
                            className="w-full bg-gray-600 rounded-md px-3 py-2 text-sm text-white" 
                            value={settingsData.bannerExpiryDates[index] || ''}
                            onChange={(e) => {
                              const newDates = [...settingsData.bannerExpiryDates];
                              newDates[index] = e.target.value;
                              setSettingsData({...settingsData, bannerExpiryDates: newDates});
                            }}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-400">
                            Banner #{index + 1} {settingsData.bannerUrls.length > 1 && `(roterer hver ${settingsData.bannerRotationSeconds}. sek)`}
                          </div>
                          <div className="text-xs text-gray-400">
                            {settingsData.bannerExpiryDates[index] && (
                              <span>Utløper: {new Date(settingsData.bannerExpiryDates[index]).toLocaleDateString('no-NO')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button 
                          onClick={async (e) => {
                            e.stopPropagation();
                            const newVisible = [...settingsData.bannerVisible];
                            newVisible[index] = !newVisible[index];
                            setSettingsData({...settingsData, bannerVisible: newVisible});
                            
                            // Auto-save visibility change to server (update both legacy and settings locations)
                            try {
                              const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
                              
                              // Update banner_visible (legacy field used by Home.js)
                              await axios.put(`${API_BASE}/api/organizations/${user?.organization_id}`, {
                                banner_visible: newVisible
                              }, { headers });
                              
                              // Also update settings.bannerVisible using dot notation endpoint
                              await axios.put(`${API_BASE}/api/organizations/${user?.organization_id}/settings-field`, {
                                field: 'bannerVisible',
                                value: newVisible
                              }, { headers });
                              
                              await refreshUser();
                              toast.success(newVisible[index] ? 'Banner er nå synlig' : 'Banner er nå skjult');
                            } catch (err) {
                              console.error('Failed to save banner visibility:', err);
                              toast.error('Kunne ikke lagre synlighetsendring');
                            }
                          }}
                          className={`px-3 py-2 text-white text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                            settingsData.bannerVisible[index] !== false 
                              ? 'bg-green-600 hover:bg-green-500' 
                              : 'bg-gray-600 hover:bg-gray-500'
                          }`}
                          title={settingsData.bannerVisible[index] !== false ? 'Skjul banner' : 'Vis banner'}
                        >
                          {settingsData.bannerVisible[index] !== false ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                        
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFile('banner', index);
                          }}
                          className="px-3 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-md transition-colors"
                        >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash2">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" x2="10" y1="11" y2="17"></line>
                          <line x1="14" x2="14" y1="11" y2="17"></line>
                        </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new banner */}
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-32 h-16 bg-gray-700 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-image w-6 h-6 text-gray-500">
                      <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                      <circle cx="9" cy="9" r="2"></circle>
                      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-2">
                      <input 
                        type="file" 
                        className="hidden" 
                        id="banner-upload" 
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && handleFileUpload('banner', e.target.files[0])}
                      />
                      <label 
                        htmlFor="banner-upload" 
                        className={`cursor-pointer inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium rounded-md transition-colors ${uploadingBanner ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload w-4 h-4 mr-2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7,10 12,15 17,10"></polyline>
                          <line x1="12" x2="12" y1="15" y2="3"></line>
                        </svg>
                        {uploadingBanner ? 'Laster opp...' : 'Last opp nytt banner'}
                      </label>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Anbefalt størrelse: 1500x250px. PNG eller JPG. Maks 10MB.</p>
                    <p className="text-xs text-gray-500 mt-1">Tips: Ved flere bannere roteres de automatisk hvert 15. sekund</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shooting Branches and Activities */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-target w-5 h-5">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="6"></circle>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>
              Skyttergrener og aktiviteter
            </h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-4 text-white">Aktive skyttergrener</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.nsf}
                      onChange={(e) => handleShootingBranchChange('nsf', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NSF - Norges Sportsskytterforbund</span>
                      <p className="text-sm text-gray-400">Tradisjonell sportsskyting, pistol og rifle</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.dfs}
                      onChange={(e) => handleShootingBranchChange('dfs', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">DFS - Det Frivillige Skyttervesen (kun rifle)</span>
                      <p className="text-sm text-gray-400">Frivillig forsvarsskyting</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.dssn}
                      onChange={(e) => handleShootingBranchChange('dssn', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">DSSN - Dynamisk Sportsskyting Norge</span>
                      <p className="text-sm text-gray-400">Dynamisk skyting og konkurranser</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.sws}
                      onChange={(e) => handleShootingBranchChange('sws', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">SWS - Scandinavian Western Shooters</span>
                      <p className="text-sm text-gray-400">Western skyting</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.nsu}
                      onChange={(e) => handleShootingBranchChange('nsu', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NSU - Norsk Svartkruttunion</span>
                      <p className="text-sm text-gray-400">Svartkruttskyting</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.nrof}
                      onChange={(e) => handleShootingBranchChange('nrof', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NROF - Norske Reserveoffiserers Forbund</span>
                      <p className="text-sm text-gray-400">Reserveoffisersskyting</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.nmsf}
                      onChange={(e) => handleShootingBranchChange('nmsf', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NMSF - Norges Metallsilhuett Forbund</span>
                      <p className="text-sm text-gray-400">Metallsilhuett-skyting</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.njff}
                      onChange={(e) => handleShootingBranchChange('njff', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NJFF - Norges Jeger- og Fiskerforbund (rifle/hagle)</span>
                      <p className="text-sm text-gray-400">Jegerskyting med rifle og hagle</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.nbif}
                      onChange={(e) => handleShootingBranchChange('nbif', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NBIF - Norges bedriftsidrettsforbund (kun rifle)</span>
                      <p className="text-sm text-gray-400">Bedriftsskyting med rifle</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.nbsf}
                      onChange={(e) => handleShootingBranchChange('nbsf', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NBSF - Norges Benkeskytterforbund (kun rifle)</span>
                      <p className="text-sm text-gray-400">Benkeskyting med rifle</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-600" 
                      checked={settingsData.shootingBranches.nssf}
                      onChange={(e) => handleShootingBranchChange('nssf', e.target.checked)}
                    />
                    <div>
                      <span className="font-medium text-white">NSSF - Norges skiskytterforbund (kun rifle)</span>
                      <p className="text-sm text-gray-400">Skiskyting med rifle</p>
                    </div>
                  </label>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-4 text-white">Aktivitetstyper</h4>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settingsData.activityTypes.map((activityType, index) => {
                      const isPermanent = activityType === 'Organisert trening' || activityType === 'Klubbstevne';
                      return (
                        <div key={index} className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
                          isPermanent ? 'bg-yellow-600/20 border border-yellow-500/50' : 'bg-gray-700'
                        }`}>
                          <span className={`${isPermanent ? 'text-yellow-300' : 'text-white'}`}>
                            {activityType}
                            {isPermanent && <span className="text-xs ml-1">(påkrevd)</span>}
                          </span>
                          {!isPermanent && (
                            <button 
                              onClick={() => removeActivityType(activityType)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      className="flex-1 bg-gray-700 rounded-md px-3 py-2 text-sm text-white" 
                      placeholder="Legg til ny aktivitetstype"
                      value={newActivityType}
                      onChange={(e) => setNewActivityType(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addActivityType()}
                    />
                    <button 
                      onClick={addActivityType}
                      className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md text-sm font-medium hover:bg-yellow-400 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Language Settings - DISABLED */}
          {false && <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-globe w-5 h-5">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
                <path d="M2 12h20"></path>
              </svg>
              Språkinnstillinger
            </h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Aktiv språk</label>
                <select 
                  className="w-full bg-gray-700 rounded-md px-3 py-2 text-white"
                  value={currentLanguage}
                  onChange={async (e) => {
                    const newLanguage = e.target.value;
                    setSettingsData({...settingsData, activeLanguage: newLanguage});
                    await changeLanguage(newLanguage, user?.organization_id, user?.role);
                    toast.success(`Språk endret til ${availableLanguages.find(lang => lang.code === newLanguage)?.name || newLanguage}`);
                  }}
                >
                  {availableLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="border border-gray-600 rounded-lg p-4">
                <h4 className="font-medium mb-3 text-white">Eksporter språkfiler</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Last ned komplett språkfil for oversettelse. Filen inneholder alle tekster i applikasjonen 
                  og kan brukes til å lage oversettelser til andre språk.
                </p>
                <button 
                  onClick={exportLanguageFile}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-md transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download w-4 h-4 mr-2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-15"></path>
                    <polyline points="7,10 12,15 17,10"></polyline>
                    <line x1="12" x2="12" y1="15" y2="3"></line>
                  </svg>
                  Last ned norsk.json
                </button>
              </div>
              
              <div className="border border-gray-600 rounded-lg p-4">
                <h4 className="font-medium mb-3 text-white">Last opp oversettelse</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Last opp oversatt språkfil for å legge til et nytt språk i applikasjonen.
                </p>
                
                {languageUploadError && (
                  <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-md">
                    <p className="text-red-300 text-sm">{languageUploadError}</p>
                  </div>
                )}
                
                <div className="flex items-center space-x-4">
                  <input 
                    type="file" 
                    className="hidden" 
                    id="translation-upload" 
                    accept=".json"
                    onChange={handleLanguageFileUpload}
                    disabled={uploadingLanguage}
                  />
                  <label 
                    htmlFor="translation-upload" 
                    className={`cursor-pointer inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-md transition-colors ${
                      uploadingLanguage 
                        ? 'bg-gray-500 cursor-not-allowed' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload w-4 h-4 mr-2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7,10 12,15 17,10"></polyline>
                      <line x1="12" x2="12" y1="15" y2="3"></line>
                    </svg>
                    {uploadingLanguage ? 'Laster opp...' : 'Last opp språkfil (.json)'}
                  </label>
                </div>
              </div>
              
              {/* Uploaded Language Files */}
              {languageFiles.length > 0 && (
                <div className="border border-gray-600 rounded-lg p-4">
                  <h4 className="font-medium mb-3 text-white">Opplastede språkfiler</h4>
                  <div className="space-y-2">
                    {languageFiles.map((langFile) => (
                      <div key={langFile.language_code} className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">
                              {langFile.language_code.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{langFile.language_name}</p>
                            <p className="text-gray-400 text-sm">
                              Opplastet: {new Date(langFile.created_at).toLocaleDateString('no-NO')}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => deleteLanguageFile(langFile.language_code)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-md transition-colors"
                          title="Slett språkfil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>}

          {/* Feature Settings - Dugnad */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings-2 w-5 h-5">
                <path d="M20 7h-9"></path>
                <path d="M14 17H5"></path>
                <circle cx="17" cy="17" r="3"></circle>
                <circle cx="7" cy="7" r="3"></circle>
              </svg>
              Funksjonsinnstillinger
            </h3>
            
            <div className="space-y-4">
              {/* Calendar Feature Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-white">Kalender</span>
                    <p className="text-sm text-gray-400">
                      Vis klubbkalender med treninger, dugnader og arrangementer
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settingsData.calendarEnabled !== false}
                    onChange={(e) => setSettingsData({...settingsData, calendarEnabled: e.target.checked})}
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>
              
              {/* «Jeg kommer»-knapp */}
              {settingsData.calendarEnabled !== false && (
                <div className="flex items-center justify-between gap-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600" data-testid="settings-attendance-row">
                  <div>
                    <span className="font-medium text-white">Vis «Jeg kommer»-knapp i kalenderen</span>
                    <p className="text-sm text-gray-400">
                      Medlemmer kan melde at de kommer på hendelser. Antall påmeldte vises under Skyteleder &gt; Dagens.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={settingsData.calendarAttendanceEnabled !== false}
                      onChange={(e) => setSettingsData({...settingsData, calendarAttendanceEnabled: e.target.checked})}
                      data-testid="settings-attendance-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>
              )}

              {settingsData.calendarEnabled !== false && settingsData.calendarAttendanceEnabled !== false && (
                <div className="flex items-center justify-between gap-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600" data-testid="settings-attendance-public-row">
                  <div>
                    <span className="font-medium text-white">Medlemmer kan se hvem som kommer</span>
                    <p className="text-sm text-gray-400">
                      Vanlige medlemmer kan trykke på «N kommer» og se navnene på de som har meldt seg på.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={settingsData.calendarAttendancePublic !== false}
                      onChange={(e) => setSettingsData({...settingsData, calendarAttendancePublic: e.target.checked})}
                      data-testid="settings-attendance-public-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>
              )}

              {settingsData.calendarEnabled !== false && settingsData.calendarAttendanceEnabled !== false && (
                <div id="kapasitet" className="flex items-center justify-between gap-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600 scroll-mt-24" data-testid="settings-capacity-row">
                  <div className="min-w-0">
                    <span className="font-medium text-white">Kapasitetsstyring med lag på treninger</span>
                    <p className="text-sm text-gray-400">
                      Treninger på baner med antall standplasser deles i lag. Medlemmene får lag etter hvem som melder seg på først (via «Jeg kommer» eller QR), og får beskjed i Meldinger. Huk av hvilke baner som skal ha lag, og antall standplasser, i listen under når bryteren er på.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={settingsData.calendarCapacityEnabled === true}
                      onChange={(e) => setSettingsData({...settingsData, calendarCapacityEnabled: e.target.checked})}
                      data-testid="settings-capacity-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>
              )}

              {settingsData.calendarEnabled !== false && settingsData.calendarAttendanceEnabled !== false && settingsData.calendarCapacityEnabled === true && (
                <CapacityLocationsSettings orgId={user?.organization_id} locations={locations} onLocationsChange={setLocations} />
              )}

              {/* Calendar Event Types Management */}
              {settingsData.calendarEnabled !== false && (
                <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium text-white">Kalender-aktivitetstyper</h4>
                      <p className="text-sm text-gray-400">Administrer typer aktiviteter som kan opprettes i kalenderen</p>
                    </div>
                    <button
                      onClick={() => {
                        setNewEventType({ id: '', label: '', color: 'bg-blue-500' });
                        setShowAddEventTypeModal(true);
                      }}
                      className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 flex items-center gap-1"
                      data-testid="add-event-type-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Legg til
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {calendarEventTypes.map((eventType) => (
                      <div 
                        key={eventType.id}
                        className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded ${eventType.color}`}></div>
                          <span className="text-white">{eventType.label}</span>
                          <span className="text-xs text-gray-500">({eventType.id})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditEventTypeModal(eventType)}
                            className="p-1.5 text-gray-400 hover:bg-gray-600 rounded"
                            title="Rediger"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCalendarEventType(eventType.id)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                            title="Slett"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {calendarEventTypes.length === 0 && (
                      <p className="text-gray-400 text-sm text-center py-4">
                        Ingen aktivitetstyper definert. Klikk "Legg til" for å opprette.
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Calendar Timezone Settings */}
              {settingsData.calendarEnabled !== false && (
                <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                  <div className="mb-4">
                    <h4 className="font-medium text-white flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                      </svg>
                      Kalender-tidssone
                    </h4>
                    <p className="text-sm text-gray-400">Velg tidssone for kalenderen. Påvirker visning av hendelser og gjentagende aktiviteter.</p>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Timezone selector */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <label className="text-sm text-gray-300 min-w-[100px]">Tidssone:</label>
                      <select
                        value={settingsData.calendarTimezone || 'Europe/Oslo'}
                        onChange={(e) => setSettingsData({...settingsData, calendarTimezone: e.target.value})}
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="timezone-select"
                      >
                        <optgroup label="Europa">
                          <option value="Europe/Oslo">🇳🇴 Norge (Europe/Oslo)</option>
                          <option value="Europe/Stockholm">🇸🇪 Sverige (Europe/Stockholm)</option>
                          <option value="Europe/Copenhagen">🇩🇰 Danmark (Europe/Copenhagen)</option>
                          <option value="Europe/Helsinki">🇫🇮 Finland (Europe/Helsinki)</option>
                          <option value="Europe/London">🇬🇧 Storbritannia (Europe/London)</option>
                          <option value="Europe/Paris">🇫🇷 Frankrike (Europe/Paris)</option>
                          <option value="Europe/Berlin">🇩🇪 Tyskland (Europe/Berlin)</option>
                          <option value="Europe/Amsterdam">🇳🇱 Nederland (Europe/Amsterdam)</option>
                          <option value="Europe/Zurich">🇨🇭 Sveits (Europe/Zurich)</option>
                          <option value="Europe/Vienna">🇦🇹 Østerrike (Europe/Vienna)</option>
                          <option value="Europe/Madrid">🇪🇸 Spania (Europe/Madrid)</option>
                          <option value="Europe/Rome">🇮🇹 Italia (Europe/Rome)</option>
                          <option value="Europe/Warsaw">🇵🇱 Polen (Europe/Warsaw)</option>
                          <option value="Europe/Prague">🇨🇿 Tsjekkia (Europe/Prague)</option>
                          <option value="Europe/Athens">🇬🇷 Hellas (Europe/Athens)</option>
                          <option value="Europe/Moscow">🇷🇺 Russland (Europe/Moscow)</option>
                        </optgroup>
                        <optgroup label="Nord-Amerika">
                          <option value="America/New_York">🇺🇸 USA Øst (America/New_York)</option>
                          <option value="America/Chicago">🇺🇸 USA Sentral (America/Chicago)</option>
                          <option value="America/Denver">🇺🇸 USA Fjell (America/Denver)</option>
                          <option value="America/Los_Angeles">🇺🇸 USA Vest (America/Los_Angeles)</option>
                          <option value="America/Toronto">🇨🇦 Canada Øst (America/Toronto)</option>
                          <option value="America/Vancouver">🇨🇦 Canada Vest (America/Vancouver)</option>
                        </optgroup>
                        <optgroup label="Asia">
                          <option value="Asia/Tokyo">🇯🇵 Japan (Asia/Tokyo)</option>
                          <option value="Asia/Seoul">🇰🇷 Sør-Korea (Asia/Seoul)</option>
                          <option value="Asia/Shanghai">🇨🇳 Kina (Asia/Shanghai)</option>
                          <option value="Asia/Hong_Kong">🇭🇰 Hong Kong (Asia/Hong_Kong)</option>
                          <option value="Asia/Singapore">🇸🇬 Singapore (Asia/Singapore)</option>
                          <option value="Asia/Dubai">🇦🇪 Dubai (Asia/Dubai)</option>
                          <option value="Asia/Kolkata">🇮🇳 India (Asia/Kolkata)</option>
                        </optgroup>
                        <optgroup label="Oceania">
                          <option value="Australia/Sydney">🇦🇺 Australia Øst (Australia/Sydney)</option>
                          <option value="Australia/Perth">🇦🇺 Australia Vest (Australia/Perth)</option>
                          <option value="Pacific/Auckland">🇳🇿 New Zealand (Pacific/Auckland)</option>
                        </optgroup>
                        <optgroup label="Sør-Amerika">
                          <option value="America/Sao_Paulo">🇧🇷 Brasil (America/Sao_Paulo)</option>
                          <option value="America/Buenos_Aires">🇦🇷 Argentina (America/Buenos_Aires)</option>
                        </optgroup>
                        <optgroup label="Afrika">
                          <option value="Africa/Johannesburg">🇿🇦 Sør-Afrika (Africa/Johannesburg)</option>
                          <option value="Africa/Cairo">🇪🇬 Egypt (Africa/Cairo)</option>
                        </optgroup>
                        <optgroup label="UTC">
                          <option value="UTC">🌐 UTC (Koordinert universaltid)</option>
                        </optgroup>
                      </select>
                    </div>
                    
                    {/* DST Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-white">Følg sommertid (DST)</span>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Aktivér for automatisk justering ved sommertid/vintertid
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={settingsData.calendarDstEnabled !== false}
                          onChange={(e) => setSettingsData({...settingsData, calendarDstEnabled: e.target.checked})}
                          data-testid="dst-toggle"
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                      </label>
                    </div>
                    
                    {/* DST Info Panel */}
                    {settingsData.calendarDstEnabled !== false && dstInfo && (
                      <div className="p-3 bg-gray-800/60 rounded-lg border border-gray-700 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full ${dstInfo.is_dst_now ? 'bg-yellow-400' : 'bg-blue-400'}`}></div>
                          <span className="text-gray-300">
                            Nåværende tid: <span className="font-semibold text-white">UTC+{dstInfo.current_offset_hours} ({dstInfo.current_time_label})</span>
                          </span>
                        </div>
                        {dstInfo.next_spring_forward && (
                          <div className="flex items-start gap-2 text-xs text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 text-yellow-500 flex-shrink-0"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                            <span>
                              Neste sommertid: <span className="text-yellow-400 font-medium">{dstInfo.next_spring_forward}</span>
                              <br/>{dstInfo.spring_description}
                            </span>
                          </div>
                        )}
                        {dstInfo.next_fall_back && (
                          <div className="flex items-start gap-2 text-xs text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 text-blue-400 flex-shrink-0"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                            <span>
                              Neste vintertid: <span className="text-blue-400 font-medium">{dstInfo.next_fall_back}</span>
                              <br/>{dstInfo.autumn_description}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                      Tidssoneinnstillingen påvirker alle kalenderhendelser og gjentagende aktiviteter for denne organisasjonen.
                    </p>

                    {/* Default views (app + embed) */}
                    <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-600">
                      <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        Standard visning av Kalender
                      </h5>
                      <p className="text-sm text-gray-400 mb-3">
                        Velg hvilken visning som vises som default når noen åpner kalenderen.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* App default view */}
                        <div>
                          <label className="block text-sm text-gray-300 mb-2">I Aktivlogg-appen:</label>
                          <select
                            value={settingsData.calendarDefaultView || 'week'}
                            onChange={(e) => setSettingsData({...settingsData, calendarDefaultView: e.target.value})}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="calendar-default-view-app"
                          >
                            <option value="day">Dag</option>
                            <option value="week">Uke</option>
                            <option value="month">Måned</option>
                            <option value="list">Liste</option>
                          </select>
                        </div>

                        {/* Embed default view */}
                        <div>
                          <label className="block text-sm text-gray-300 mb-2">På Embed (klubbens nettside):</label>
                          <select
                            value={settingsData.calendarEmbedDefaultView || 'week'}
                            onChange={(e) => setSettingsData({...settingsData, calendarEmbedDefaultView: e.target.value})}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="calendar-default-view-embed"
                          >
                            <option value="week">Uke</option>
                            <option value="month">Måned</option>
                            <option value="list">Liste</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Brukes hvis ?view= ikke er satt i iframe-URL-en.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Import calendar (.ics / webcal) */}
                    <CalendarImportSection user={user} />
                    
                    {/* Iframe Embed Section */}
                    <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-600">
                      <h5 className="font-medium text-white mb-2 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                          <line x1="8" y1="21" x2="16" y2="21"></line>
                          <line x1="12" y1="17" x2="12" y2="21"></line>
                        </svg>
                        Embed på nettside
                      </h5>
                      <p className="text-sm text-gray-400 mb-3">
                        Bruk denne koden for å vise kalenderen på klubbens nettside.
                      </p>
                      
                      {/* Theme selector */}
                      <div className="mb-4">
                        <label className="text-sm text-gray-300 block mb-2">Velg fargetema:</label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setSettingsData({...settingsData, embedTheme: 'dark'})}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                              (settingsData.embedTheme || 'dark') === 'dark' 
                                ? 'bg-gray-800 border-yellow-500 text-white' 
                                : 'bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500'
                            }`}
                            data-testid="embed-theme-dark"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                            </svg>
                            Dark mode
                          </button>
                          <button
                            type="button"
                            onClick={() => setSettingsData({...settingsData, embedTheme: 'light'})}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                              settingsData.embedTheme === 'light' 
                                ? 'bg-white border-yellow-500 text-gray-800' 
                                : 'bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500'
                            }`}
                            data-testid="embed-theme-light"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="5"></circle>
                              <line x1="12" y1="1" x2="12" y2="3"></line>
                              <line x1="12" y1="21" x2="12" y2="23"></line>
                              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                              <line x1="1" y1="12" x2="3" y2="12"></line>
                              <line x1="21" y1="12" x2="23" y2="12"></line>
                              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                            </svg>
                            Light mode
                          </button>
                        </div>
                      </div>
                      
                      <div className="relative">
                        <pre className="bg-gray-900 p-3 rounded text-xs text-green-400 overflow-x-auto whitespace-pre-wrap break-all">
{`<iframe 
  src="${window.location.origin}/embed/calendar/${user?.organization_id}?theme=${settingsData.embedTheme || 'dark'}" 
  width="100%" 
  height="600" 
  frameborder="0" 
  style="border-radius: 8px; border: 1px solid ${(settingsData.embedTheme || 'dark') === 'light' ? '#e5e7eb' : '#374151'};">
</iframe>`}
                        </pre>
                        <button
                          type="button"
                          onClick={() => {
                            const theme = settingsData.embedTheme || 'dark';
                            const borderColor = theme === 'light' ? '#e5e7eb' : '#374151';
                            const iframeCode = `<iframe src="${window.location.origin}/embed/calendar/${user?.organization_id}?theme=${theme}" width="100%" height="600" frameborder="0" style="border-radius: 8px; border: 1px solid ${borderColor};"></iframe>`;
                            navigator.clipboard.writeText(iframeCode);
                            alert('Iframe-kode kopiert til utklippstavle!');
                          }}
                          className="absolute top-2 right-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded flex items-center gap-1"
                          data-testid="copy-iframe-btn"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                          Kopier
                        </button>
                      </div>
                      
                      <div className="mt-3">
                        <a 
                          href={`/embed/calendar/${user?.organization_id}?theme=${settingsData.embedTheme || 'dark'}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                          Forhåndsvis embed-kalender
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Dugnad Feature Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#EAB308"/>
                      <path d="M19 19H5v-2h14v2z" fill="#EAB308"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-white">Dugnad-statistikk</span>
                    <p className="text-sm text-gray-400">
                      Vis dugnadsdeltakelse, krone-ikoner og PDF-rapporter
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settingsData.dugnadFeatureEnabled}
                    onChange={(e) => setSettingsData({...settingsData, dugnadFeatureEnabled: e.target.checked})}
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>
              
              {!settingsData.dugnadFeatureEnabled && (
                <div className="p-3 bg-amber-900/20 border border-amber-600/50 rounded-lg">
                  <p className="text-amber-300 text-sm flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    Når denne funksjonen er deaktivert, skjules alle dugnad-relaterte elementer fra sidene Medlemmer, Klubb-logg og Statistikk.
                  </p>
                </div>
              )}
              
              {/* Dugnad Settings - Only show when feature is enabled */}
              {settingsData.dugnadFeatureEnabled && (
                <div className="mt-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                  <div className="flex flex-wrap items-center gap-6">
                    {/* Activity type dropdown */}
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-300">Aktivitetstype:</label>
                      <select
                        value={settingsData.dugnadActivity}
                        onChange={(e) => {
                          setSettingsData({...settingsData, dugnadActivity: e.target.value});
                          setSelectedDugnadActivity(e.target.value);
                        }}
                        className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      >
                        {(settingsData.activityTypes || ['Dugnad']).map((activity) => (
                          <option key={activity} value={activity}>
                            {activity}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Threshold input */}
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-300">Krav for gullkrone:</label>
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={settingsData.dugnadThreshold}
                        onChange={(e) => {
                          const newThreshold = Math.max(1, parseInt(e.target.value) || 3);
                          setSettingsData({...settingsData, dugnadThreshold: newThreshold});
                          setDugnadThreshold(newThreshold);
                        }}
                        className="w-20 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-center focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                      <span className="text-sm text-gray-400">per år</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Medlemmer som oppnår kravet vises med gullkrone i medlemslisten og klubb-loggen.
                  </p>
                </div>
              )}

              {/* Messages Feature Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <Mail size={20} className="text-green-500" />
                  </div>
                  <div>
                    <span className="font-medium text-white">Meldinger</span>
                    <p className="text-sm text-gray-400">
                      Send meldinger til medlemmer med rich-text formatering
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settingsData.messagesEnabled !== false}
                    onChange={(e) => setSettingsData({...settingsData, messagesEnabled: e.target.checked})}
                    data-testid="messages-feature-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
              
              {settingsData.messagesEnabled === false && (
                <div className="p-3 bg-amber-900/20 border border-amber-600/50 rounded-lg">
                  <p className="text-amber-300 text-sm flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    Når denne funksjonen er deaktivert, skjules Meldinger-fanen i Skyteleder-seksjonen, popup-varsler og meldingsvisning på Hjem-siden.
                  </p>
                </div>
              )}

              {/* Merkeskyting Feature Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EAB308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                      <path d="M4 22h16"/>
                      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-white">Merkeskyting</span>
                    <p className="text-sm text-gray-400">
                      NSFs ordning for ferdighetsmerker – registrering, progresjon og diplomer
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settingsData.merkeskytingEnabled !== false}
                    onChange={(e) => setSettingsData({...settingsData, merkeskytingEnabled: e.target.checked})}
                    data-testid="merkeskyting-feature-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>

              {settingsData.merkeskytingEnabled === false && (
                <div className="p-3 bg-amber-900/20 border border-amber-600/50 rounded-lg">
                  <p className="text-amber-300 text-sm flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    Når denne funksjonen er deaktivert, skjules Merkeskyting-modulen under Klubb drift, Hjem-kortene og Signage-visningen.
                  </p>
                </div>
              )}

              {/* Club Operations Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-indigo-400" />
                  </div>
                  <div>
                    <span className="font-medium text-white">Klubb drift</span>
                    <p className="text-sm text-gray-400">
                      Våpenforvaltning, ammunisjonsforvaltning og baneforvaltning
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settingsData.clubOperationsEnabled === true}
                    onChange={(e) => setSettingsData({...settingsData, clubOperationsEnabled: e.target.checked})}
                    data-testid="club-operations-feature-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
              
              {settingsData.clubOperationsEnabled === true && (
                <div className="p-3 bg-indigo-900/20 border border-indigo-600/50 rounded-lg">
                  <p className="text-indigo-300 text-sm flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Klubb drift er aktivert. Du finner modulene under "Klubb drift" i hovedmenyen.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Discipline Boxes Editor */}
          <div className="bg-gray-800 rounded-lg p-3 md:p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <Target className="w-5 h-5" />
              Disiplin-bokser på Logg-siden
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              Rediger innholdet i de tre disciplinboksene som vises nederst på treningslogg-siden.
            </p>
            
            <DisciplineBoxEditor organizationId={user?.organization_id} />
          </div>

          {/* Ammo Sales Settings */}
          <div id="ammo-salg" className="p-6 bg-gray-700 rounded-lg scroll-mt-20">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
              <img src="/patron-symbol.svg" alt="Ammo" className="w-5 h-5" />
              Ammo-salg
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              Aktiver og administrer ammunisjonssalg for din organisasjon.
            </p>
            
            <AmmoSettings onSettingsChange={(enabled) => {
              setAmmoModuleEnabled(enabled);
              // Force refresh to hide/show ammo elements immediately
              setTimeout(() => {
                setAmmoModuleEnabled(enabled);
              }, 100);
            }} />
          </div>

          {/* Automatic Ammo Reports - Only show if module is enabled */}
          {ammoModuleEnabled && (
            <div className="p-6 bg-gray-700 rounded-lg">
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-6 text-white">
                <Mail className="w-5 h-5" />
                Automatiske rapporter
              </h3>
              <p className="text-gray-400 text-sm mb-6">
                Konfigurer automatisk utsendelse av ammunisjonsrapporter via e-post.
              </p>
              
              <AmmoReportSettings />
            </div>
          )}

          {/* Innebygd statistikk på klubbens nettside (kun admin/super_admin) - PLASSERT NEDERST */}
          {(user?.role === 'admin' || user?.role === 'super_admin' || impersonationContext?.originalRole === 'super_admin') && (
            <StatsEmbedSection
              organizationId={user?.organization_id}
              isAdmin={true}
            />
          )}

          {/* Save Button */}
          <div className="flex justify-end space-x-4">
            <button 
              onClick={saveSettings}
              disabled={settingsLoading}
              className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
            >
              {settingsLoading ? 'Lagrer...' : 'LAGRE'}
            </button>
          </div>
        </div>
      )}

      {/* Location Form Modal */}
      {showLocationForm && (
        <LocationFormModal 
          editingLocation={editingLocation}
          organizationActivityTypes={settingsData.activityTypes || []}
          onSave={async (locationData) => {
            try {
              const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
              
              if (editingLocation) {
                // Update existing location
                await axios.put(`${API_BASE}/api/organizations/${user.organization_id}/locations/${editingLocation.id}`, locationData, { headers });
                toast.success('Skytebanen ble oppdatert!');
              } else {
                // Create new location
                await axios.post(`${API_BASE}/api/organizations/${user.organization_id}/locations`, locationData, { headers });
                toast.success('Ny skytebane opprettet!');
              }
              
              setShowLocationForm(false);
              setEditingLocation(null);
              await loadAdminData();
            } catch (error) {
              console.error('Failed to save location:', error);
              toast.error('Kunne ikke lagre skytebanen. Prøv igjen.');
            }
          }}
          onCancel={() => {
            setShowLocationForm(false);
            setEditingLocation(null);
          }}
        />
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && (
        <div className="space-y-6">
          <StatisticsSection 
            sessions={sessions} 
            members={members} 
            locations={locations}
            organization={user?.organization}
          />

          {/* Merkeskyting Statistics – vises alltid, viser tomt-state hvis ingen data */}
          <div className="mt-6 bg-gray-800/40 rounded-xl p-5 border border-gray-700">
            <MerkeskytingStatsCard />
          </div>
          
          {/* Ammo Statistics - Only show if module is enabled */}
          {ammoModuleEnabled && (
            <AmmoStatistics />
          )}

          {/* Internal Activity Statistics - Only show if ammo module is enabled */}
          {ammoModuleEnabled && (
            <div className="mt-6">
              <InternalActivityStatistics />
            </div>
          )}

          {/* Ammo Annual Report - Only show if module is enabled */}
          {ammoModuleEnabled && (
            <div className="mt-6">
              <AmmoAnnualReport />
            </div>
          )}
        </div>
      )}

      {/* QR Poster Generator Modal */}
      {showPosterGenerator && selectedLocation && (
        <QRPosterGenerator
          location={selectedLocation}
          organization={user?.organization}
          onClose={() => {
            setShowPosterGenerator(false);
            setSelectedLocation(null);
          }}
        />
      )}

      {/* QR Generator Modal */}
      {showQRGenerator && (
        <QRGeneratorModal
          isOpen={showQRGenerator}
          onClose={() => {
            setShowQRGenerator(false);
            setQRGeneratorLocation(null);
          }}
          initialText={qrGeneratorLocation?.qr_code_data || qrGeneratorLocation?.id || ''}
          initialLocationName={qrGeneratorLocation?.name || ''}
          discipline={qrGeneratorLocation?.discipline || ''}
          disciplineColor={qrGeneratorLocation?.discipline_color || ''}
        />
      )}

      {/* Manual Training Registration Modal */}
      {showManualRegistration && (
        <ManualTrainingRegistration
          isOpen={showManualRegistration}
          onClose={() => setShowManualRegistration(false)}
          onSuccess={() => {
            loadAdminData(); // Refresh data after successful registration
          }}
        />
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-white">Legg til medlem</h3>
                <button
                  onClick={() => setShowAddMemberModal(false)}
                  className="text-gray-400 hover:text-white text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleAddMember}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Navn *
                    </label>
                    <input
                      type="text"
                      required
                      value={addMemberData.full_name}
                      onChange={(e) => setAddMemberData({...addMemberData, full_name: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Fullt navn"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      E-post *
                    </label>
                    <input
                      type="email"
                      required
                      value={addMemberData.email}
                      onChange={(e) => setAddMemberData({...addMemberData, email: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="medlem@eksempel.no"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Rolle *
                    </label>
                    <select
                      value={addMemberData.role}
                      onChange={(e) => setAddMemberData({...addMemberData, role: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="member">Medlem</option>
                      <option value="org_admin">Skyteleder</option>
                      {(user?.role === 'super_admin' || impersonationContext?.originalRole === 'super_admin') && (
                        <option value="admin">Admin</option>
                      )}
                    </select>
                  </div>

                  <div className="bg-green-900/20 border border-green-500/30 p-3 rounded-lg">
                    <p className="text-sm text-green-300 flex items-center gap-2">
                      <span>🔒</span>
                      <span>Medlemmet vil motta en sikker e-post med link for å opprette sitt eget passord (gyldig i 24 timer).</span>
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddMemberModal(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    disabled={addingMember}
                    className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-400 disabled:opacity-50"
                  >
                    {addingMember ? 'Oppretter...' : 'Legg til medlem'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Member Edit Modal */}
      {showMemberEdit && (
        <MemberEditModal
          member={selectedMember}
          isOpen={showMemberEdit}
          memberFilter={memberFilter}
          onClose={() => {
            setShowMemberEdit(false);
            setSelectedMember(null);
          }}
          onSuccess={() => {
            loadAdminData(); // Refresh data after successful update
          }}
        />
      )}

      {/* PAUSE / DEAKTIVER Modal (åpnes ved klikk på grønt ikon i medlemstabellen) */}
      {showPauseModal && pauseTargetMember && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" data-testid="pause-modal">
          <div className="bg-gray-800 rounded-lg border border-gray-600 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500/20 p-2 rounded-full">
                  <CheckCircle className="h-6 w-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Endre status for medlem</h3>
                  <p className="text-sm text-gray-400">{pauseTargetMember.full_name} · {pauseTargetMember.email}</p>
                </div>
              </div>
              <button
                onClick={() => { setShowPauseModal(false); setPauseTargetMember(null); setPauseReason(''); }}
                className="text-gray-400 hover:text-white p-1"
                aria-label="Lukk"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* OPTION 1: Pause (gul) */}
              <div className="border border-yellow-500/40 bg-yellow-500/5 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">🟡</span>
                  <div>
                    <h4 className="text-white font-semibold">Sett på PAUSE</h4>
                    <p className="text-xs text-gray-300 mt-1">
                      Medlemmet kan ikke logge inn i klubben din inntil reaktivert. Alle treningsdata, dokumenter og logg bevares fullt ut. Hvis medlemmet er i flere klubber, kan de fortsatt logge inn i andre klubber.
                    </p>
                  </div>
                </div>
                <label className="block text-sm font-medium text-yellow-200 mb-1">
                  Årsak (obligatorisk) <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="F.eks: Manglende betaling av kontingent for 2026. Ta kontakt med kasserer for å ordne dette."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                  data-testid="pause-reason-textarea"
                />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-400">Denne årsaken vises for medlemmet på login-siden.</span>
                  <span className="text-xs text-gray-400">{(pauseReason || '').length}/500</span>
                </div>
                <button
                  onClick={submitPause}
                  disabled={pauseSubmitting || !(pauseReason || '').trim()}
                  className="mt-3 w-full px-4 py-2 bg-yellow-500 text-gray-900 font-semibold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="pause-submit-btn"
                >
                  {pauseSubmitting ? 'Setter på pause…' : '🟡 Sett på PAUSE'}
                </button>
              </div>

              {/* OPTION 2: Deactivate permanently (rød) */}
              <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">🔴</span>
                  <div>
                    <h4 className="text-white font-semibold">Deaktiver permanent</h4>
                    <p className="text-xs text-gray-300 mt-1">
                      Medlemmet markeres som ikke-godkjent og kan ikke logge inn. Bruk dette ved utmelding eller eksklusjon. Data bevares, men medlemmet havner under filteret &quot;Deaktiverte&quot;.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm(`Sikker på at du vil deaktivere ${pauseTargetMember.full_name} permanent? Medlemmet kan reaktiveres senere via medlemstabellen.`)) {
                      deactivateUser(pauseTargetMember.id, pauseTargetMember.full_name);
                      setShowPauseModal(false);
                      setPauseTargetMember(null);
                      setPauseReason('');
                    }
                  }}
                  className="w-full px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  data-testid="deactivate-btn"
                >
                  🔴 Deaktiver permanent
                </button>
              </div>

              {/* Cancel */}
              <button
                onClick={() => { setShowPauseModal(false); setPauseTargetMember(null); setPauseReason(''); }}
                className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Email Change Modal */}
      {showEmailChangeModal && emailChangeMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-600 p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-yellow-500/20 p-2 rounded-full">
                  <Mail className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Endre e-postadresse
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {emailChangeMember.full_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowEmailChangeModal(false);
                  setEmailChangeMember(null);
                  setNewEmailAddress('');
                  setEmailChangeReason('');
                  setBackupCreated(false);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Backup Section */}
            <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-300 font-medium">Anbefalt: Lag backup først</p>
                  <p className="text-blue-400/70 text-sm">Last ned en sikkerhetskopi av medlemmets data før endring</p>
                </div>
                <button
                  onClick={() => createMemberBackup(emailChangeMember.id)}
                  disabled={isCreatingBackup}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    backupCreated 
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 hover:bg-blue-500 text-white'
                  } disabled:opacity-50`}
                >
                  {isCreatingBackup ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Oppretter...
                    </>
                  ) : backupCreated ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Backup lastet ned
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Kjør backup
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Current Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Nåværende e-postadresse
              </label>
              <div className="px-4 py-3 bg-gray-700/50 rounded-lg text-gray-300 border border-gray-600">
                {emailChangeMember.email}
              </div>
            </div>

            {/* New Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Ny e-postadresse <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={newEmailAddress}
                onChange={(e) => setNewEmailAddress(e.target.value)}
                placeholder="ny.epost@eksempel.no"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              />
            </div>

            {/* Reason */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Årsak til endring (valgfritt)
              </label>
              <textarea
                value={emailChangeReason}
                onChange={(e) => setEmailChangeReason(e.target.value)}
                placeholder="F.eks: Bruker byttet e-postleverandør"
                rows={2}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 resize-none"
              />
            </div>

            {/* Info Box */}
            <div className="bg-gray-700/50 rounded-lg p-3 mb-6">
              <p className="text-gray-400 text-sm">
                <strong className="text-gray-300">Hva skjer?</strong>
              </p>
              <ul className="text-gray-400 text-sm mt-2 space-y-1">
                <li>• E-posten oppdateres umiddelbart</li>
                <li>• Brukeren varsles på både gammel og ny e-post</li>
                <li>• Alle eksisterende data beholdes</li>
                <li>• Endringen loggføres i audit-loggen</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEmailChangeModal(false);
                  setEmailChangeMember(null);
                  setNewEmailAddress('');
                  setEmailChangeReason('');
                  setBackupCreated(false);
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
              >
                Avbryt
              </button>
              <button
                onClick={handleEmailChange}
                disabled={!newEmailAddress.trim()}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Endre e-post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Old bulk delete modal removed - using simple modal from TrainingLog.js instead */}
      {/* QR Warning Modal */}
      {showQRWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-600 p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-yellow-500/20 p-2 rounded-full">
                <AlertTriangle className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Advarsel - Redigering av QR-kode
                </h3>
                <p className="text-gray-400 text-sm">
                  Dette kan påvirke eksisterende QR-koder
                </p>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3 mb-4">
              <p className="text-blue-300 text-sm">
                <strong>Anbefaling:</strong> Vi anbefaler å ikke gjøre endringer på QR-koden etter at den er opprettet og tatt i bruk.
              </p>
            </div>

            {qrWarningLocation && (
              <div className="bg-gray-700/50 rounded-lg p-3 mb-4">
                <p className="text-gray-300 text-sm mb-1">Du er i ferd med å redigere:</p>
                <p className="text-white font-medium">{qrWarningLocation.name}</p>
                <p className="text-gray-400 text-sm">QR-kode: {qrWarningLocation.qr_code_data || qrWarningLocation.id}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowQRWarningModal(false);
                  setQRWarningLocation(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  // Proceed with editing
                  setShowQRWarningModal(false);
                  setEditingLocation(qrWarningLocation);
                  setShowLocationForm(true);
                  setQRWarningLocation(null);
                }}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-400 font-medium"
              >
                Forstått, fortsett likevel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal (from TrainingLog.js) */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-red-400 mb-4">Bekreft sletting</h3>
            <p className="text-gray-300 mb-6">
              Er du sikker på at du vil slette {selectedSessions.size} treningsoppføringer? 
              Denne handlingen kan ikke angres.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Slett {selectedSessions.size} oppføringer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Ammo Sale Modal */}
      {showAmmoSaleModal && (
        <AmmoSaleModal
          key={`ammo-sale-${selectedSessionForAmmo?.id || 'direct'}`}
          sessionId={selectedSessionForAmmo?.id || null}
          memberId={selectedSessionForAmmo?.user_id || user?.id}
          memberName={selectedSessionForAmmo?.is_trial 
            ? selectedSessionForAmmo?.trial_name 
            : (selectedSessionForAmmo?.user_name || user?.full_name)}
          isTrial={selectedSessionForAmmo?.is_trial || false}
          trialName={selectedSessionForAmmo?.trial_name || ''}
          allowMemberSelection={!selectedSessionForAmmo}
          defaultTag={selectedSessionForAmmo?.is_trial ? 'GJESTE-SKYTING' : 
            (selectedSessionForAmmo?.activity?.toLowerCase()?.includes('sikkerhet') ? 'SIKKERH.KURS' :
            (selectedSessionForAmmo?.activity?.toLowerCase()?.includes('innskyting') ? 'INNSKYTING' : null))}
          onClose={() => {
            setShowAmmoSaleModal(false);
            setSelectedSessionForAmmo(null);
          }}
          onSuccess={() => {
            // Reload admin data to refresh ammo stock
            loadAdminData();
          }}
        />
      )}

      {/* Edit Training Session Modal */}
      {showEditSessionModal && editingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium text-white">Rediger Treningslogg</h3>
              <button
                onClick={() => {
                  setShowEditSessionModal(false);
                  setEditingSession(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Medlem - redigerbar dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Medlem
                </label>
                <select
                  value={editSessionData.user_id}
                  onChange={(e) => setEditSessionData({ ...editSessionData, user_id: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  {/* Current member as first option */}
                  <option value={editingSession.user_id}>
                    {editingSession.user_name} (nåværende)
                  </option>
                  {/* Other members */}
                  {members
                    .filter(m => m.id !== editingSession.user_id)
                    .sort((a, b) => a.full_name.localeCompare(b.full_name))
                    .map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.full_name}
                      </option>
                    ))}
                </select>
                {editSessionData.user_id !== editingSession.user_id && (
                  <p className="text-xs text-yellow-400 mt-1">
                    ⚠️ Medlem vil endres fra "{editingSession.user_name}" til valgt medlem
                  </p>
                )}
              </div>

              {/* Dato */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Dato
                </label>
                <input
                  type="date"
                  value={editSessionData.date}
                  onChange={(e) => setEditSessionData({ ...editSessionData, date: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              {/* Aktivitet */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Aktivitet
                </label>
                <select
                  value={editSessionData.activity}
                  onChange={(e) => setEditSessionData({ ...editSessionData, activity: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  {user?.organization?.activity_types?.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Bane */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bane
                </label>
                <select
                  value={editSessionData.location_id}
                  onChange={(e) => setEditSessionData({ ...editSessionData, location_id: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Velg bane</option>
                  {locations
                    .filter(loc => loc.active)
                    .map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Skyttergren */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Skyttergren
                </label>
                <select
                  value={editSessionData.shooting_branch}
                  onChange={(e) => setEditSessionData({ ...editSessionData, shooting_branch: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  {/* Only show branches that are enabled for this organization */}
                  {(user?.organization?.shooting_branches?.nsf === true || user?.organization?.shooting_branches?.nsf === undefined) && (
                    <option value="nsf">NSF (Norges Sportsskytterforbund)</option>
                  )}
                  {user?.organization?.shooting_branches?.dfs === true && (
                    <option value="dfs">DFS (Det Frivillige Skyttervesen)</option>
                  )}
                  {user?.organization?.shooting_branches?.dssn === true && (
                    <option value="dssn">DSSN (Dynamisk Sportsskyting Norge)</option>
                  )}
                  {user?.organization?.shooting_branches?.sws === true && (
                    <option value="sws">SWS (Scandinavian Western Shooters)</option>
                  )}
                  {user?.organization?.shooting_branches?.nsu === true && (
                    <option value="nsu">NSU (Norsk Svartkruttunion)</option>
                  )}
                  {user?.organization?.shooting_branches?.nrof === true && (
                    <option value="nrof">NROF (Norges Reserveoffiserers Forbund)</option>
                  )}
                  {user?.organization?.shooting_branches?.nmsf === true && (
                    <option value="nmsf">NMSF (Norges Metallsilhuett Forbund)</option>
                  )}
                  {user?.organization?.shooting_branches?.njff === true && (
                    <option value="njff">NJFF (Norges Jeger- og Fiskerforbund)</option>
                  )}
                  {user?.organization?.shooting_branches?.nbif === true && (
                    <option value="nbif">NBIF (Norges Bedriftsidrettsforbund)</option>
                  )}
                  {user?.organization?.shooting_branches?.nbsf === true && (
                    <option value="nbsf">NBSF (Norges Benkeskytterforbund)</option>
                  )}
                  {user?.organization?.shooting_branches?.nssf === true && (
                    <option value="nssf">NSSF (Norges skiskytterforbund)</option>
                  )}
                </select>
              </div>

              {/* Skyteleder */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Skyteleder
                </label>
                <input
                  type="text"
                  value={editSessionData.range_officer_name}
                  onChange={(e) => setEditSessionData({ ...editSessionData, range_officer_name: e.target.value })}
                  placeholder="Navn på skyteleder"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              {/* Admin-notat – kun synlig for skyteledere/admins/super_admin */}
              <div className="pt-2 border-t border-gray-700">
                <label className="block text-sm font-medium text-yellow-300 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Internt notat (kun synlig for Skyteledere / Admin)
                </label>
                <textarea
                  value={editSessionData.admin_notes}
                  onChange={(e) => setEditSessionData({ ...editSessionData, admin_notes: e.target.value })}
                  rows={3}
                  placeholder="Notater her er synlige for klubbens skyteledere og admin, men ikke for medlemmet selv."
                  className="w-full px-4 py-2 bg-gray-900/60 border border-yellow-700/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-y"
                  data-testid="edit-session-admin-notes"
                />
                {editingSession?.admin_notes_updated_by && (
                  <p className="text-xs text-gray-400 mt-1">
                    Sist redigert av {editingSession.admin_notes_updated_by}
                    {editingSession.admin_notes_updated_at && (
                      <> – {new Date(editingSession.admin_notes_updated_at).toLocaleString('nb-NO')}</>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditSessionModal(false);
                  setEditingSession(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={saveSessionEdit}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
              >
                Lagre endringer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ammo Sales Reminder Modal */}
      {showAmmoReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-yellow-500">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-yellow-500/20 rounded-full">
                <svg className="h-8 w-8 text-yellow-400" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1720.46 1622.88">
                  <path d="M239.43,0C56.46,0,19.96,196.91,18.46,308.13h438.7C453.42,194.7,429.65,0,239.43,0Z"/>
                  <path d="M437.17,345.42H38.43c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69H0v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                  <path d="M861.85,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                  <path d="M1059.59,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                  <path d="M1484.28,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
                  <path d="M1682.02,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Påminnelse: Ammunisjonssalg</h3>
            </div>
            
            <p className="text-gray-300 mb-6">
              Det ser ikke ut til at det er registrert ammunisjonssalg på noen av dagens treninger. 
              Stemmer det at det ikke har vært noe ammunisjonssalg i dag?
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowAmmoReminderModal(false);
                  // Navigate to ammo-salg tab
                  setActiveTab('ammo');
                  toast.info('Gå til Ammo-salg for å registrere salg');
                }}
                className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors"
              >
                😊 Jeg fyller ut ammo-salg nå
              </button>
              
              <button
                onClick={() => {
                  setShowAmmoReminderModal(false);
                  // Continue with approval, skip ammo check
                  approveAllPendingSessions(true);
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Ingen ammosalg idag
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Klubb-logg PDF Options Dialog with Document Selection */}
      {showKlubbloggPdfOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">
                {klubbloggPdfType === 'politi' ? 'Politi-PDF' : 'Standard-PDF'} - Valg
              </h3>
              <button
                onClick={() => setShowKlubbloggPdfOptions(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Show selected member info */}
            {sessionMemberFilter && (() => {
              const memberData = members.find(m => m.id === sessionMemberFilter);
              return memberData ? (
                <div className="bg-gray-700/50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-400">Genererer PDF for:</p>
                  <p className="text-white font-medium">{memberData.full_name}</p>
                </div>
              ) : null;
            })()}

            {/* Document selection */}
            {(() => {
              const availableDocs = getFilteredMemberDocs();
              return availableDocs.length > 0 ? (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-white">Velg vedlegg å inkludere:</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedMemberDocs(availableDocs.map(d => d.url))}
                        className="text-xs text-yellow-400 hover:text-yellow-300"
                      >
                        Velg alle
                      </button>
                      <span className="text-gray-500">|</span>
                      <button
                        onClick={() => setSelectedMemberDocs([])}
                        className="text-xs text-gray-400 hover:text-gray-300"
                      >
                        Velg ingen
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableDocs.map((doc, index) => (
                      <label 
                        key={index}
                        className="flex items-center p-2 bg-gray-700/50 rounded-lg hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedMemberDocs.includes(doc.url)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMemberDocs([...selectedMemberDocs, doc.url]);
                            } else {
                              setSelectedMemberDocs(selectedMemberDocs.filter(u => u !== doc.url));
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500 mr-3"
                        />
                        <FileText className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-white">{doc.name}</span>
                      </label>
                    ))}
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2">
                    {selectedMemberDocs.length} av {availableDocs.length} vedlegg valgt
                  </p>
                </div>
              ) : (
                <div className="bg-gray-700/30 rounded-lg p-4 mb-6 text-center">
                  <p className="text-gray-400 text-sm">Ingen vedlegg tilgjengelig for dette medlemmet.</p>
                  <p className="text-gray-500 text-xs mt-1">
                    Medlemmet må laste opp startkort, medlemskort eller våpenkort under "Min profil".
                  </p>
                </div>
              );
            })()}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowKlubbloggPdfOptions(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  // Get current valid docs to ensure we only use docs for the currently filtered member
                  const currentValidDocs = getFilteredMemberDocs();
                  const validDocUrls = currentValidDocs.map(d => d.url);
                  
                  // Filter selectedMemberDocs to only include valid docs for current member
                  const docsToDownload = selectedMemberDocs.filter(url => validDocUrls.includes(url));
                  
                  console.log('📋 PDF Download - Type:', klubbloggPdfType);
                  console.log('📋 Current member filter:', sessionMemberFilter);
                  console.log('📋 Valid docs for member:', validDocUrls.length);
                  console.log('📋 Selected docs to download:', docsToDownload.length);
                  
                  setShowKlubbloggPdfOptions(false);
                  if (klubbloggPdfType === 'politi') {
                    downloadOrgPolitiPDF(docsToDownload);
                  } else {
                    downloadOrgStandardPDF(docsToDownload);
                  }
                }}
                className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-medium rounded-lg transition-colors"
              >
                <Download className="h-4 w-4 inline mr-2" />
                Last ned PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unlock Account Modal */}
      {selfDemote && (
        <SelfDemoteModal toRole={selfDemote.toRole} onCancel={() => setSelfDemote(null)}
          onConfirm={() => { const a = selfDemote.action; setSelfDemote(null); a(); }} />
      )}
      {showUnlockModal && selectedLockedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                🔒 Konto låst
              </h3>
              <button
                onClick={() => {
                  setShowUnlockModal(false);
                  setSelectedLockedMember(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-gray-400 text-sm">Medlem:</p>
                <p className="text-white font-medium">{selectedLockedMember.full_name}</p>
                <p className="text-gray-400 text-sm">{selectedLockedMember.email}</p>
              </div>

              <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
                <p className="text-red-400 text-sm font-medium mb-2">Låsedetaljer:</p>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-300">
                    <span className="text-gray-400">Grunn:</span> {
                      selectedLockedMember.lockout?.reason === 'too_many_failed_attempts' 
                        ? 'For mange feilede påloggingsforsøk' 
                        : selectedLockedMember.lockout?.reason || 'Ukjent'
                    }
                  </p>
                  <p className="text-gray-300">
                    <span className="text-gray-400">Antall forsøk:</span> {selectedLockedMember.lockout?.failed_attempts_count || 0}
                  </p>
                  <p className="text-gray-300">
                    <span className="text-gray-400">Låst til:</span> {
                      selectedLockedMember.lockout?.locked_until 
                        ? new Date(selectedLockedMember.lockout.locked_until).toLocaleString('no-NO')
                        : 'Ukjent'
                    }
                  </p>
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
                <p className="text-blue-400 text-sm">
                  ℹ️ Når du låser opp kontoen, vil medlemmet kunne logge inn igjen. 
                  Det anbefales at medlemmet bruker "Glemt passord?" funksjonen for å tilbakestille passordet.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowUnlockModal(false);
                  setSelectedLockedMember(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => unlockMemberAccount(selectedLockedMember.email)}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors font-medium"
              >
                🔓 Lås opp konto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Calendar Event Type Modal */}
      {showAddEventTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Legg til aktivitetstype</h3>
              <button
                onClick={() => setShowAddEventTypeModal(false)}
                className="p-1 hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Navn *</label>
                <input
                  type="text"
                  value={newEventType.label}
                  onChange={(e) => setNewEventType({...newEventType, label: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="F.eks. Sikkerhetskurs"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">ID (valgfritt)</label>
                <input
                  type="text"
                  value={newEventType.id}
                  onChange={(e) => setNewEventType({...newEventType, id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Genereres automatisk"
                />
                <p className="text-xs text-gray-500 mt-1">Brukes internt. Genereres fra navnet hvis tom.</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Farge</label>
                <div className="flex flex-wrap gap-2">
                  {availableColors.map(color => (
                    <button
                      key={color}
                      onClick={() => setNewEventType({...newEventType, color})}
                      className={`w-8 h-8 rounded-lg ${color} ${newEventType.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                      title={color.replace('bg-', '').replace('-500', '')}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowAddEventTypeModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleAddCalendarEventType}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Legg til
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Type Modal */}
      {showEditEventTypeModal && editingEventType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Rediger aktivitetstype</h3>
              <button
                onClick={() => { setShowEditEventTypeModal(false); setEditingEventType(null); }}
                className="p-1 hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Navn *</label>
                <input
                  type="text"
                  value={editingEventType.label}
                  onChange={(e) => setEditingEventType({...editingEventType, label: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="F.eks. Sikkerhetskurs"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">ID</label>
                <input
                  type="text"
                  value={editingEventType.id}
                  disabled
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-gray-400 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">ID kan ikke endres etter opprettelse.</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Bakgrunnsfarge</label>
                <div className="flex flex-wrap gap-2 items-center">
                  {availableColors.map(color => (
                    <button
                      key={color}
                      onClick={() => setEditingEventType({...editingEventType, color, customColor: null})}
                      className={`w-8 h-8 rounded-lg ${color} ${editingEventType.color === color && !editingEventType.customColor ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                      title={color.replace('bg-', '').replace('-500', '')}
                    />
                  ))}
                  {/* Custom color picker */}
                  <div className="relative">
                    <input
                      type="color"
                      value={editingEventType.customColor || '#6366f1'}
                      onChange={(e) => setEditingEventType({
                        ...editingEventType, 
                        color: null,
                        customColor: e.target.value
                      })}
                      className="w-8 h-8 rounded-lg cursor-pointer border-2 border-dashed border-gray-500 hover:border-gray-400"
                      title="Velg egendefinert farge"
                    />
                    {editingEventType.customColor && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border border-gray-800" />
                    )}
                  </div>
                </div>
                {editingEventType.customColor && (
                  <p className="text-xs text-yellow-400 mt-2">
                    Egendefinert farge: {editingEventType.customColor}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Tekstfarge</label>
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => setEditingEventType({...editingEventType, textColor: 'text-white'})}
                    className={`px-3 py-1.5 rounded-lg bg-gray-700 text-white text-sm ${editingEventType.textColor === 'text-white' || !editingEventType.textColor ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                  >
                    Hvit
                  </button>
                  <button
                    onClick={() => setEditingEventType({...editingEventType, textColor: 'text-black'})}
                    className={`px-3 py-1.5 rounded-lg bg-gray-200 text-black text-sm ${editingEventType.textColor === 'text-black' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                  >
                    Svart
                  </button>
                  <button
                    onClick={() => setEditingEventType({...editingEventType, textColor: 'text-gray-900'})}
                    className={`px-3 py-1.5 rounded-lg bg-gray-400 text-gray-900 text-sm ${editingEventType.textColor === 'text-gray-900' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                  >
                    Mørk grå
                  </button>
                  <button
                    onClick={() => setEditingEventType({...editingEventType, textColor: 'text-yellow-900'})}
                    className={`px-3 py-1.5 rounded-lg bg-yellow-200 text-yellow-900 text-sm ${editingEventType.textColor === 'text-yellow-900' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                  >
                    Mørk gul
                  </button>
                  {/* Custom text color picker */}
                  <div className="relative flex items-center gap-1">
                    <input
                      type="color"
                      value={editingEventType.customTextColor || '#ffffff'}
                      onChange={(e) => setEditingEventType({
                        ...editingEventType, 
                        textColor: null,
                        customTextColor: e.target.value
                      })}
                      className="w-8 h-8 rounded-lg cursor-pointer border-2 border-dashed border-gray-500 hover:border-gray-400"
                      title="Velg egendefinert tekstfarge"
                    />
                  </div>
                </div>
                {editingEventType.customTextColor && !editingEventType.textColor && (
                  <p className="text-xs text-yellow-400 mt-2">
                    Egendefinert tekstfarge: {editingEventType.customTextColor}
                  </p>
                )}
              </div>
              
              {/* Preview */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Forhåndsvisning</label>
                <div 
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${editingEventType.color || ''} ${editingEventType.textColor || 'text-white'}`}
                  style={{
                    backgroundColor: editingEventType.customColor || undefined,
                    color: editingEventType.customTextColor && !editingEventType.textColor ? editingEventType.customTextColor : undefined
                  }}
                >
                  {editingEventType.label || 'Eksempel'}
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowEditEventTypeModal(false); setEditingEventType(null); }}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleUpdateCalendarEventType}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Lagre endringer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE ONLY: Quick Stats Info Boxes at BOTTOM of page (always shown as a soft closing on mobile) */}
      <div className="md:hidden grid grid-cols-2 gap-3 mt-6" data-testid="admin-info-cards-mobile">
          <div className="bg-gradient-to-br from-green-600/20 to-green-600/10 border border-green-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-600/30 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-green-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Totalt antall treningsøkter</p>
              <p className="text-xl font-bold text-white">{totalTrainingSessions}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-600/20 to-blue-600/10 border border-blue-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-600/30 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Ukens treningsøkter</p>
              <p className="text-xl font-bold text-white">{weekTrainingSessions}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-600/20 to-purple-600/10 border border-purple-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-600/30 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-purple-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Dagens treningsøkter</p>
              <p className="text-xl font-bold text-white">{todayTrainingSessions}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-600/20 to-orange-600/10 border border-orange-600/30 rounded-lg p-3 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-orange-600/30 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-orange-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 truncate">Totalt antall medlemmer</p>
              <p className="text-xl font-bold text-white">{totalMembersCount}</p>
            </div>
          </div>
        </div>
    </div>
  );
};

export default Admin;